import { BrowserRouter, Link, Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import API_BASE_URL from "../config/api";
import "./StockAnalystApp.css";

function apiUrl(path) {
  return `${API_BASE_URL}${path}`;
}

async function fetchJson(path, options = {}) {
  const response = await fetch(apiUrl(path), {
    credentials: "include",
    ...options,
    headers: {
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(options.headers || {}),
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.error || data.message || `Request failed with HTTP ${response.status}`);
    error.response = data;
    throw error;
  }
  return data;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const ALERT_TIMEZONE_OPTIONS = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu",
  "UTC",
];

const DEFAULT_DISPLAY_TIMEZONE = "America/New_York";

function safeDisplayTimezone(value) {
  const timezone = String(value || "").trim();
  if (!timezone) return DEFAULT_DISPLAY_TIMEZONE;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: timezone }).format(new Date());
    return timezone;
  } catch {
    return DEFAULT_DISPLAY_TIMEZONE;
  }
}

function currentDisplayTimezone() {
  if (typeof window === "undefined") return DEFAULT_DISPLAY_TIMEZONE;
  return safeDisplayTimezone(window.localStorage.getItem("stockAnalystDisplayTimezone") || DEFAULT_DISPLAY_TIMEZONE);
}

function rememberDisplayTimezone(value) {
  if (typeof window === "undefined") return;
  const timezone = safeDisplayTimezone(value);
  window.localStorage.setItem("stockAnalystDisplayTimezone", timezone);
}

function formatWithDisplayTimezone(value, options = {}) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const timezone = safeDisplayTimezone(options.timeZone || currentDisplayTimezone());
  return new Intl.DateTimeFormat("en-US", {
    ...options,
    timeZone: timezone,
  }).format(date);
}

function BrandMark() {
  return (
    <span className="sa-mark" aria-hidden="true">
      <svg viewBox="0 0 48 48" fill="none">
        <path d="M24 8.5 36 13.6v10.1c0 7.1-4.8 13.2-12 16.5-7.2-3.3-12-9.4-12-16.5V13.6L24 8.5Z" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <path d="m17.5 24.4 4.1 4.2 8.9-9.1" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

function LoginPage({ onLogin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      await fetchJson("/auth/site-login", {
        method: "POST",
        body: JSON.stringify({ username, password }),
      });
      const session = await fetchJson("/auth/site-session");
      if (!session.authenticated) {
        throw new Error("Login was accepted, but the admin session cookie was not available on the next request. Check that interpresnetwork.com is pointed to the backend service, not a separate static frontend, and that the cookie domain matches the site.");
      }
      onLogin(true);
      navigate(location.state?.from?.pathname || "/products", { replace: true });
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="sa-login-shell">
      <section className="sa-login-card">
        <div className="sa-brand" style={{ color: "#123f72" }}>
          <BrandMark />
          <div>
            <strong style={{ color: "#123f72" }}>Interpres Analyst</strong>
            <span style={{ color: "#5d7189" }}>Private trading research cockpit</span>
          </div>
        </div>
        <h1>Admin Login</h1>
        <p className="sa-muted">The site is locked at the server before the scanner loads.</p>
        <form onSubmit={submit}>
          <label className="sa-field">Username<input value={username} onChange={(event) => setUsername(event.target.value)} /></label>
          <label className="sa-field">Password<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoFocus /></label>
          <button className="sa-btn" type="submit" disabled={busy}>{busy ? "Unlocking..." : "Unlock Cockpit"}</button>
        </form>
        {message && <p className="sa-error">{message}</p>}
      </section>
    </main>
  );
}

function Shell({ onLogout, children }) {
  const location = useLocation();
  const nav = [
    ["/products", "Product Home"],
    ["/dashboard", "Dashboard"],
    ["/command-center", "Command Center"],
    ["/scan", "Scan"],
    ["/hourly-scanner", "Hourly Scanner"],
    ["/day-trade", "Day Trade"],
    ["/live-score", "Live Score"],
    ["/indicator-alerts", "Indicator Alerts"],
    ["/valuation", "Valuation"],
    ["/earnings", "Earnings"],
    ["/saved-earnings", "Saved Printouts"],
    ["/watchlist", "Watchlist"],
    ["/journal", "Journal"],
    ["/trades", "Trades"],
    ["/execution", "Execution"],
    ["/strategy", "Strategy"],
    ["/settings", "Settings"],
  ];

  async function logout() {
    await fetchJson("/auth/site-logout", { method: "POST" }).catch(() => null);
    onLogout(false);
    window.location.href = "/login";
  }

  return (
    <div className="sa-shell">
      <aside className="sa-sidebar">
        <Link to="/dashboard" className="sa-brand">
          <BrandMark />
          <div>
            <strong>Interpres Analyst</strong>
            <span>Private market scanner</span>
          </div>
        </Link>
        <nav className="sa-nav">
          {nav.map(([to, label]) => (
            <Link key={to} to={to} className={location.pathname === to ? "is-active" : ""}>{label}</Link>
          ))}
          <a href="/real-estate">Real Estate</a>
          <button className="sa-logout" onClick={logout}>Lock Site</button>
        </nav>
      </aside>
      <main className="sa-main">{children}</main>
    </div>
  );
}

function Protected({ authenticated, children }) {
  const location = useLocation();
  if (!authenticated) return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}

function ProductChooserPage() {
  return (
    <section className="sa-card sa-product-home">
      <div className="sa-section-heading">
        <div>
          <p className="sa-kicker">Interpres private workspace</p>
          <h1>Choose your dashboard</h1>
          <p className="sa-muted">One admin login now unlocks each private business cockpit.</p>
        </div>
      </div>
      <div className="sa-product-grid">
        <a className="sa-product-card" href="/dashboard">
          <span className="sa-product-icon">SA</span>
          <div>
            <strong>Stock Analyst</strong>
            <p>Live scores, indicator alerts, hourly scanner, journal, brokerage review, and earnings research.</p>
          </div>
        </a>
        <a className="sa-product-card" href="/real-estate">
          <span className="sa-product-icon">RE</span>
          <div>
            <strong>Real Estate Dashboard</strong>
            <p>Lead intake, repair scope, material quoting, cart drafts, deal math, and market scans.</p>
          </div>
        </a>
      </div>
    </section>
  );
}

function RatingPill({ rating }) {
  const normalized = String(rating || "").toUpperCase();
  const className = normalized === "BEST_SETUP"
    ? "best"
    : normalized === "WATCH"
      ? "watch"
      : ["AVOID", "EARNINGS_DANGER", "DATA_ERROR"].includes(normalized)
        ? "avoid"
        : "neutral";
  return <span className={`sa-pill ${className}`}>{normalized.replace(/_/g, " ") || "N/A"}</span>;
}

function WatchStatePill({ state, label }) {
  const normalized = String(state || "").toUpperCase();
  const className = normalized === "TRADEABLE_NOW"
    ? "best"
    : ["NEAR_SETUP_ZONE", "WATCH"].includes(normalized)
      ? "watch"
      : normalized === "AVOID"
        ? "avoid"
        : "neutral";
  return <span className={`sa-pill ${className}`}>{label || normalized.replace(/_/g, " ") || "Wait"}</span>;
}

function TradeManagementPill({ action, label }) {
  const normalized = String(action || "").toUpperCase();
  const className = ["ENTRY_CANDIDATE", "ADD_ON_CANDIDATE", "HOLD"].includes(normalized)
    ? "best"
    : ["WATCH_FOR_ENTRY", "HOLD_WITH_CAUTION", "TRIM_OR_TAKE_PROFIT"].includes(normalized)
      ? "watch"
      : ["EXIT_PRESSURE"].includes(normalized)
        ? "avoid"
        : "neutral";
  return <span className={`sa-pill ${className}`}>{label || normalized.replace(/_/g, " ") || "Manage"}</span>;
}

function MicroObstaclePanel({ obstacles, trackingSide = "NONE" }) {
  if (!obstacles) return null;
  const longObstacle = obstacles.long || {};
  const shortObstacle = obstacles.short || {};
  const trackedKey = String(trackingSide || "").toUpperCase() === "SHORT" ? "short" : "long";
  const tracked = trackingSide === "NONE" ? null : obstacles[trackedKey];
  const topReasons = [
    ...(longObstacle.reasons || []).map((reason) => ({ ...reason, obstacleSide: "Long" })),
    ...(shortObstacle.reasons || []).map((reason) => ({ ...reason, obstacleSide: "Short" })),
  ].sort((left, right) => Number(right.weight || 0) - Number(left.weight || 0)).slice(0, 5);
  return (
    <section className="sa-micro-obstacles" aria-label="Micro obstacle pressure">
      <div className="sa-micro-obstacles-header">
        <div>
          <h2>Micro Obstacles</h2>
          <p className="sa-muted">High means more nearby friction in that direction. This is for trims, add-ons, and scalp awareness; it is not the main trade edge.</p>
        </div>
        {tracked && (
          <div className="sa-micro-focus">
            <span>{trackingSide} pressure</span>
            <strong>{tracked.score ?? "-"}</strong>
            <em>{tracked.label || "Watching"}</em>
          </div>
        )}
      </div>
      <div className="sa-micro-grid">
        {[["Long obstacles", longObstacle, "long"], ["Short obstacles", shortObstacle, "short"]].map(([label, side, className]) => (
          <div className={`sa-micro-side ${className}`} key={label}>
            <span>{label}</span>
            <strong>{side.score ?? "-"}</strong>
            <p>{side.label || "Not measured"}</p>
            <div className="sa-micro-bar" aria-hidden="true">
              <span style={{ width: `${Math.max(0, Math.min(100, Number(side.score || 0)))}%` }} />
            </div>
          </div>
        ))}
      </div>
      {topReasons.length > 0 && (
        <div className="sa-micro-reasons">
          {topReasons.map((reason) => (
            <div className="sa-micro-reason" key={`${reason.obstacleSide}-${reason.code}-${reason.detail || ""}`}>
              <strong>{reason.obstacleSide}: {reason.label}</strong>
              <span>{reason.detail || reason.source || ""}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function formatDataSource(provider) {
  const normalized = String(provider || "").trim().toLowerCase();
  if (normalized === "webull") return "Webull";
  if (normalized === "fmp") return "Financial Modeling Prep";
  if (normalized === "alpaca") return "Alpaca";
  if (normalized === "sample") return "Sample fallback";
  return provider || "Not recorded";
}

function buildEmaSeries(values = [], period = 22) {
  const multiplier = 2 / (period + 1);
  const result = [];
  let previous = null;
  values.forEach((value, index) => {
    const number = Number(value);
    if (!Number.isFinite(number)) {
      result.push(null);
      return;
    }
    if (previous === null) {
      const seedValues = values.slice(0, index + 1).map(Number).filter(Number.isFinite);
      previous = seedValues.reduce((sum, item) => sum + item, 0) / seedValues.length;
    } else {
      previous = (number - previous) * multiplier + previous;
    }
    result.push(previous);
  });
  return result;
}

function PriceChart({ bars = [], chartType = "line", showEma22 = true, showCurrentPrice = true }) {
  const chart = useMemo(() => {
    const usable = bars.filter((bar) => Number.isFinite(Number(bar.close))).slice(-120);
    if (!usable.length) return { usable: [], linePoints: "", candleRows: [], ema22Points: "", latest: null, latestEma22: null, latestPriceY: null, latestEma22Y: null };
    const ema22Series = buildEmaSeries(usable.map((bar) => bar.close), 22);
    const numericValues = [
      ...usable.flatMap((bar) => [Number(bar.low ?? bar.close), Number(bar.high ?? bar.close), Number(bar.close)]),
      ...(showEma22 ? ema22Series.filter((value) => Number.isFinite(Number(value))).map(Number) : []),
    ];
    const min = Math.min(...numericValues);
    const max = Math.max(...numericValues);
    const width = 760;
    const height = 220;
    const padding = Math.max((max - min) * 0.08, 0.01);
    const chartMin = min - padding;
    const chartMax = max + padding;
    const scaleY = (price) => (chartMax === chartMin ? height / 2 : height - ((Number(price) - chartMin) / (chartMax - chartMin)) * height);
    const linePoints = usable.map((bar, index) => {
      const x = usable.length === 1 ? 0 : (index / (usable.length - 1)) * width;
      const y = scaleY(bar.close);
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    }).join(" ");
    const ema22Points = ema22Series.map((value, index) => {
      if (!Number.isFinite(Number(value))) return null;
      const x = usable.length === 1 ? 0 : (index / (usable.length - 1)) * width;
      const y = scaleY(value);
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    }).filter(Boolean).join(" ");
    const slot = usable.length ? width / usable.length : width;
    const candleWidth = Math.max(2, Math.min(9, slot * 0.58));
    const candleRows = usable.map((bar, index) => {
      const x = slot * index + slot / 2;
      const open = Number(bar.open ?? bar.close);
      const close = Number(bar.close);
      const high = Number(bar.high ?? Math.max(open, close));
      const low = Number(bar.low ?? Math.min(open, close));
      const yOpen = scaleY(open);
      const yClose = scaleY(close);
      const yHigh = scaleY(high);
      const yLow = scaleY(low);
      return {
        x,
        yHigh,
        yLow,
        yBody: Math.min(yOpen, yClose),
        bodyHeight: Math.max(2, Math.abs(yClose - yOpen)),
        candleWidth,
        up: close >= open,
      };
    });
    const latest = usable[usable.length - 1] || null;
    const latestEma22 = ema22Series[ema22Series.length - 1] || null;
    return {
      usable,
      linePoints,
      candleRows,
      ema22Points,
      latest,
      latestEma22,
      latestPriceY: latest ? scaleY(latest.close) : null,
      latestEma22Y: latestEma22 ? scaleY(latestEma22) : null,
    };
  }, [bars, showEma22]);

  return (
    <svg className="sa-chart" viewBox="0 0 760 220" preserveAspectRatio="none" role="img" aria-label="Price chart">
      <line x1="0" x2="760" y1="55" y2="55" stroke="#d9e5f2" />
      <line x1="0" x2="760" y1="110" y2="110" stroke="#d9e5f2" />
      <line x1="0" x2="760" y1="165" y2="165" stroke="#d9e5f2" />
      {showCurrentPrice && chart.latestPriceY !== null && chart.latest && (
        <g>
          <line x1="0" x2="760" y1={chart.latestPriceY} y2={chart.latestPriceY} stroke="#0f477e" strokeDasharray="5 5" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
          <rect x="650" y={Math.max(4, Math.min(198, chart.latestPriceY - 13))} width="104" height="24" rx="8" fill="#0f477e" />
          <text x="702" y={Math.max(19, Math.min(213, chart.latestPriceY + 4))} textAnchor="middle" fill="#fff" fontSize="11" fontWeight="800">Price {Number(chart.latest.close).toFixed(2)}</text>
        </g>
      )}
      {showEma22 && chart.ema22Points && (
        <g>
          <polyline points={chart.ema22Points} fill="none" stroke="#8a5a00" strokeWidth="2.5" strokeDasharray="7 5" vectorEffect="non-scaling-stroke" />
          {chart.latestEma22Y !== null && chart.latestEma22 && (
            <>
              <circle cx="746" cy={chart.latestEma22Y} r="4" fill="#8a5a00" />
              <rect x="606" y={Math.max(30, Math.min(168, chart.latestEma22Y + 8))} width="148" height="24" rx="8" fill="#fff4cf" stroke="#8a5a00" />
              <text x="680" y={Math.max(45, Math.min(184, chart.latestEma22Y + 25))} textAnchor="middle" fill="#805f00" fontSize="11" fontWeight="850">EMA 22 {Number(chart.latestEma22).toFixed(2)}</text>
            </>
          )}
        </g>
      )}
      {chartType === "candles" ? chart.candleRows.map((bar, index) => (
        <g key={`${index}-${bar.x}`}>
          <line x1={bar.x} x2={bar.x} y1={bar.yHigh} y2={bar.yLow} stroke={bar.up ? "#17633a" : "#8a1f33"} strokeWidth="2" vectorEffect="non-scaling-stroke" />
          <rect
            x={bar.x - bar.candleWidth / 2}
            y={bar.yBody}
            width={bar.candleWidth}
            height={bar.bodyHeight}
            rx="1"
            fill={bar.up ? "#d9f8e7" : "#ffe1e6"}
            stroke={bar.up ? "#17633a" : "#8a1f33"}
            strokeWidth="1.5"
            vectorEffect="non-scaling-stroke"
          />
        </g>
      )) : chart.linePoints && <polyline points={chart.linePoints} fill="none" stroke="#0f477e" strokeWidth="4" vectorEffect="non-scaling-stroke" />}
    </svg>
  );
}

function CandidateTable({ candidates = [], onAdd }) {
  return (
    <div className="sa-table-wrap">
      <table className="sa-table">
        <thead>
          <tr>
            <th>Symbol</th>
            <th>Rating</th>
            <th>Score</th>
            <th>Price</th>
            <th>RSI</th>
            <th>MACD Hist</th>
            <th>Rel Vol</th>
            <th>Earnings</th>
            <th>Reason</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {candidates.map((candidate) => (
            <tr key={candidate.symbol}>
              <td><Link to={`/ticker/${candidate.symbol}`}><strong>{candidate.symbol}</strong></Link></td>
              <td><RatingPill rating={candidate.rating} /></td>
              <td>{candidate.score}</td>
              <td>{candidate.latestPrice ?? "-"}</td>
              <td>{candidate.technicals?.rsi14 ?? "-"}</td>
              <td>{candidate.technicals?.macdHistogram ?? "-"}</td>
              <td>{candidate.technicals?.relativeVolume ?? "-"}</td>
              <td>{candidate.earningsRisk?.nearest ? `${candidate.earningsRisk.nearest.date} ${candidate.earningsRisk.nearest.time || ""}` : "None in window"}</td>
              <td>{candidate.blockers?.[0] || candidate.reasons?.[0] || "-"}</td>
              <td><button className="sa-btn secondary" onClick={() => onAdd?.(candidate.symbol)}>Watch</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatNumber(value) {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  if (!Number.isFinite(number)) return "-";
  return new Intl.NumberFormat("en-US").format(number);
}

function formatMoney(value, currency = "USD") {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  if (!Number.isFinite(number)) return "-";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
    maximumFractionDigits: Math.abs(number) >= 1000 ? 0 : 2,
  }).format(number);
}

function formatPercent(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "-";
  return `${number.toFixed(2)}%`;
}

function formatYears(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "-";
  if (number >= 100) return `${Math.round(number)} years`;
  if (number >= 10) return `${number.toFixed(1)} years`;
  return `${number.toFixed(2)} years`;
}

function formatDateTime(value) {
  return formatWithDisplayTimezone(value, {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "shortGeneric",
  });
}

function formatTimeOnly(value) {
  return formatWithDisplayTimezone(value, {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "shortGeneric",
  });
}

function formatEnabled(value) {
  return value ? "On" : "Off";
}

function isoDateFromUtcDate(date) {
  return date.toISOString().slice(0, 10);
}

function addDaysIsoDate(isoDate, days) {
  const date = new Date(`${isoDate}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + Number(days || 0));
  return isoDateFromUtcDate(date);
}

function upcomingFridayIso(today = new Date()) {
  const date = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
  const day = date.getUTCDay();
  const daysUntilFriday = (5 - day + 7) % 7;
  date.setUTCDate(date.getUTCDate() + daysUntilFriday);
  return isoDateFromUtcDate(date);
}

function weekRangeEndingFriday(fridayIso) {
  const friday = String(fridayIso || upcomingFridayIso()).slice(0, 10);
  return {
    from: addDaysIsoDate(friday, -4),
    to: friday,
  };
}

function weekRangeForIsoDate(isoDate) {
  const selected = String(isoDate || upcomingFridayIso()).slice(0, 10);
  const date = new Date(`${selected}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return weekRangeEndingFriday(upcomingFridayIso());
  const day = date.getUTCDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const fridayOffset = day === 0 ? -2 : 5 - day;
  return {
    from: addDaysIsoDate(selected, mondayOffset),
    to: addDaysIsoDate(selected, fridayOffset),
  };
}

function StatusBadge({ status }) {
  const normalized = String(status || "").toUpperCase();
  const className = ["FILLED", "COMPLETED", "EXECUTED"].includes(normalized)
    ? "best"
    : ["CANCELED", "CANCELLED", "REJECTED", "FAILED"].includes(normalized)
      ? "avoid"
      : ["OPEN", "PENDING", "PARTIAL_FILLED", "SUBMITTED"].includes(normalized)
        ? "watch"
        : "neutral";
  return <span className={`sa-pill ${className}`}>{normalized.replace(/_/g, " ") || "Unknown"}</span>;
}

function PassFail({ pass, unknownLabel = "Unknown" }) {
  if (pass === true) return <span className="sa-pill best">Pass</span>;
  if (pass === false) return <span className="sa-pill avoid">Fail</span>;
  return <span className="sa-pill neutral">{unknownLabel}</span>;
}

function RsiContextCell({ row }) {
  const rotation = row?.trend?.rsiRotation || {};
  const latestZone = rotation.latestZone || "UNKNOWN";
  const labels = [
    latestZone !== "UNKNOWN" ? latestZone.replace(/_/g, " ").toLowerCase() : "",
    rotation.longReversalSetup ? "Long reversal watch" : "",
    rotation.shortReversalSetup ? "Short reversal watch" : "",
    rotation.longRotationOpen && rotation.longRoomToOverbought !== null && rotation.longRoomToOverbought !== undefined ? `${rotation.longRoomToOverbought} pts to overbought` : "",
    rotation.shortRotationOpen && rotation.shortRoomToOversold !== null && rotation.shortRoomToOversold !== undefined ? `${rotation.shortRoomToOversold} pts to oversold` : "",
    rotation.longTakeProfitZone ? "Long profit zone" : "",
    rotation.shortTakeProfitZone ? "Short profit zone" : "",
  ].filter(Boolean);
  return (
    <>
      {row?.technicals?.rsi14 ?? "-"}
      {labels.length > 0 && <span className="sa-subtle">{labels.join(" / ")}</span>}
      {rotation.fullRotationOversoldOverboughtOversold && <span className="sa-subtle">Full OS-OB-OS rotation</span>}
      {rotation.fullRotationOverboughtOversoldOverbought && <span className="sa-subtle">Full OB-OS-OB rotation</span>}
    </>
  );
}

function MomentumContextCell({ row }) {
  const summary = row?.trend?.oscillatorRegime?.summary || {};
  const labels = [];
  if (summary.longReversalEnergyCount > summary.shortReversalEnergyCount) labels.push("Long reversal energy");
  if (summary.shortReversalEnergyCount > summary.longReversalEnergyCount) labels.push("Short reversal energy");
  if (summary.longContinuationRoomCount > summary.shortContinuationRoomCount) labels.push("Long continuation room");
  if (summary.shortContinuationRoomCount > summary.longContinuationRoomCount) labels.push("Short continuation room");
  if (summary.bullishSignalCrossCount > 0) labels.push(`${summary.bullishSignalCrossCount} bullish cross`);
  if (summary.bearishSignalCrossCount > 0) labels.push(`${summary.bearishSignalCrossCount} bearish cross`);
  if (summary.nearSignalLongContinuationPullbackCount > 0) labels.push("Long near-signal pullback");
  if (summary.nearSignalShortContinuationPullbackCount > 0) labels.push("Short near-signal pullback");
  return (
    <>
      {row?.technicals?.macdHistogram ?? "-"}
      {summary.farFromZeroCount > 0 && <span className="sa-subtle">{summary.farFromZeroCount} far from zero</span>}
      {labels.length > 0 && <span className="sa-subtle">{labels.slice(0, 3).join(" / ")}</span>}
    </>
  );
}

function VwapContextCell({ row }) {
  const regime = row?.trend?.vwapRegime || {};
  const latest = regime.latest || {};
  const rotation = regime.rotation || {};
  const labels = [];
  if (regime.longReversalZone) labels.push("Lower band edge");
  if (regime.shortReversalZone) labels.push("Upper band edge");
  if (regime.insideBandCaution) labels.push("Inside-band caution");
  if (regime.longRotationOpportunity) labels.push("Rotating toward upper");
  if (regime.shortRotationOpportunity) labels.push("Rotating toward lower");
  if (rotation.madeLowerIntradayLowAfterUpperToLower) labels.push("Lower low after upper-to-lower");
  if (rotation.madeHigherIntradayHighAfterLowerToUpper) labels.push("Higher high after lower-to-upper");
  return (
    <>
      {latest.bandPosition ? latest.bandPosition.replace(/_/g, " ").toLowerCase() : "-"}
      {latest.bandPercent !== null && latest.bandPercent !== undefined && <span className="sa-subtle">{latest.bandPercent} band units</span>}
      {labels.length > 0 && <span className="sa-subtle">{labels.slice(0, 3).join(" / ")}</span>}
    </>
  );
}

function formatOptionStatus(status) {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "NASDAQ_CHAIN_VERIFIED") return "Chain activity";
  if (normalized === "VERIFIED") return "Snapshot";
  if (normalized === "OVERRIDE") return "Override";
  if (normalized === "NEEDS_OPTION_DATA" || normalized === "NEEDS_OPTION_CODES") return "Needs data";
  if (normalized === "OPTION_CHAIN_FAILED") return "Chain failed";
  if (normalized === "OPTION_SNAPSHOT_FAILED") return "Snapshot failed";
  return normalized.replace(/_/g, " ") || "Unknown";
}

function normalizeTickerSymbol(value) {
  const raw = String(value || "").trim().toUpperCase().replace(/\s+/g, "");
  const stripped = raw.replace(/^[.$^]+/, "");
  if (["SPX", "SPXW", "INX", "GSPC", "IDXSP-SPX"].includes(stripped) || ["^SPX", ".SPX", "$SPX", "^GSPC"].includes(raw)) {
    return "SPX";
  }
  return raw.replace(/[^A-Z0-9.-]/g, "");
}

function chartOutputSizeForTimeframe(timeframe) {
  const normalized = String(timeframe || "").toLowerCase();
  if (normalized === "daily") return 520;
  if (["weekly", "monthly", "quarterly", "biannual", "yearly"].includes(normalized)) return 260;
  if (["1m", "2m", "5m"].includes(normalized)) return 390;
  if (["10m", "15m"].includes(normalized)) return 260;
  if (normalized === "30m") return 220;
  if (normalized === "1h") return 180;
  return 390;
}

function timeframeMinutesForClock(timeframe) {
  const normalized = String(timeframe || "").trim().toLowerCase();
  if (normalized === "1h" || normalized === "60m") return 60;
  if (normalized === "daily" || normalized === "1d") return 390;
  if (normalized.endsWith("m")) {
    const minutes = Number(normalized.slice(0, -1));
    return Number.isFinite(minutes) && minutes > 0 ? minutes : null;
  }
  return null;
}

function marketClockParts(nowMs = Date.now()) {
  const date = new Date(nowMs);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour12: false,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(date).reduce((acc, part) => {
    if (part.type !== "literal") acc[part.type] = part.value;
    return acc;
  }, {});
  const hour = Number(parts.hour === "24" ? "0" : parts.hour);
  const minute = Number(parts.minute || 0);
  const second = Number(parts.second || 0);
  return {
    weekday: parts.weekday,
    hour,
    minute,
    second,
    secondsSinceMidnight: hour * 3600 + minute * 60 + second,
  };
}

function formatClockDuration(seconds) {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  const minutes = Math.floor(total / 60);
  const remainder = total % 60;
  return `${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`;
}

function formatLiveScoreAge(ms) {
  const totalSeconds = Math.max(0, Math.round((Number(ms) || 0) / 1000));
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return `${minutes}m ${seconds}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

function formatFreshnessAge(ms) {
  if (ms === null || ms === undefined) return null;
  return formatLiveScoreAge(ms);
}

const DAY_TRADE_TIMEFRAMES = ["1m", "2m", "5m", "10m", "15m", "30m", "1h", "daily", "weekly", "monthly", "quarterly", "biannual", "yearly"];

function formatCompactNumber(value, digits = 2) {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  if (!Number.isFinite(number)) return "-";
  if (Math.abs(number) >= 1000) return number.toLocaleString("en-US", { maximumFractionDigits: digits });
  if (Math.abs(number) >= 100) return number.toFixed(Math.min(digits, 2));
  if (Math.abs(number) >= 10) return number.toFixed(Math.min(digits, 2));
  return number.toFixed(digits);
}

function formatTinyDate(value) {
  return formatWithDisplayTimezone(value, {
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "shortGeneric",
  });
}

function getTimeframeRows(analysis) {
  const source = analysis?.multiTimeframe?.timeframes || {};
  return DAY_TRADE_TIMEFRAMES.map((timeframe) => ({
    timeframe,
    ...(source[timeframe] || {}),
  }));
}

function getScoreValue(score) {
  const value = Number(score?.score);
  return Number.isFinite(value) ? value : null;
}

function getDominantDayTradeSide(scores) {
  const longScore = getScoreValue(scores?.long);
  const shortScore = getScoreValue(scores?.short);
  if (longScore === null && shortScore === null) return { side: "Loading", score: null };
  if (longScore === null) return { side: "Short", score: shortScore };
  if (shortScore === null) return { side: "Long", score: longScore };
  if (longScore === shortScore) return { side: "Balanced", score: longScore };
  return longScore > shortScore ? { side: "Long", score: longScore } : { side: "Short", score: shortScore };
}

function signalTrendClass(value, signal) {
  if (value === null || value === undefined || value === "" || signal === null || signal === undefined || signal === "") return "";
  const valueNumber = Number(value);
  const signalNumber = Number(signal);
  if (!Number.isFinite(valueNumber) || !Number.isFinite(signalNumber)) return "";
  if (valueNumber > signalNumber) return "sa-dt-up";
  if (valueNumber < signalNumber) return "sa-dt-down";
  return "";
}

function signalTrendLabel(value, signal) {
  if (value === null || value === undefined || value === "" || signal === null || signal === undefined || signal === "") return "-";
  const valueNumber = Number(value);
  const signalNumber = Number(signal);
  if (!Number.isFinite(valueNumber) || !Number.isFinite(signalNumber)) return "-";
  if (valueNumber > signalNumber) return "Above";
  if (valueNumber < signalNumber) return "Below";
  return "At";
}

function buildCandleClock(timeframe, nowMs = Date.now()) {
  const minutes = timeframeMinutesForClock(timeframe);
  const parts = marketClockParts(nowMs);
  if (!minutes || !parts || ["Sat", "Sun"].includes(parts.weekday)) {
    return {
      status: "closed",
      label: "Market closed",
      secondsToClose: null,
      refreshZone: "closed",
    };
  }
  const marketOpen = 9 * 3600 + 30 * 60;
  const marketClose = 16 * 3600;
  const preMarketOpen = 4 * 3600;
  const afterHoursClose = 20 * 3600;
  const nowSeconds = parts.secondsSinceMidnight;
  if (nowSeconds >= preMarketOpen && nowSeconds < marketOpen) {
    return {
      status: "extended",
      label: `Pre-market, regular opens in ${formatClockDuration(marketOpen - nowSeconds)}`,
      secondsToClose: 60,
      refreshZone: "extended",
    };
  }
  if (nowSeconds < preMarketOpen) {
    return {
      status: "closed",
      label: "Market closed",
      secondsToClose: null,
      refreshZone: "closed",
    };
  }
  if (nowSeconds >= marketClose && nowSeconds < afterHoursClose) {
    return {
      status: "extended",
      label: `After-hours, closes in ${formatClockDuration(afterHoursClose - nowSeconds)}`,
      secondsToClose: 60,
      refreshZone: "extended",
    };
  }
  if (nowSeconds >= afterHoursClose) {
    return {
      status: "closed",
      label: "Market closed",
      secondsToClose: null,
      refreshZone: "closed",
    };
  }
  if (String(timeframe || "").trim().toLowerCase() === "daily") {
    const secondsToClose = marketClose - nowSeconds;
    return {
      status: "open",
      label: `Daily close in ${formatClockDuration(secondsToClose)}`,
      secondsToClose,
      refreshZone: secondsToClose <= 60 ? "closing" : "normal",
    };
  }
  const frameSeconds = minutes * 60;
  const secondsFromOpen = nowSeconds - marketOpen;
  const secondsIntoFrame = ((secondsFromOpen % frameSeconds) + frameSeconds) % frameSeconds;
  const secondsToClose = secondsIntoFrame === 0 ? frameSeconds : frameSeconds - secondsIntoFrame;
  return {
    status: "open",
    label: `${timeframe} candle closes in ${formatClockDuration(secondsToClose)}`,
    secondsToClose,
    refreshZone: secondsToClose <= 60 ? "closing" : secondsToClose <= 180 ? "near_close" : "normal",
  };
}

function liveRefreshDelayMs(mode, candleClock) {
  const normalized = String(mode || "smart").toLowerCase();
  if (normalized === "off") return null;
  if (normalized === "5s") return 5000;
  if (normalized === "15s") return 15000;
  if (normalized === "30s") return 30000;
  if (normalized === "candle") {
    return candleClock?.secondsToClose !== null && candleClock?.secondsToClose !== undefined
      ? Math.max(1500, Number(candleClock.secondsToClose) * 1000 + 900)
      : 60000;
  }
  if (candleClock?.status === "extended") return 15000;
  if (candleClock?.status !== "open") return 60000;
  if (candleClock?.secondsToClose <= 15) return 3000;
  if (candleClock?.secondsToClose <= 60) return 5000;
  if (candleClock?.secondsToClose <= 180) return 10000;
  return 15000;
}

function getEarningsReportRows(report = {}) {
  if (Array.isArray(report.earnings)) return report.earnings;
  if (Array.isArray(report.candidates)) return report.candidates;
  return [];
}

function escapeCsvValue(value) {
  if (value === null || value === undefined) return "";
  const text = String(value);
  if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

function buildEarningsCsv(report = {}) {
  const headers = [
    "symbol",
    "date",
    "time",
    "tradeDate",
    "averageDailyVolume",
    "latestVolume",
    "maxBidActivity",
    "maxAskActivity",
    "totalOptionVolume",
    "qualified",
    "readinessScore",
    "source",
  ];
  const rows = getEarningsReportRows(report).map((row) => [
    row.symbol,
    row.date,
    row.time || "",
    row.tradeDate || "",
    row.averageDailyVolume ?? "",
    row.latestVolume ?? "",
    row.optionActivity?.maxBidSize ?? "",
    row.optionActivity?.maxAskSize ?? "",
    row.optionActivity?.totalOptionVolume ?? "",
    row.filters?.qualified ?? row.readiness?.readyForPrimaryWatchlist ?? "",
    row.readiness?.score ?? "",
    row.source || report.provider || report.sourceReport?.provider || "",
  ]);
  return [
    headers.map(escapeCsvValue).join(","),
    ...rows.map((row) => row.map(escapeCsvValue).join(",")),
  ].join("\n");
}

function downloadTextFile(filename, text, type = "text/plain") {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function exportEarningsCsv(report = {}, label = "earnings") {
  const from = report.from || report.sourceReport?.from || "from";
  const to = report.to || report.sourceReport?.to || "to";
  const safeLabel = String(label || "earnings").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "earnings";
  downloadTextFile(`${safeLabel}-${from}-to-${to}.csv`, buildEarningsCsv(report), "text/csv");
}

function EarningsReportTable({ report = {}, busy = false }) {
  const rows = getEarningsReportRows(report);
  return (
    <div className="sa-table-wrap">
      <table className="sa-table">
        <thead>
          <tr>
            <th>Symbol</th>
            <th>Date</th>
            <th>Time</th>
            <th>Avg Volume</th>
            <th>Volume</th>
            <th>Bid Activity</th>
            <th>Ask Activity</th>
            <th>Options</th>
            <th>Qualified</th>
            <th>Source</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={`${row.symbol}-${row.date}-${index}`}>
              <td><Link to={`/ticker/${row.symbol}`}><strong>{row.symbol}</strong></Link></td>
              <td>{row.date}</td>
              <td>{row.time || "-"}</td>
              <td>
                {formatNumber(row.averageDailyVolume)}
                {row.averageDailyVolumeDays ? <span className="sa-subtle">{row.averageDailyVolumeDays}-bar average</span> : null}
                {row.latestVolume ? <span className="sa-subtle">Latest {formatNumber(row.latestVolume)} {row.latestVolumeDate || ""}</span> : null}
                {row.marketDataWarning ? <span className="sa-subtle sa-warning">Volume unavailable</span> : null}
              </td>
              <td><PassFail pass={row.filters?.shareVolumePass ?? row.readiness?.averageVolumePass} /></td>
              <td>{formatNumber(row.optionActivity?.maxBidSize)}</td>
              <td>{formatNumber(row.optionActivity?.maxAskSize)}</td>
              <td>
                <span className="sa-subtle">{formatOptionStatus(row.optionActivity?.status)}</span>
                {row.optionActivity?.totalOptionVolume !== null && row.optionActivity?.totalOptionVolume !== undefined
                  ? <span className="sa-subtle"> Vol {formatNumber(row.optionActivity.totalOptionVolume)}</span>
                  : null}
                {row.optionActivity?.maxOpenInterest !== null && row.optionActivity?.maxOpenInterest !== undefined
                  ? <span className="sa-subtle"> OI {formatNumber(row.optionActivity.maxOpenInterest)}</span>
                  : null}
                {row.readiness?.score !== null && row.readiness?.score !== undefined ? <span className="sa-subtle">Score {row.readiness.score}</span> : null}
              </td>
              <td><PassFail pass={row.filters?.qualified ?? row.readiness?.readyForPrimaryWatchlist} /></td>
              <td>{row.source || report.provider || report.sourceReport?.provider}</td>
            </tr>
          ))}
          {!busy && rows.length === 0 && (
            <tr><td colSpan="10">No earnings rows returned for this report.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function Dashboard() {
  const [status, setStatus] = useState(null);
  const [keepalive, setKeepalive] = useState(null);
  const [scan, setScan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [alertToggleBusy, setAlertToggleBusy] = useState(false);
  const [alertToggleMessage, setAlertToggleMessage] = useState("");

  const loadDashboardStatus = useCallback(async () => {
    const [nextStatus, nextKeepalive] = await Promise.all([
      fetchJson("/stock-analyst/status"),
      fetchJson("/health/keepalive").catch((err) => ({ ok: false, status: "ERROR", error: err.message })),
    ]);
    setStatus(nextStatus);
    setKeepalive(nextKeepalive);
    rememberDisplayTimezone(nextStatus?.alertControls?.displayTimezone);
    return nextStatus;
  }, []);

  useEffect(() => {
    loadDashboardStatus().catch((err) => setError(err.message));
  }, [loadDashboardStatus]);

  async function runScan() {
    setBusy(true);
    setError("");
    try {
      const data = await fetchJson("/stock-analyst/scan", { method: "POST", body: JSON.stringify({}) });
      setScan(data.report);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function toggleAllAlerts() {
    const currentlyEnabled = status?.alertControls?.dashboardAlertsEnabled !== false;
    setAlertToggleBusy(true);
    setAlertToggleMessage("");
    setError("");
    try {
      await fetchJson("/stock-analyst/alerts/master", {
        method: "POST",
        body: JSON.stringify({ enabled: !currentlyEnabled }),
      });
      await loadDashboardStatus();
      setAlertToggleMessage(!currentlyEnabled ? "Alert delivery resumed." : "Alert delivery paused.");
    } catch (err) {
      setAlertToggleMessage(err.message);
    } finally {
      setAlertToggleBusy(false);
    }
  }

  const providerStatus = status?.providerStatus;
  const alertControls = status?.alertControls || {};
  const dashboardAlertsEnabled = alertControls.dashboardAlertsEnabled !== false;
  const dashboardToggleEnabled = alertControls.dashboardToggleEnabled !== false;
  const envMasterEnabled = alertControls.envMasterEnabled !== false;
  const stockAlertHealth = keepalive?.stockAlertHealth || null;
  const liveScoreRuntime = status?.stockAlertRuntime || {};
  const scoreUpdateRuntime = status?.stockScoreUpdateRuntime || {};
  const indicatorRuntime = status?.stockIndicatorAlertRuntime || {};
  const hourlyScannerRuntime = status?.stockHourlyScannerRuntime || {};
  const persistenceHealth = status?.persistence || stockAlertHealth?.persistence || null;
  const statusPageUrl = status?.uptimeMonitor?.statusPageUrl || "https://stats.uptimerobot.com/C1gRjDyGCJ";
  const persistenceOk = !persistenceHealth || persistenceHealth.ok !== false;
  const operationalState = keepalive?.ok && stockAlertHealth?.ok !== false && persistenceOk ? "operational" : "degraded";
  const operationalLabel = operationalState === "operational" ? "Operational" : "Needs attention";
  const latestScan = scan || null;
  const alertLaneRows = [
    {
      label: "Live score alerts",
      enabled: alertControls.liveScoreAlertsEnabled,
      detail: liveScoreRuntime.lastCheckedAt ? `checked ${formatDateTime(liveScoreRuntime.lastCheckedAt)}` : "threshold lane",
    },
    {
      label: "Indicator alerts",
      enabled: alertControls.indicatorAlertsEnabled,
      detail: indicatorRuntime.lastCheckedAt ? `checked ${formatDateTime(indicatorRuntime.lastCheckedAt)}` : "technical lane",
    },
    {
      label: "Status updates",
      enabled: alertControls.scoreUpdateAlertsEnabled,
      detail: scoreUpdateRuntime.nextRunAt ? `next ${formatDateTime(scoreUpdateRuntime.nextRunAt)}` : "scheduled lane",
    },
    {
      label: "Hourly scanner alerts",
      enabled: alertControls.hourlyScannerAlertsEnabled,
      detail: hourlyScannerRuntime.lastCheckedAt ? `checked ${formatDateTime(hourlyScannerRuntime.lastCheckedAt)}` : "scanner lane",
    },
  ];

  return (
    <>
      <section className="sa-hero">
        <div className="sa-card">
          <h1>Morning Trading Cockpit</h1>
          <p className="sa-muted">Private scan logic runs on your server. Data providers receive symbol/date requests, not your strategy rules.</p>
          <div className="sa-actions">
            <button className="sa-btn" onClick={runScan} disabled={busy}>{busy ? "Scanning..." : "Run Morning Scan"}</button>
            <Link className="sa-btn secondary" to="/earnings">Review Earnings</Link>
            <Link className="sa-btn secondary" to="/strategy">Rules</Link>
          </div>
          {error && <p className="sa-error">{error}</p>}
        </div>
        <div className="sa-card">
          <h2>Provider Readiness</h2>
          <p><strong>Webull:</strong> {providerStatus?.providers?.webull?.configured ? "Configured" : "Not configured"}</p>
          <p><strong>FMP:</strong> {providerStatus?.providers?.fmp?.configured ? "Configured" : "Not configured"}</p>
          <p><strong>Alpaca:</strong> {providerStatus?.providers?.alpaca?.configured ? "Configured" : "Not configured"}</p>
          <p className="sa-muted">Fallback sample mode is allowed for UI testing only.</p>
        </div>
      </section>

      <section className="sa-grid">
        <div className="sa-kpi sa-status-kpi">
          <span>System status</span>
          <strong><span className={`sa-status-dot ${operationalState}`} />{operationalLabel}</strong>
          <span className="sa-subtle">Live score alert runtime: {stockAlertHealth?.status || "checking"} | Last live-score check {formatDateTime(stockAlertHealth?.lastCheckedAt)}</span>
          <span className="sa-subtle">Alert delivery master: {envMasterEnabled ? dashboardAlertsEnabled ? "on" : "paused" : "off by env"}</span>
          <div className="sa-alert-lanes">
            {alertLaneRows.map((lane) => (
              <span className="sa-alert-lane" key={lane.label}>
                <strong>{lane.label}: {formatEnabled(lane.enabled)}</strong>
                <em>{lane.detail}</em>
              </span>
            ))}
          </div>
          <span className="sa-subtle">Database: {persistenceHealth?.provider || "checking"} {persistenceHealth?.dirty ? "pending flush" : persistenceHealth?.ok === false ? "degraded" : "ready"}</span>
          <div className="sa-kpi-actions">
            <button
              className={`sa-btn ${dashboardAlertsEnabled ? "danger" : "secondary"}`}
              type="button"
              onClick={toggleAllAlerts}
              disabled={alertToggleBusy || !dashboardToggleEnabled}
            >
              {alertToggleBusy ? "Updating..." : dashboardAlertsEnabled ? "Pause All Alert Delivery" : "Resume Alert Delivery"}
            </button>
          </div>
          {!dashboardToggleEnabled && <span className="sa-subtle">Dashboard alert toggle disabled by env.</span>}
          {alertToggleMessage && <span className="sa-subtle">{alertToggleMessage}</span>}
          <a className="sa-subtle sa-external-status-link" href={statusPageUrl} target="_blank" rel="noreferrer">Open public uptime monitor</a>
        </div>
        <div className="sa-kpi"><span>Best setups</span><strong>{latestScan?.summary?.bestSetupCount ?? "-"}</strong></div>
        <div className="sa-kpi"><span>Watchlist-quality</span><strong>{latestScan?.summary?.watchCount ?? "-"}</strong></div>
        <div className="sa-kpi"><span>Earnings danger</span><strong>{latestScan?.summary?.earningsDangerCount ?? "-"}</strong></div>
      </section>

      {latestScan && (
        <section className="sa-card" style={{ marginTop: 16 }}>
          <h2>Top Candidates</h2>
          <CandidateTable candidates={latestScan.candidates.slice(0, 12)} />
        </section>
      )}
    </>
  );
}

function ScanPage() {
  const [symbols, setSymbols] = useState("");
  const [limit, setLimit] = useState("75");
  const [scan, setScan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  async function runScan() {
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson("/stock-analyst/scan", {
        method: "POST",
        body: JSON.stringify({ symbols, limit: Number(limit) }),
      });
      setScan(data.report);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function addWatch(symbol) {
    await fetchJson("/stock-analyst/watchlist", {
      method: "POST",
      body: JSON.stringify({ symbol }),
    });
    setMessage(`${symbol} added to watchlist`);
  }

  return (
    <section className="sa-card">
      <h1>Scan</h1>
      <p className="sa-muted">Leave symbols blank to use your configured universe. Comma-separated symbols work well for focused scans.</p>
      <div className="sa-grid-two">
        <label className="sa-field">Symbols<input value={symbols} onChange={(event) => setSymbols(event.target.value)} placeholder="AAPL,NVDA,AMD,PLTR" /></label>
        <label className="sa-field">Max symbols<input value={limit} onChange={(event) => setLimit(event.target.value)} /></label>
      </div>
      <button className="sa-btn" onClick={runScan} disabled={busy}>{busy ? "Analyzing..." : "Analyze"}</button>
      {message && <p className={message.includes("failed") ? "sa-error" : "sa-muted"}>{message}</p>}
      {scan && (
        <>
          <div className="sa-grid" style={{ margin: "18px 0" }}>
            <div className="sa-kpi"><span>Scanned</span><strong>{scan.universe.requested}</strong></div>
            <div className="sa-kpi"><span>Provider</span><strong>{scan.providerSummary.marketProviders.join(", ") || "-"}</strong></div>
            <div className="sa-kpi"><span>Warnings</span><strong>{scan.providerSummary.warnings.length}</strong></div>
          </div>
          <CandidateTable candidates={scan.candidates} onAdd={addWatch} />
        </>
      )}
    </section>
  );
}

function EarningsPage() {
  const [earnings, setEarnings] = useState(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState({
    minDailyVolume: "300000",
    minOptionBidSize: "10",
    minOptionAskSize: "10",
    maxRows: "150",
  });

  async function loadEarnings() {
    setBusy(true);
    setMessage("");
    const params = new URLSearchParams({
      enriched: "true",
      all: "true",
      minDailyVolume: filters.minDailyVolume || "300000",
      minOptionBidSize: filters.minOptionBidSize || "10",
      minOptionAskSize: filters.minOptionAskSize || "10",
      maxRows: filters.maxRows || "150",
    });
    try {
      const data = await fetchJson(`/stock-analyst/earnings?${params.toString()}`);
      setEarnings(data.report);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function saveEarnings() {
    if (!earnings) return;
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson("/stock-analyst/saved-earnings", {
        method: "POST",
        body: JSON.stringify({
          type: "earnings_calendar",
          label: `Earnings ${earnings.from || ""} to ${earnings.to || ""}`.trim(),
          filters,
          report: earnings,
        }),
      });
      setMessage(`Saved ${data.savedReport?.rowCount ?? rows.length} earnings rows. You can reload it from Saved Printouts.`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    loadEarnings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = earnings?.earnings || [];

  return (
    <section className="sa-card">
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div>
          <h1>This Week's Earnings</h1>
          <p className="sa-muted">Filtered for stocks with at least 300,000 daily share volume and active two-sided option activity that meets your threshold.</p>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button className="sa-btn" onClick={loadEarnings} disabled={busy}>{busy ? "Loading..." : "Load This Week"}</button>
          <button className="sa-btn secondary" onClick={saveEarnings} disabled={busy || !earnings}>Save Snapshot</button>
          <button className="sa-btn secondary" onClick={() => exportEarningsCsv(earnings, "earnings-calendar")} disabled={!earnings}>Export CSV</button>
          <Link className="sa-btn secondary" to="/saved-earnings">Saved Printouts</Link>
        </div>
      </div>

      <div className="sa-grid" style={{ margin: "14px 0" }}>
        <label className="sa-field">Min daily share volume<input value={filters.minDailyVolume} onChange={(event) => setFilters({ ...filters, minDailyVolume: event.target.value })} /></label>
        <label className="sa-field">Min option bid activity<input value={filters.minOptionBidSize} onChange={(event) => setFilters({ ...filters, minOptionBidSize: event.target.value })} /></label>
        <label className="sa-field">Min option ask activity<input value={filters.minOptionAskSize} onChange={(event) => setFilters({ ...filters, minOptionAskSize: event.target.value })} /></label>
      </div>

      {earnings && (
        <div className="sa-grid" style={{ marginBottom: 16 }}>
          <div className="sa-kpi"><span>Week</span><strong>{earnings.from} to {earnings.to}</strong></div>
          <div className="sa-kpi"><span>Qualified</span><strong>{earnings.summary?.fullyQualifiedCount ?? 0}</strong></div>
          <div className="sa-kpi"><span>Missing volume</span><strong>{earnings.summary?.marketDataWarningCount ?? 0}</strong></div>
        </div>
      )}

      {earnings?.sourceWarnings?.length > 0 && (
        <div className="sa-list" style={{ marginBottom: 16 }}>
          {earnings.sourceWarnings.map((warning) => <div className="sa-list-item sa-warning" key={warning}>{warning}</div>)}
        </div>
      )}

      <p className="sa-muted">
        Webull option snapshots verify exact quote details when option contract codes are configured. Otherwise, the Nasdaq public option-chain fallback checks active two-sided option rows, volume, and open interest.
      </p>

      {message && <p className={message.startsWith("Saved") ? "sa-muted" : "sa-error"}>{message}</p>}
      <EarningsReportTable report={earnings || { earnings: [] }} busy={busy} />
    </section>
  );
}

function SavedEarningsPage() {
  const [reports, setReports] = useState([]);
  const [active, setActive] = useState(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  async function loadReports() {
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson("/stock-analyst/saved-earnings");
      setReports(data.reports || []);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function loadReport(reportId) {
    if (!reportId) return;
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson(`/stock-analyst/saved-earnings/${encodeURIComponent(reportId)}`);
      setActive(data.savedReport);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function deleteReport(reportId) {
    if (!reportId) return;
    setBusy(true);
    setMessage("");
    try {
      await fetchJson(`/stock-analyst/saved-earnings/${encodeURIComponent(reportId)}`, { method: "DELETE" });
      const data = await fetchJson("/stock-analyst/saved-earnings");
      setReports(data.reports || []);
      setActive(null);
      setMessage("Saved earnings report deleted.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeReport = active?.report || null;
  const activeRows = getEarningsReportRows(activeReport || {});

  return (
    <>
      <section className="sa-card">
        <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <h1>Saved Printouts</h1>
            <p className="sa-muted">Open saved earnings calendars and command center printouts without rebuilding the full research workflow.</p>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="sa-btn" onClick={loadReports} disabled={busy}>{busy ? "Loading..." : "Refresh Saved"}</button>
            <Link className="sa-btn secondary" to="/earnings">Back to Earnings</Link>
            <Link className="sa-btn secondary" to="/earnings-command-center">Command Center</Link>
          </div>
        </div>
        {message && <p className={message.includes("deleted") ? "sa-muted" : "sa-error"}>{message}</p>}
        <div className="sa-grid" style={{ marginTop: 16 }}>
          {reports.map((report) => (
            <button
              type="button"
              className="sa-list-item"
              key={report.reportId}
              onClick={() => loadReport(report.reportId)}
              style={{ textAlign: "left", cursor: "pointer" }}
            >
              <strong>{report.label}</strong>
              <span className={`sa-pill ${report.type === "earnings_command_center" ? "best" : "neutral"}`}>{report.type === "earnings_command_center" ? "Command Center" : "Calendar"}</span>
              <span className="sa-subtle">{report.from || "-"} to {report.to || "-"} / {report.rowCount} rows</span>
              <span className="sa-subtle">Saved {formatDateTime(report.savedAt)}</span>
            </button>
          ))}
          {!busy && reports.length === 0 && <div className="sa-list-item">No saved printouts yet. Run Earnings or the Command Center and save the report.</div>}
        </div>
      </section>

      {active && (
        <section className="sa-card" style={{ marginTop: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
            <div>
              <h2>{active.label}</h2>
              <p className="sa-muted">{activeReport?.from || activeReport?.sourceReport?.from || "-"} to {activeReport?.to || activeReport?.sourceReport?.to || "-"} / {activeRows.length} rows / saved {formatDateTime(active.savedAt)}</p>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className="sa-btn secondary" onClick={() => exportEarningsCsv(activeReport, active.label)}>Export CSV</button>
              <button className="sa-btn secondary" onClick={() => downloadTextFile(`${active.reportId}.json`, JSON.stringify(active, null, 2), "application/json")}>Export JSON</button>
              <button className="sa-btn danger" onClick={() => deleteReport(active.reportId)} disabled={busy}>Delete</button>
            </div>
          </div>
          {active.type !== "earnings_command_center" && <EarningsReportTable report={activeReport || { earnings: [] }} busy={busy} />}
        </section>
      )}
      {active?.type === "earnings_command_center" && <CommandCenterReportBody report={activeReport} />}
    </>
  );
}

function CommandCenterReportBody({ report, onAddWatch = null }) {
  const candidates = report?.candidates || [];
  if (!report) return null;
  return (
    <>
      <section className="sa-card" style={{ marginTop: 16 }}>
        <div className="sa-grid">
          <div className="sa-kpi"><span>Scheduled</span><strong>{report.summary?.scheduledCount ?? 0}</strong></div>
          <div className="sa-kpi"><span>Primary watchlist</span><strong>{report.summary?.primaryWatchlistCount ?? 0}</strong></div>
          <div className="sa-kpi"><span>Follow-only</span><strong>{report.summary?.followOnlyCount ?? 0}</strong></div>
        </div>
        <div className="sa-grid-two" style={{ marginTop: 16 }}>
          <div className="sa-list-item">
            <strong>Open Momentum Rule</strong>
            <p className="sa-muted">Up reaction: long only near RSI {report.strategy?.longEntryRsi}. Down reaction: short only near RSI {report.strategy?.shortEntryRsi}.</p>
          </div>
          <div className="sa-list-item">
            <strong>Reversal Rule</strong>
            <p className="sa-muted">Up reaction reversal short after RSI {report.strategy?.reversalOverboughtRsi}+. Down reaction reversal long after RSI {report.strategy?.reversalOversoldRsi} or lower.</p>
          </div>
          <div className="sa-list-item">
            <strong>VWAP Context</strong>
            <p className="sa-muted">Use VWAP with {report.strategy?.vwapStdDevBand} standard-deviation bands on {(report.strategy?.vwapTimeframes || []).join(", ")}.</p>
          </div>
          <div className="sa-list-item">
            <strong>Reaction Threshold</strong>
            <p className="sa-muted">Start with {report.strategy?.reactionThresholdPercent}% up/down from prior close as the reaction filter before the open.</p>
          </div>
        </div>
      </section>

      <section className="sa-card" style={{ marginTop: 16 }}>
        <h2>Daily Earnings Watchlists</h2>
        <div className="sa-grid">
          {(report.dailyWatchlists || []).map((bucket) => (
            <div className="sa-list-item" key={bucket.date}>
              <strong>{bucket.label} Earnings</strong>
              <p className="sa-muted">{bucket.date}</p>
              <p><strong>Ready:</strong> {bucket.ready.map((item) => item.symbol).join(", ") || "-"}</p>
              <p><strong>Follow:</strong> {bucket.follow.slice(0, 8).map((item) => item.symbol).join(", ") || "-"}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="sa-card" style={{ marginTop: 16 }}>
        <h2>Research Department</h2>
        <div className="sa-grid-two">
          <div>
            <h3>Friday Prep</h3>
            <ul>{(report.researchDepartment?.fridayPrep || []).map((item) => <li key={item}>{item}</li>)}</ul>
          </div>
          <div>
            <h3>Morning Prep</h3>
            <ul>{(report.researchDepartment?.morningPrep || []).map((item) => <li key={item}>{item}</li>)}</ul>
          </div>
        </div>
        <p className="sa-muted">{report.researchDepartment?.disclaimer}</p>
      </section>

      <section className="sa-card" style={{ marginTop: 16 }}>
        <h2>Candidate Workbench</h2>
        <div className="sa-table-wrap">
          <table className="sa-table">
            <thead>
              <tr>
                <th>Trade Day</th>
                <th>Symbol</th>
                <th>Earnings</th>
                <th>Avg Volume</th>
                <th>Option Activity</th>
                <th>Score</th>
                <th>Status</th>
                <th>Open Plan</th>
                {onAddWatch && <th></th>}
              </tr>
            </thead>
            <tbody>
              {candidates.map((candidate) => (
                <tr key={`${candidate.symbol}-${candidate.date}-${candidate.tradeDate}`}>
                  <td>{candidate.tradeDay}<span className="sa-subtle">{candidate.tradeDate}</span></td>
                  <td><Link to={`/ticker/${candidate.symbol}`}><strong>{candidate.symbol}</strong></Link></td>
                  <td>{candidate.date}<span className="sa-subtle">{candidate.readiness?.timing?.time}</span></td>
                  <td>{formatNumber(candidate.averageDailyVolume)}<span className="sa-subtle">{candidate.averageDailyVolumeDays} bars</span></td>
                  <td>{formatNumber(candidate.optionActivity?.maxBidSize)} / {formatNumber(candidate.optionActivity?.maxAskSize)}<span className="sa-subtle">{formatOptionStatus(candidate.optionActivity?.status)}</span></td>
                  <td>{candidate.readiness?.score ?? "-"}</td>
                  <td>
                    {candidate.readiness?.readyForPrimaryWatchlist ? <span className="sa-pill best">Ready</span> : candidate.readiness?.followOnly ? <span className="sa-pill watch">Follow</span> : <span className="sa-pill neutral">Filtered</span>}
                  </td>
                  <td>
                    <span className="sa-subtle">{candidate.playbook?.openMomentumRules?.long}</span>
                    <span className="sa-subtle">{candidate.playbook?.openMomentumRules?.short}</span>
                  </td>
                  {onAddWatch && <td><button className="sa-btn secondary" onClick={() => onAddWatch(candidate)}>Watch</button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function CommandCenterPage() {
  const [report, setReport] = useState(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [selectedDate, setSelectedDate] = useState(upcomingFridayIso());
  const [maxRows, setMaxRows] = useState("75");
  const selectedRange = useMemo(() => weekRangeForIsoDate(selectedDate), [selectedDate]);

  async function waitForCommandCenterJob(jobId) {
    if (!jobId) throw new Error("Command center job did not return a job ID.");
    const maxPolls = 120;
    for (let poll = 0; poll < maxPolls; poll += 1) {
      await sleep(poll === 0 ? 1200 : 3000);
      const data = await fetchJson(`/stock-analyst/earnings-command-center/jobs/${encodeURIComponent(jobId)}`);
      if (data.pending) {
        setMessage(data.job?.message || `Building command center... ${poll + 1}/${maxPolls}`);
        continue;
      }
      if (data.ok === false) {
        throw new Error(data.error || "Command center build failed.");
      }
      setReport(data.report);
      if (data.warning || data.degraded || data.report?.degraded) {
        setMessage(data.warning || data.report?.warning || "Command center loaded in degraded mode.");
      } else {
        setMessage(`Command center ready: ${data.report?.summary?.displayedCount ?? 0} stocks loaded.`);
      }
      return data;
    }
    throw new Error("Command center is still building after 6 minutes. Refresh the job status or reduce Max stocks.");
  }

  async function loadCommandCenter({ refresh = false } = {}) {
    setBusy(true);
    setMessage("");
    try {
      const params = new URLSearchParams({
        from: selectedRange.from,
        to: selectedRange.to,
        maxRows: String(maxRows || "75"),
      });
      if (refresh) params.set("refresh", "true");
      params.set("async", "true");
      const data = await fetchJson(`/stock-analyst/earnings-command-center?${params.toString()}`);
      if (data.pending) {
        setMessage(data.job?.message || "Building command center in the background...");
        await waitForCommandCenterJob(data.jobId);
        return;
      }
      setReport(data.report);
      if (data.warning || data.degraded || data.report?.degraded) {
        setMessage(data.warning || data.report?.warning || "Command center loaded in degraded mode.");
      } else {
        setMessage("");
      }
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function addWatch(candidate) {
    await fetchJson("/stock-analyst/watchlist", {
      method: "POST",
      body: JSON.stringify({
        symbol: candidate.symbol,
        source: "earnings_command_center",
        tags: ["earnings", candidate.tradeDay, candidate.readiness?.timing?.time].filter(Boolean),
        earningsDate: candidate.date,
        tradeDate: candidate.tradeDate,
        strategy: "earnings_open_reaction",
        notes: candidate.playbook?.journalPrompt || "",
      }),
    });
    setMessage(`${candidate.symbol} added to watchlist for ${candidate.tradeDay}`);
  }

  async function addAllReadyToWatchlist() {
    if (!report) return;
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson("/stock-analyst/watchlist/from-command-center", {
        method: "POST",
        body: JSON.stringify({
          generatedAt: report.generatedAt,
          readyOnly: true,
        }),
      });
      setMessage(`Added ${data.addedCount || 0} passing stocks to ${data.watchlistNames?.join(", ") || "earnings watchlists"}.`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function saveCommandCenter() {
    if (!report) return;
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson("/stock-analyst/earnings-command-center/save", {
        method: "POST",
        body: JSON.stringify({
          label: `Earnings Command Center ${report.sourceReport?.from || ""} to ${report.sourceReport?.to || ""}`.trim(),
          generatedAt: report.generatedAt,
          filters: { source: "earnings_command_center", selectedDate, selectedRange, maxRows },
        }),
      });
      setMessage(`Saved command center printout with ${data.savedReport?.rowCount ?? report.candidates?.length ?? 0} candidates.`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    loadCommandCenter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <section className="sa-card">
        <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <h1>Earnings Command Center</h1>
            <p className="sa-muted">Friday prep, daily watchlists, open-reaction rules, reversal rules, and journal prompts built around your earnings routine.</p>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="sa-btn" onClick={() => loadCommandCenter({ refresh: true })} disabled={busy}>{busy ? "Building..." : "Refresh Command Center"}</button>
            <button className="sa-btn secondary" onClick={saveCommandCenter} disabled={busy || !report}>Save Friday Printout</button>
            <button className="sa-btn secondary" onClick={addAllReadyToWatchlist} disabled={busy || !report}>Add All Passing To Watchlists</button>
            <Link className="sa-btn secondary" to="/saved-earnings">Saved Printouts</Link>
          </div>
        </div>
        <div className="sa-form-row" style={{ marginTop: 14 }}>
          <label className="sa-field">Pick any date in the earnings week
            <input type="date" value={selectedDate} onChange={(event) => setSelectedDate(event.target.value)} />
          </label>
          <label className="sa-field">Max stocks
            <select value={maxRows} onChange={(event) => setMaxRows(event.target.value)}>
              <option value="50">50 fastest</option>
              <option value="75">75 balanced</option>
              <option value="100">100 broader</option>
              <option value="150">150 full</option>
            </select>
          </label>
          <button className="sa-btn secondary" type="button" onClick={() => loadCommandCenter({ refresh: true })} disabled={busy}>
            {busy ? "Loading..." : "Load Selected Week"}
          </button>
          <span className="sa-muted">Requesting {selectedRange.from} through {selectedRange.to}.</span>
        </div>
        {message && <p className={message.includes("failed") || message.includes("required") ? "sa-error" : "sa-muted"}>{message}</p>}
      </section>
      <CommandCenterReportBody report={report} onAddWatch={addWatch} />
    </>
  );
}

function HourlyScannerPage() {
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState({});
  const [report, setReport] = useState(null);
  const [runtime, setRuntime] = useState(null);
  const [slot, setSlot] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [watchlistOptions, setWatchlistOptions] = useState([]);

  async function loadHourlyScanner() {
    const data = await fetchJson("/stock-analyst/hourly-scanner");
    setSettings(data.settings || {});
    setDraft(data.settings || {});
    setReport(data.latestReport || null);
    setRuntime(data.runtime || null);
    setSlot(data.slot || null);
    setWatchlistOptions(data.watchlistOptions || []);
  }

  useEffect(() => {
    loadHourlyScanner().catch((error) => setMessage(error.message));
  }, []);

  async function saveSettings() {
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson("/stock-analyst/hourly-scanner/settings", {
        method: "POST",
        body: JSON.stringify({ settings: draft }),
      });
      setSettings(data.settings || {});
      setDraft(data.settings || {});
      setWatchlistOptions(data.watchlistOptions || []);
      setMessage("Hourly scanner settings saved.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function waitForHourlyScannerJob(jobId) {
    let poll = 0;
    while (true) {
      await sleep(poll === 0 ? 1200 : 3000);
      let data = null;
      try {
        data = await fetchJson(`/stock-analyst/hourly-scanner/jobs/${encodeURIComponent(jobId)}`);
      } catch (error) {
        setMessage(`Hourly scanner still running. Last status check failed: ${error.message}`);
        poll += 1;
        continue;
      }
      setJob(data.job || null);
      if (data.pending) {
        const progress = data.job?.progress;
        const count = progress?.total ? ` ${progress.completed || 0}/${progress.total}` : "";
        setMessage(data.job?.message || `Hourly scanner running${count}...`);
        poll += 1;
        continue;
      }
      if (data.ok === false) throw new Error(data.error || "Hourly scanner failed.");
      setReport(data.report || null);
      setDeliveries(data.deliveries || []);
      setJob(data.job || null);
      setMessage(`Hourly scanner ready: ${data.report?.summary?.topCount || 0} live-score candidates, ${data.report?.summary?.indicatorModuleCount || 0} indicator module(s).`);
      return data;
    }
  }

  async function runNow({ sendAlert = true } = {}) {
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson("/stock-analyst/hourly-scanner/run", {
        method: "POST",
        body: JSON.stringify({ settings: draft, sendAlert }),
      });
      setJob(data.job || null);
      setMessage(data.job?.message || "Hourly scanner queued.");
      await waitForHourlyScannerJob(data.jobId);
      await loadHourlyScanner().catch(() => null);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  const top = report?.top || [];
  const candidates = report?.candidates || [];
  const indicatorModules = report?.modules?.indicators || [];
  const deliveryText = Array.isArray(draft.deliveryChannels) ? draft.deliveryChannels.join(",") : String(draft.deliveryChannels || "SMS");
  const progress = job?.progress || null;
  const universeSources = Array.isArray(draft.universeSources) ? draft.universeSources : String(draft.universeSources || "QQQ,SP500").split(",").map((item) => item.trim()).filter(Boolean);
  const customSymbolsText = Array.isArray(draft.customSymbols) ? draft.customSymbols.join(",") : String(draft.customSymbols || "");
  const setUniverseSource = (source, enabled) => {
    const next = new Set(universeSources);
    if (enabled) next.add(source);
    else next.delete(source);
    setDraft({ ...draft, universeSources: Array.from(next) });
  };
  const scannerIndicatorOptions = ["LIVE_SCORE", "LIVE_SCORE_LONG", "LIVE_SCORE_SHORT", "RSI", "EMA", "VWAP", "VWAP_UPPER_BAND", "VWAP_LOWER_BAND", "MACD", "MACD_SIGNAL", "MACD_HISTOGRAM", "TSI", "TSI_SIGNAL", "TSI_HISTOGRAM", "SMI", "SMI_SIGNAL", "SMI_HISTOGRAM", "PPO", "PPO_SIGNAL", "PPO_HISTOGRAM", "BOLLINGER_UPPER", "BOLLINGER_LOWER", "BOLLINGER_BANDWIDTH", "STOCHASTIC_K", "STOCHASTIC_D", "STOCH_RSI_K", "STOCH_RSI_D", "MFI", "ADX", "PLUS_DI", "MINUS_DI", "RELATIVE_VOLUME"];
  const scannerConditionOptions = ["AT_OR_ABOVE", "AT_OR_BELOW", "CROSSING_UP", "CROSSING_DOWN", "ABOVE_SIGNAL", "BELOW_SIGNAL", "CROSSING_ABOVE_SIGNAL", "CROSSING_BELOW_SIGNAL"];
  const scannerModeOptions = [
    { value: "CONDITION", label: "Condition match" },
    { value: "SIGNAL_CROSS", label: "Signal crosses" },
    { value: "EXTREMES", label: "Top overbought + oversold" },
  ];
  const scannerIndicatorModules = Array.isArray(draft.indicatorModules) ? draft.indicatorModules : [];
  function defaultScannerParams(indicator) {
    const normalized = String(indicator || "").toUpperCase();
    if (normalized.startsWith("LIVE_SCORE")) return {};
    if (normalized === "RSI") return { period: 14 };
    if (normalized === "EMA") return { period: 22 };
    if (normalized.startsWith("VWAP")) return { bandMultiplier: 1.25 };
    if (normalized.startsWith("MACD")) return { fast: 12, slow: 26, signal: 9 };
    if (normalized.startsWith("TSI")) return { long: 25, short: 13, signal: 13 };
    if (normalized.startsWith("SMI")) return { lookback: 20, smooth: 5, signal: 5 };
    if (normalized.startsWith("PPO")) return { slow: 22, fast: 9, signal: 9 };
    if (normalized.startsWith("BOLLINGER")) return { period: 20, multiplier: 2 };
    if (normalized.startsWith("STOCH_RSI")) return { period: 14, k: 3, d: 3 };
    if (normalized.startsWith("STOCHASTIC")) return { k: 14, d: 3 };
    if (normalized === "MFI") return { period: 14 };
    if (normalized === "ADX" || normalized === "PLUS_DI" || normalized === "MINUS_DI") return { period: 14 };
    if (normalized === "RELATIVE_VOLUME") return { lookback: 20 };
    return {};
  }
  function scannerParameterFields(indicator) {
    const normalized = String(indicator || "").toUpperCase();
    if (normalized.startsWith("LIVE_SCORE")) return [];
    if (normalized === "RSI" || normalized === "EMA" || normalized === "MFI" || normalized === "ADX" || normalized === "PLUS_DI" || normalized === "MINUS_DI") return [{ key: "period", label: "Period", step: "1" }];
    if (normalized.startsWith("VWAP")) return [{ key: "bandMultiplier", label: "Band multiplier", step: "0.05" }];
    if (normalized.startsWith("MACD")) return [
      { key: "fast", label: "Fast", step: "1" },
      { key: "slow", label: "Slow", step: "1" },
      { key: "signal", label: "Signal", step: "1" },
    ];
    if (normalized.startsWith("TSI")) return [
      { key: "long", label: "Long", step: "1" },
      { key: "short", label: "Short", step: "1" },
      { key: "signal", label: "Signal", step: "1" },
    ];
    if (normalized.startsWith("SMI")) return [
      { key: "lookback", label: "Lookback", step: "1" },
      { key: "smooth", label: "Smooth", step: "1" },
      { key: "signal", label: "Signal", step: "1" },
    ];
    if (normalized.startsWith("PPO")) return [
      { key: "slow", label: "Slow", step: "1" },
      { key: "fast", label: "Fast", step: "1" },
      { key: "signal", label: "Signal", step: "1" },
    ];
    if (normalized.startsWith("BOLLINGER")) return [
      { key: "period", label: "Period", step: "1" },
      { key: "multiplier", label: "Multiplier", step: "0.1" },
    ];
    if (normalized.startsWith("STOCH_RSI")) return [
      { key: "period", label: "Period", step: "1" },
      { key: "k", label: "%K", step: "1" },
      { key: "d", label: "%D", step: "1" },
    ];
    if (normalized.startsWith("STOCHASTIC")) return [
      { key: "k", label: "%K", step: "1" },
      { key: "d", label: "%D", step: "1" },
    ];
    if (normalized === "RELATIVE_VOLUME") return [{ key: "lookback", label: "Lookback", step: "1" }];
    return [];
  }
  function updateScannerModule(index, changes) {
    const next = scannerIndicatorModules.map((module, moduleIndex) => (
      moduleIndex === index ? { ...module, ...changes } : module
    ));
    setDraft({ ...draft, indicatorModules: next });
  }
  function setScannerModuleIndicator(index, indicator) {
    const normalized = String(indicator || "").toUpperCase();
    const liveScoreDefaults = normalized.startsWith("LIVE_SCORE")
      ? {
        mode: "CONDITION",
        condition: "AT_OR_ABOVE",
        threshold: 80,
        thresholdRankBy: "FARTHEST",
        crossDistanceRank: "FARTHEST",
      }
      : {};
    updateScannerModule(index, {
      indicator,
      params: defaultScannerParams(indicator),
      crossClosenessTarget: "AUTO",
      ...liveScoreDefaults,
    });
  }
  function updateScannerModuleParam(index, key, value) {
    const current = scannerIndicatorModules[index] || {};
    updateScannerModule(index, {
      params: {
        ...(current.params || {}),
        [key]: value === "" ? "" : Number(value),
      },
    });
  }
  function defaultCrossDistanceRankForModule(module = {}) {
    const mode = String(module.mode || "").toUpperCase();
    const condition = String(module.condition || "").toUpperCase();
    const target = String(module.crossClosenessTarget || "AUTO").toUpperCase();
    const threshold = Number(module.threshold);
    if (
      mode === "SIGNAL_CROSS" ||
      condition.includes("SIGNAL") ||
      target === "ZERO" ||
      (["CROSSING_UP", "CROSSING_DOWN"].includes(condition) && threshold === 0)
    ) return "CLOSEST";
    if (["CROSSING_UP", "CROSSING_DOWN"].includes(condition)) return "FARTHEST";
    return "CLOSEST";
  }
  function addScannerModule() {
    const index = scannerIndicatorModules.length + 1;
    const moduleId = `custom_indicator_${index}`;
    setDraft({
      ...draft,
      indicatorModules: [
        ...scannerIndicatorModules,
        {
          moduleId,
          label: `Custom indicator ${index}`,
          enabled: true,
          mode: "CONDITION",
          indicator: "RSI",
          condition: "AT_OR_ABOVE",
          threshold: 70,
          timeframe: "1m",
          outputSize: 390,
          resultCount: 5,
          resultMode: "SPLIT",
          crossRankBy: "FRESHNESS",
          crossClosenessTarget: "AUTO",
          crossLookbackCandles: 1,
          thresholdRankBy: "FARTHEST",
          params: { period: 14 },
        },
      ],
    });
  }
  function removeScannerModule(index) {
    setDraft({
      ...draft,
      indicatorModules: scannerIndicatorModules.filter((_, moduleIndex) => moduleIndex !== index),
    });
  }
  const renderIndicatorRows = (rows = []) => rows.slice(0, 10).map((row, index) => {
    const crossLabel = row.crossSide === "BULLISH" ? "Bull cross" : row.crossSide === "BEARISH" ? "Bear cross" : row.cross ? "Cross" : "";
    const barsAgo = row.cross?.barsAgo !== null && row.cross?.barsAgo !== undefined ? ` (${row.cross.barsAgo} bar${Number(row.cross.barsAgo) === 1 ? "" : "s"} ago)` : "";
    const status = crossLabel
      ? `${crossLabel}${barsAgo}`
      : row.matched || row.bullishMatched || row.bearishMatched ? "Matched" : "Watch";
    return (
      <tr key={`${row.moduleId}-${row.symbol}-${index}`}>
        <td>{index + 1}</td>
        <td><Link to={`/ticker/${row.symbol}`}><strong>{row.symbol}</strong></Link></td>
        <td>{row.side ? `${row.indicator} ${row.side}` : row.indicator}</td>
        <td>{Number(row.value).toFixed(Math.abs(Number(row.value)) >= 10 ? 1 : 2)}</td>
        <td>{row.signal === null || row.signal === undefined ? "-" : Number(row.signal).toFixed(Math.abs(Number(row.signal)) >= 10 ? 1 : 2)}</td>
        <td>{status}</td>
      </tr>
    );
  });

  return (
    <>
      <section className="sa-card">
        <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <h1>Hourly Scanner</h1>
            <p className="sa-muted">Scans before the 9:30, 10:30, 11:30, 12:30, 1:30, 2:30, and 3:30 ET candles and ranks the best long/short candidates for that hour.</p>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="sa-btn" type="button" onClick={() => runNow({ sendAlert: true })} disabled={busy}>{busy ? "Scanning..." : "Run + Send Alert"}</button>
            <button className="sa-btn secondary" type="button" onClick={() => runNow({ sendAlert: false })} disabled={busy}>{busy ? "Scanning..." : "Run Without Alert"}</button>
            <button className="sa-btn secondary" type="button" onClick={saveSettings} disabled={busy || !settings}>{busy ? "Saving..." : "Save Settings"}</button>
          </div>
        </div>
        <div className="sa-grid-three" style={{ marginTop: 14 }}>
          <div className="sa-kpi"><span>Next scan slot</span><strong>{slot?.activeSlot || slot?.nextScanSlot || "-"}</strong></div>
          <div className="sa-kpi"><span>Runtime</span><strong>{runtime?.enabled ? "Enabled" : "Disabled"}</strong></div>
          <div className="sa-kpi"><span>Last checked</span><strong>{formatDateTime(runtime?.lastCheckedAt)}</strong></div>
        </div>
        {message && <p className={message.toLowerCase().includes("failed") || message.toLowerCase().includes("error") ? "sa-error" : "sa-muted"}>{message}</p>}
        {progress && (
          <div className="sa-progress" style={{ marginTop: 12 }}>
            <div>
              <strong>{String(progress.stage || "running").toUpperCase()}</strong>
              <span>{progress.message || "Hourly scanner running."}</span>
            </div>
            <span>{progress.total ? `${progress.completed || 0}/${progress.total}` : ""}</span>
          </div>
        )}
      </section>

      {top.length > 0 && (
        <section className="sa-card" style={{ marginTop: 16 }}>
          <h2>Top {top.length} For This Hour</h2>
          <div className="sa-grid-five">
            {top.map((candidate, index) => (
              <div className="sa-kpi" key={`${candidate.symbol}-${candidate.side}-${index}`}>
                <span>#{index + 1} {candidate.side}</span>
                <strong>{candidate.symbol}</strong>
                <span className="sa-subtle">Score {Math.round(Number(candidate.activeScore || 0))} | L {Math.round(Number(candidate.longScore || 0))} / S {Math.round(Number(candidate.shortScore || 0))}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {indicatorModules.length > 0 && (
        <section className="sa-card" style={{ marginTop: 16 }}>
          <h2>Indicator Scan Results</h2>
          <div className="sa-grid-two">
            {indicatorModules.map((module) => (
              <div className="sa-kpi" key={module.moduleId}>
                <span>{module.label} / {module.timeframe}</span>
                <strong>{module.indicator}</strong>
                <span className="sa-subtle">Scanned {module.summary?.scannedCount || 0}; matches {module.summary?.matchCount || 0}; errors {module.summary?.errorCount || 0}</span>
              </div>
            ))}
          </div>
          {indicatorModules.map((module) => (
            <div key={`${module.moduleId}-table`} style={{ marginTop: 18 }}>
              <h3>{module.label}</h3>
              <div className="sa-grid-two">
                {module.groups?.overbought && (
                  <div>
                    <h4>Top Overbought</h4>
                    <div className="sa-table-wrap">
                      <table className="sa-table">
                        <thead><tr><th>#</th><th>Symbol</th><th>Indicator</th><th>Value</th><th>Signal</th><th>Status</th></tr></thead>
                        <tbody>{renderIndicatorRows(module.groups.overbought)}</tbody>
                      </table>
                    </div>
                  </div>
                )}
                {module.groups?.oversold && (
                  <div>
                    <h4>Top Oversold</h4>
                    <div className="sa-table-wrap">
                      <table className="sa-table">
                        <thead><tr><th>#</th><th>Symbol</th><th>Indicator</th><th>Value</th><th>Signal</th><th>Status</th></tr></thead>
                        <tbody>{renderIndicatorRows(module.groups.oversold)}</tbody>
                      </table>
                    </div>
                  </div>
                )}
                {module.groups?.bullish && (
                  <div>
                    <h4>Bullish Crosses</h4>
                    <div className="sa-table-wrap">
                      <table className="sa-table">
                        <thead><tr><th>#</th><th>Symbol</th><th>Indicator</th><th>Value</th><th>Signal</th><th>Status</th></tr></thead>
                        <tbody>{renderIndicatorRows(module.groups.bullish)}</tbody>
                      </table>
                    </div>
                  </div>
                )}
                {module.groups?.bearish && (
                  <div>
                    <h4>Bearish Crosses</h4>
                    <div className="sa-table-wrap">
                      <table className="sa-table">
                        <thead><tr><th>#</th><th>Symbol</th><th>Indicator</th><th>Value</th><th>Signal</th><th>Status</th></tr></thead>
                        <tbody>{renderIndicatorRows(module.groups.bearish)}</tbody>
                      </table>
                    </div>
                  </div>
                )}
                {module.groups?.crosses && (
                  <div>
                    <h4>Freshest Crosses</h4>
                    <div className="sa-table-wrap">
                      <table className="sa-table">
                        <thead><tr><th>#</th><th>Symbol</th><th>Indicator</th><th>Value</th><th>Signal</th><th>Status</th></tr></thead>
                        <tbody>{renderIndicatorRows(module.groups.crosses)}</tbody>
                      </table>
                    </div>
                  </div>
                )}
                {module.groups?.matches && (
                  <div>
                    <h4>Matches</h4>
                    <div className="sa-table-wrap">
                      <table className="sa-table">
                        <thead><tr><th>#</th><th>Symbol</th><th>Indicator</th><th>Value</th><th>Signal</th><th>Status</th></tr></thead>
                        <tbody>{renderIndicatorRows(module.groups.matches)}</tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </section>
      )}

      <section className="sa-card" style={{ marginTop: 16 }}>
        <h2>Module Settings</h2>
        <div className="sa-grid-two">
          <label className="sa-field">Enabled
            <select value={draft.enabled === false ? "false" : "true"} onChange={(event) => setDraft({ ...draft, enabled: event.target.value === "true" })}>
              <option value="true">Enabled</option>
              <option value="false">Disabled</option>
            </select>
          </label>
          <label className="sa-field">Top tickers to show/send<input value={draft.resultCount ?? ""} onChange={(event) => setDraft({ ...draft, resultCount: Number(event.target.value) })} /></label>
          <div className="sa-field">
            <span>Scan universe sources</span>
            <label><input type="checkbox" checked={universeSources.includes("MARKET_CAP")} onChange={(event) => setUniverseSource("MARKET_CAP", event.target.checked)} /> Top market cap</label>
            <label><input type="checkbox" checked={universeSources.includes("NASDAQ")} onChange={(event) => setUniverseSource("NASDAQ", event.target.checked)} /> Nasdaq top list</label>
            <label><input type="checkbox" checked={universeSources.includes("SP500")} onChange={(event) => setUniverseSource("SP500", event.target.checked)} /> S&P 500 top weight</label>
            <label><input type="checkbox" checked={universeSources.includes("QQQ")} onChange={(event) => setUniverseSource("QQQ", event.target.checked)} /> QQQ top weight</label>
            <label><input type="checkbox" checked={universeSources.includes("WATCHLIST")} onChange={(event) => setUniverseSource("WATCHLIST", event.target.checked)} /> Saved watchlist</label>
            <label><input type="checkbox" checked={universeSources.includes("CUSTOM")} onChange={(event) => setUniverseSource("CUSTOM", event.target.checked)} /> Custom symbols</label>
          </div>
          {universeSources.includes("WATCHLIST") && (
            <label className="sa-field">Saved watchlist to scan
              <select value={draft.watchlistName || ""} onChange={(event) => setDraft({ ...draft, watchlistName: event.target.value })}>
                <option value="">All saved watchlists</option>
                {watchlistOptions.map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
            </label>
          )}
          <label className="sa-field">Top market-cap count<input value={draft.marketCapCount ?? ""} onChange={(event) => setDraft({ ...draft, marketCapCount: Number(event.target.value) })} /></label>
          <label className="sa-field">Nasdaq export count<input value={draft.nasdaqCount ?? ""} onChange={(event) => setDraft({ ...draft, nasdaqCount: Number(event.target.value) })} /></label>
          <label className="sa-field">QQQ top-weight count<input value={draft.qqqCount ?? ""} onChange={(event) => setDraft({ ...draft, qqqCount: Number(event.target.value) })} /></label>
          <label className="sa-field">S&P 500 top-weight count<input value={draft.sp500Count ?? ""} onChange={(event) => setDraft({ ...draft, sp500Count: Number(event.target.value) })} /></label>
          <label className="sa-field">Custom symbols<textarea value={customSymbolsText} onChange={(event) => setDraft({ ...draft, customSymbols: event.target.value.toUpperCase() })} placeholder="SPX,SPY,AMZN,NVDA" /></label>
          <label className="sa-field">Live score scan
            <select value={draft.liveScoreScanEnabled === false ? "false" : "true"} onChange={(event) => setDraft({ ...draft, liveScoreScanEnabled: event.target.value === "true" })}>
              <option value="true">Enabled</option>
              <option value="false">Disabled</option>
            </select>
          </label>
          <label className="sa-field">RSI overbought/oversold scan
            <select value={draft.rsiScanEnabled ? "true" : "false"} onChange={(event) => setDraft({ ...draft, rsiScanEnabled: event.target.value === "true" })}>
              <option value="false">Disabled</option>
              <option value="true">Enabled</option>
            </select>
          </label>
          <label className="sa-field">RSI timeframe<input value={draft.rsiTimeframe || "1m"} onChange={(event) => setDraft({ ...draft, rsiTimeframe: event.target.value })} /></label>
          <label className="sa-field">RSI period<input value={draft.rsiPeriod ?? ""} onChange={(event) => setDraft({ ...draft, rsiPeriod: Number(event.target.value) })} /></label>
          <label className="sa-field">RSI overbought<input value={draft.rsiOverboughtThreshold ?? ""} onChange={(event) => setDraft({ ...draft, rsiOverboughtThreshold: Number(event.target.value) })} /></label>
          <label className="sa-field">RSI oversold<input value={draft.rsiOversoldThreshold ?? ""} onChange={(event) => setDraft({ ...draft, rsiOversoldThreshold: Number(event.target.value) })} /></label>
          <label className="sa-field">RSI result count<input value={draft.rsiResultCount ?? ""} onChange={(event) => setDraft({ ...draft, rsiResultCount: Number(event.target.value) })} /></label>
          <label className="sa-field">RSI ranking
            <select value={draft.rsiThresholdRankBy || "FARTHEST"} onChange={(event) => setDraft({ ...draft, rsiThresholdRankBy: event.target.value })}>
              <option value="FARTHEST">Farthest from threshold</option>
              <option value="CLOSEST">Closest to threshold</option>
              <option value="UNIVERSE">Universe order</option>
            </select>
          </label>
          <label className="sa-field">PPO signal-cross scan
            <select value={draft.ppoScanEnabled ? "true" : "false"} onChange={(event) => setDraft({ ...draft, ppoScanEnabled: event.target.value === "true" })}>
              <option value="false">Disabled</option>
              <option value="true">Enabled</option>
            </select>
          </label>
          <label className="sa-field">PPO timeframe<input value={draft.ppoTimeframe || "2m"} onChange={(event) => setDraft({ ...draft, ppoTimeframe: event.target.value })} /></label>
          <label className="sa-field">PPO slow<input value={draft.ppoSlow ?? ""} onChange={(event) => setDraft({ ...draft, ppoSlow: Number(event.target.value) })} /></label>
          <label className="sa-field">PPO fast<input value={draft.ppoFast ?? ""} onChange={(event) => setDraft({ ...draft, ppoFast: Number(event.target.value) })} /></label>
          <label className="sa-field">PPO signal<input value={draft.ppoSignal ?? ""} onChange={(event) => setDraft({ ...draft, ppoSignal: Number(event.target.value) })} /></label>
          <label className="sa-field">PPO return mode
            <select value={draft.ppoResultMode || "SPLIT"} onChange={(event) => setDraft({ ...draft, ppoResultMode: event.target.value })}>
              <option value="SPLIT">Bullish + bearish lists</option>
              <option value="COMBINED">One combined list</option>
            </select>
          </label>
          <label className="sa-field">PPO cross ranking
            <select value={draft.ppoCrossRankBy || "FRESHNESS"} onChange={(event) => setDraft({ ...draft, ppoCrossRankBy: event.target.value })}>
              <option value="FRESHNESS">Freshest cross first</option>
              <option value="CLOSENESS">Closest to cross target first</option>
              <option value="UNIVERSE">Universe rank first</option>
            </select>
          </label>
          <label className="sa-field">PPO closeness target
            <select value={draft.ppoCrossClosenessTarget || "AUTO"} onChange={(event) => setDraft({ ...draft, ppoCrossClosenessTarget: event.target.value })}>
              <option value="AUTO">Auto: crossing trigger</option>
              <option value="ZERO">Zero line</option>
              <option value="SIGNAL">Signal line</option>
              <option value="THRESHOLD">Threshold</option>
            </select>
          </label>
          <label className="sa-field">PPO distance preference
            <select value={draft.ppoCrossDistanceRank || "CLOSEST"} onChange={(event) => setDraft({ ...draft, ppoCrossDistanceRank: event.target.value })}>
              <option value="CLOSEST">Closest to target</option>
              <option value="FARTHEST">Farthest from target</option>
            </select>
          </label>
          <label className="sa-field">PPO cross lookback candles<input value={draft.ppoCrossLookbackCandles ?? 1} onChange={(event) => setDraft({ ...draft, ppoCrossLookbackCandles: Number(event.target.value) })} /></label>
          <label className="sa-field">PPO result count<input value={draft.ppoResultCount ?? ""} onChange={(event) => setDraft({ ...draft, ppoResultCount: Number(event.target.value) })} /></label>
          <div className="sa-field sa-form-span">
            <span>Custom indicator scan categories</span>
            <p className="sa-muted" style={{ marginTop: 0 }}>Add extra hourly scanner categories here when you want the same universe scanned for a specific indicator setup.</p>
            <div className="sa-list">
              {scannerIndicatorModules.map((module, index) => {
                const fields = scannerParameterFields(module.indicator);
                const mode = String(module.mode || "CONDITION").toUpperCase();
                const moduleCondition = String(module.condition || "").toUpperCase();
                const moduleIsCross = mode === "SIGNAL_CROSS" || (mode === "CONDITION" && moduleCondition.startsWith("CROSSING_"));
                return (
                  <div className="sa-list-item" key={module.moduleId || index}>
                    <div className="sa-grid-two" style={{ width: "100%" }}>
                      <label className="sa-field">Category name
                        <input value={module.label || ""} onChange={(event) => updateScannerModule(index, { label: event.target.value })} placeholder="2m PPO cross" />
                      </label>
                      <label className="sa-field">Status
                        <select value={module.enabled === false ? "false" : "true"} onChange={(event) => updateScannerModule(index, { enabled: event.target.value === "true" })}>
                          <option value="true">Enabled</option>
                          <option value="false">Disabled</option>
                        </select>
                      </label>
                      <label className="sa-field">Scan type
                        <select value={mode} onChange={(event) => updateScannerModule(index, { mode: event.target.value })}>
                          {scannerModeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                      </label>
                      {mode === "SIGNAL_CROSS" && (
                        <>
                          <label className="sa-field">Return mode
                            <select value={module.resultMode || "SPLIT"} onChange={(event) => updateScannerModule(index, { resultMode: event.target.value })}>
                              <option value="SPLIT">Bullish + bearish lists</option>
                              <option value="COMBINED">One combined list</option>
                            </select>
                          </label>
                          <label className="sa-field">Cross ranking
                            <select value={module.crossRankBy || "FRESHNESS"} onChange={(event) => updateScannerModule(index, { crossRankBy: event.target.value })}>
                              <option value="FRESHNESS">Freshest cross first</option>
                              <option value="CLOSENESS">Closest to target first</option>
                              <option value="UNIVERSE">Universe rank first</option>
                            </select>
                          </label>
                          <label className="sa-field">Closeness target
                            <select value={module.crossClosenessTarget || "AUTO"} onChange={(event) => updateScannerModule(index, { crossClosenessTarget: event.target.value })}>
                              <option value="AUTO">Auto: crossing trigger</option>
                              <option value="ZERO">Zero line</option>
                              <option value="SIGNAL">Signal line</option>
                              <option value="THRESHOLD">Threshold</option>
                            </select>
                          </label>
                        </>
                      )}
                      <label className="sa-field">Indicator
                        <select value={module.indicator || "RSI"} onChange={(event) => setScannerModuleIndicator(index, event.target.value)}>
                          {scannerIndicatorOptions.map((indicator) => <option key={indicator} value={indicator}>{indicator.replace(/_/g, " ")}</option>)}
                        </select>
                      </label>
                      {mode === "CONDITION" && (
                        <>
                          <label className="sa-field">Condition
                            <select value={module.condition || "AT_OR_ABOVE"} onChange={(event) => updateScannerModule(index, { condition: event.target.value })}>
                              {scannerConditionOptions.map((condition) => <option key={condition} value={condition}>{condition.replace(/_/g, " ").toLowerCase()}</option>)}
                            </select>
                          </label>
                          <label className="sa-field">Threshold
                            <input value={module.threshold ?? ""} onChange={(event) => updateScannerModule(index, { threshold: event.target.value === "" ? "" : Number(event.target.value) })} />
                          </label>
                        </>
                      )}
                      {mode === "EXTREMES" && (
                        <>
                          <label className="sa-field">Overbought level
                            <input value={module.overboughtThreshold ?? 70} onChange={(event) => updateScannerModule(index, { overboughtThreshold: Number(event.target.value) })} />
                          </label>
                          <label className="sa-field">Oversold level
                            <input value={module.oversoldThreshold ?? 30} onChange={(event) => updateScannerModule(index, { oversoldThreshold: Number(event.target.value) })} />
                          </label>
                        </>
                      )}
                      {(mode === "CONDITION" || mode === "EXTREMES") && (
                        <label className="sa-field">Threshold ranking
                          <select value={module.thresholdRankBy || "FARTHEST"} onChange={(event) => updateScannerModule(index, { thresholdRankBy: event.target.value })}>
                            <option value="FARTHEST">Farthest from threshold</option>
                            <option value="CLOSEST">Closest to threshold</option>
                            <option value="UNIVERSE">Universe order</option>
                          </select>
                        </label>
                      )}
                      {moduleIsCross && (
                        <label className="sa-field">Cross distance preference
                          <select value={module.crossDistanceRank || defaultCrossDistanceRankForModule(module)} onChange={(event) => updateScannerModule(index, { crossDistanceRank: event.target.value })}>
                            <option value="CLOSEST">Closest to target</option>
                            <option value="FARTHEST">Farthest from target</option>
                          </select>
                        </label>
                      )}
                      <label className="sa-field">Timeframe
                        <select value={module.timeframe || "1m"} onChange={(event) => updateScannerModule(index, { timeframe: event.target.value })}>
                          {["1m", "2m", "5m", "10m", "15m", "30m", "1h", "daily", "weekly", "monthly"].map((timeframe) => <option key={timeframe} value={timeframe}>{timeframe}</option>)}
                        </select>
                      </label>
                      <label className="sa-field">Result count
                        <input value={module.resultCount ?? 5} onChange={(event) => updateScannerModule(index, { resultCount: Number(event.target.value) })} />
                      </label>
                      <label className="sa-field">Cross lookback candles
                        <input value={module.crossLookbackCandles ?? 1} onChange={(event) => updateScannerModule(index, { crossLookbackCandles: Number(event.target.value) })} />
                      </label>
                      <label className="sa-field">Bars
                        <input value={module.outputSize ?? 390} onChange={(event) => updateScannerModule(index, { outputSize: Number(event.target.value) })} />
                      </label>
                      {fields.map((field) => (
                        <label className="sa-field" key={`${module.moduleId || index}-${field.key}`}>{field.label}
                          <input
                            type="number"
                            step={field.step || "1"}
                            value={module.params?.[field.key] ?? ""}
                            onChange={(event) => updateScannerModuleParam(index, field.key, event.target.value)}
                          />
                        </label>
                      ))}
                    </div>
                    <div className="sa-actions" style={{ marginTop: 10 }}>
                      <button className="sa-btn danger" type="button" onClick={() => removeScannerModule(index)}>Remove Category</button>
                    </div>
                  </div>
                );
              })}
              {!scannerIndicatorModules.length && <p className="sa-muted">No custom indicator scan categories yet. RSI and PPO presets above can run without adding a custom module.</p>}
            </div>
            <button className="sa-btn secondary" type="button" onClick={addScannerModule} style={{ marginTop: 10 }}>Add Indicator Category</button>
          </div>
          <label className="sa-field">Delivery
            <select value={deliveryText} onChange={(event) => setDraft({ ...draft, deliveryChannels: event.target.value.split(",").filter(Boolean) })}>
              <option value="SMS">Text only</option>
              <option value="EMAIL">Email only</option>
              <option value="SMS,EMAIL">Text + email</option>
            </select>
          </label>
          <label className="sa-field">Allowed alert start<input value={draft.deliveryWindowStart || ""} onChange={(event) => setDraft({ ...draft, deliveryWindowStart: event.target.value })} placeholder="09:25" /></label>
          <label className="sa-field">Allowed alert end<input value={draft.deliveryWindowEnd || ""} onChange={(event) => setDraft({ ...draft, deliveryWindowEnd: event.target.value })} placeholder="16:00" /></label>
          <label className="sa-field">Alert timezone
            <select value={draft.deliveryTimezone || "America/New_York"} onChange={(event) => setDraft({ ...draft, deliveryTimezone: event.target.value })}>
              {ALERT_TIMEZONE_OPTIONS.map((timezone) => <option key={timezone} value={timezone}>{timezone}</option>)}
            </select>
          </label>
          <label className="sa-field">Allowed alert days<input value={draft.deliveryDays || ""} onChange={(event) => setDraft({ ...draft, deliveryDays: event.target.value })} placeholder="Mon,Tue,Wed,Thu,Fri" /></label>
          <label className="sa-field">Email<input value={draft.emailTo || ""} onChange={(event) => setDraft({ ...draft, emailTo: event.target.value })} /></label>
          <label className="sa-field">SMS phone<input value={draft.smsPhone || ""} onChange={(event) => setDraft({ ...draft, smsPhone: event.target.value })} /></label>
          <label className="sa-field">SMS carrier
            <select value={draft.smsCarrierId || "verizon"} onChange={(event) => setDraft({ ...draft, smsCarrierId: event.target.value })}>
              <option value="verizon">Verizon / Visible</option>
              <option value="att">AT&T / Cricket</option>
              <option value="tmobile">T-Mobile / Metro / Mint</option>
              <option value="uscellular">US Cellular</option>
              <option value="boost">Boost</option>
            </select>
          </label>
          <label className="sa-field">Scan lead minutes<input value={draft.scanLeadMinutes ?? ""} onChange={(event) => setDraft({ ...draft, scanLeadMinutes: Number(event.target.value) })} /></label>
          <label className="sa-field">Concurrency<input value={draft.scanConcurrency ?? ""} onChange={(event) => setDraft({ ...draft, scanConcurrency: Number(event.target.value) })} /></label>
          <label className="sa-field">Fast prefilter
            <select value={draft.prefilterEnabled === false ? "false" : "true"} onChange={(event) => setDraft({ ...draft, prefilterEnabled: event.target.value === "true" })}>
              <option value="true">Enabled</option>
              <option value="false">Disabled</option>
            </select>
          </label>
          <label className="sa-field">Deep-score finalists<input value={draft.prefilterLimit ?? ""} onChange={(event) => setDraft({ ...draft, prefilterLimit: Number(event.target.value) })} /></label>
          <label className="sa-field">Stored ranking rows<input value={draft.maxStoredCandidates ?? ""} onChange={(event) => setDraft({ ...draft, maxStoredCandidates: Number(event.target.value) })} /></label>
        </div>
        {deliveries.length > 0 && (
          <div className="sa-list" style={{ marginTop: 14 }}>
            {deliveries.map((delivery, index) => (
              <div className="sa-list-item" key={`${delivery.channel}-${index}`}>
                <strong>{delivery.channel}</strong>
                <span>{delivery.status}{delivery.reason ? `: ${delivery.reason}` : ""}{delivery.error ? `: ${delivery.error}` : ""}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      {candidates.length > 0 && (
        <section className="sa-card" style={{ marginTop: 16 }}>
          <h2>Full Ranking</h2>
          <div className="sa-table-wrap">
            <table className="sa-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Symbol</th>
                  <th>Side</th>
                  <th>Score</th>
                  <th>L/S</th>
                  <th>Price</th>
                  <th>State</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {candidates.slice(0, 50).map((candidate, index) => (
                  <tr key={`${candidate.symbol}-${candidate.side}-${index}`}>
                    <td>{index + 1}</td>
                    <td><Link to={`/ticker/${candidate.symbol}`}><strong>{candidate.symbol}</strong></Link></td>
                    <td>{candidate.side}</td>
                    <td>{Math.round(Number(candidate.activeScore || 0))}</td>
                    <td>{Math.round(Number(candidate.longScore || 0))} / {Math.round(Number(candidate.shortScore || 0))}</td>
                    <td>{candidate.latestPrice ?? "-"}</td>
                    <td>{candidate.liveState || candidate.tradeManagementAction || candidate.error || "-"}</td>
                    <td>{(candidate.sources || []).join(", ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}

function DayTradePage({ defaultSymbol = "SPX" }) {
  const [symbolInput, setSymbolInput] = useState(defaultSymbol);
  const [symbol, setSymbol] = useState(defaultSymbol);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [refreshMode, setRefreshMode] = useState("15s");
  const requestRef = useRef(0);

  const loadDayTrade = useCallback(async ({ nextSymbol, quiet = false } = {}) => {
    const normalized = normalizeTickerSymbol(nextSymbol || defaultSymbol);
    if (!normalized) {
      setMessage("Enter a symbol first.");
      return;
    }
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    if (!quiet) setBusy(true);
    setMessage("");
    const query = new URLSearchParams({
      timeframe: "1m",
      outputSize: "390",
      chartTimeframe: "1m",
      chartOutputSize: "390",
      autoSync: "false",
      noCache: "true",
    });
    try {
      const payload = await fetchJson(`/stock-analyst/live-score/${encodeURIComponent(normalized)}?${query.toString()}`);
      if (requestId !== requestRef.current) return;
      setSymbol(normalized);
      setSymbolInput(normalized);
      setData(payload);
    } catch (error) {
      if (requestId !== requestRef.current) return;
      setMessage(error.message);
      const lastKnown = error.response?.lastKnown;
      if (lastKnown?.scores || lastKnown?.analysis) {
        setData({
          ...lastKnown,
          ok: false,
          stale: true,
          warning: error.response?.error || error.message,
        });
      }
    } finally {
      if (requestId === requestRef.current) setBusy(false);
    }
  }, [defaultSymbol]);

  useEffect(() => {
    loadDayTrade({ nextSymbol: defaultSymbol });
  }, [defaultSymbol, loadDayTrade]);

  useEffect(() => {
    if (refreshMode === "off") return undefined;
    const intervalMs = refreshMode === "5s" ? 5000 : refreshMode === "30s" ? 30000 : 15000;
    const timer = window.setInterval(() => {
      loadDayTrade({ nextSymbol: symbol, quiet: true }).catch(() => null);
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [refreshMode, loadDayTrade, symbol]);

  function submit(event) {
    event.preventDefault();
    const nextSymbol = normalizeTickerSymbol(symbolInput);
    if (!nextSymbol) {
      setMessage("Enter a symbol first.");
      return;
    }
    loadDayTrade({ nextSymbol });
  }

  const analysis = data?.analysis || null;
  const scores = data?.scores || null;
  const rows = getTimeframeRows(analysis);
  const longScore = getScoreValue(scores?.long);
  const shortScore = getScoreValue(scores?.short);
  const edge = getDominantDayTradeSide(scores);
  const microObstacles = scores?.long?.microObstacles || scores?.short?.microObstacles || null;
  const longObstacle = microObstacles?.long?.score ?? null;
  const shortObstacle = microObstacles?.short?.score ?? null;
  const latestPrice = analysis?.chartLatestPrice ?? analysis?.latestPrice ?? analysis?.selectedChart?.latestPrice ?? null;
  const latestDate = analysis?.chartLatestDate || analysis?.latestDate || analysis?.selectedChart?.latestDate || null;
  const freshness = data?.freshness || analysis?.freshness || null;
  const stale = Boolean(data?.stale || data?.cache?.stale || data?.scoreUsable === false);
  const oscillatorPanels = [
    { area: "tsi", title: "TSI 25/13/13", valueField: "tsi", signalField: "tsiSignal", histogramField: "tsiHistogram", digits: 2 },
    { area: "smi", title: "SMI 20/5/5", valueField: "smi", signalField: "smiSignal", histogramField: "smiHistogram", digits: 2 },
    { area: "macd", title: "MACD 12/26/9", valueField: "macd", signalField: "macdSignal", histogramField: "macdHistogram", digits: 3 },
    { area: "ppo", title: "PPO 22/9", valueField: "ppo", signalField: "ppoSignal", histogramField: "ppoHistogram", digits: 3 },
  ];

  function renderOscillatorPanel(panel) {
    return (
      <div className={`sa-card sa-daytrade-panel sa-daytrade-oscillator ${panel.area}`} key={panel.area}>
        <h2>{panel.title}</h2>
        <div className="sa-daytrade-table-scroll">
          <table className="sa-daytrade-table">
            <thead><tr><th>TF</th><th>Value</th><th>Signal</th><th>Hist</th><th>State</th></tr></thead>
            <tbody>
              {rows.map((row) => {
                const tech = row.technicals || {};
                const value = tech[panel.valueField];
                const signal = tech[panel.signalField];
                const trendClass = signalTrendClass(value, signal);
                return (
                  <tr key={`${panel.area}-${row.timeframe}`}>
                    <td>{row.timeframe}</td>
                    <td className={trendClass}>{formatCompactNumber(value, panel.digits)}</td>
                    <td>{formatCompactNumber(signal, panel.digits)}</td>
                    <td>{formatCompactNumber(tech[panel.histogramField], 3)}</td>
                    <td className={trendClass}>{signalTrendLabel(value, signal)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <>
      <section className="sa-card sa-daytrade-hero">
        <div>
          <h1>Day Trade</h1>
          <p className="sa-muted">One-screen symbol monitor with universal score and the live multi-timeframe levels behind it.</p>
        </div>
        <form className="sa-daytrade-form" onSubmit={submit}>
          <label className="sa-field">Symbol
            <input value={symbolInput} onChange={(event) => setSymbolInput(event.target.value.toUpperCase())} placeholder="SPX" />
          </label>
          <label className="sa-field">Refresh
            <select value={refreshMode} onChange={(event) => setRefreshMode(event.target.value)}>
              <option value="5s">Every 5 seconds</option>
              <option value="15s">Every 15 seconds</option>
              <option value="30s">Every 30 seconds</option>
              <option value="off">Manual only</option>
            </select>
          </label>
          <button className="sa-btn" type="submit" disabled={busy}>{busy ? "Monitoring..." : "Monitor"}</button>
          <button className="sa-btn secondary" type="button" onClick={() => loadDayTrade({ nextSymbol: symbol, quiet: false })} disabled={busy}>{busy ? "Refreshing..." : "Refresh"}</button>
        </form>
      </section>

      <section className="sa-daytrade-topline">
        <div className="sa-daytrade-tile symbol">
          <span>Symbol</span>
          <strong>{symbol}</strong>
          <em>{formatCompactNumber(latestPrice, 2)} / {formatTinyDate(latestDate)}</em>
        </div>
        <div className={`sa-daytrade-tile long ${stale ? "stale" : ""}`}>
          <span>Long</span>
          <strong>{longScore !== null ? longScore.toFixed(1) : "-"}</strong>
          <em>{scores?.long?.tradeManagement?.label || scores?.long?.liveDecision?.label || "score"}</em>
        </div>
        <div className={`sa-daytrade-tile short ${stale ? "stale" : ""}`}>
          <span>Short</span>
          <strong>{shortScore !== null ? shortScore.toFixed(1) : "-"}</strong>
          <em>{scores?.short?.tradeManagement?.label || scores?.short?.liveDecision?.label || "score"}</em>
        </div>
        <div className={`sa-daytrade-tile edge ${String(edge.side).toLowerCase()}`}>
          <span>Edge</span>
          <strong>{edge.side}</strong>
          <em>{edge.score !== null ? edge.score.toFixed(1) : "-"}</em>
        </div>
        <div className="sa-daytrade-tile obstacle">
          <span>Obstacles L/S</span>
          <strong>{formatCompactNumber(longObstacle, 1)} / {formatCompactNumber(shortObstacle, 1)}</strong>
          <em>{microObstacles?.spread?.dominantObstacleSide || "level pressure"}</em>
        </div>
        <div className={`sa-daytrade-tile ${stale ? "stale" : "fresh"}`}>
          <span>Status</span>
          <strong>{stale ? "Last Known" : "Live"}</strong>
          <em>{freshness?.scoreBarAgeMs !== undefined && freshness?.scoreBarAgeMs !== null ? `${formatFreshnessAge(freshness.scoreBarAgeMs)} old` : formatDateTime(data?.refreshedAt)}</em>
        </div>
      </section>

      {message && <p className="sa-error">{message}</p>}
      {data?.warning && <p className="sa-warning">{data.warning}</p>}

      <section className="sa-daytrade-grid">
        <div className="sa-card sa-daytrade-panel ema">
          <h2>EMA Levels</h2>
          <div className="sa-daytrade-table-scroll">
            <table className="sa-daytrade-table">
              <thead><tr><th>TF</th><th>Last</th><th>9</th><th>22</th><th>50</th><th>200</th><th>Trend</th></tr></thead>
              <tbody>
                {rows.map((row) => {
                  const tech = row.technicals || {};
                  const trend = row.trend || {};
                  return (
                    <tr key={`ema-${row.timeframe}`}>
                      <td>{row.timeframe}</td>
                      <td>{formatCompactNumber(row.latestPrice, 2)}</td>
                      <td>{formatCompactNumber(tech.ema9, 2)}</td>
                      <td>{formatCompactNumber(tech.ema22, 2)}</td>
                      <td>{formatCompactNumber(tech.ema50, 2)}</td>
                      <td>{formatCompactNumber(tech.ema200, 2)}</td>
                      <td className={trend.ema9AboveEma22 ? "sa-dt-up" : trend.ema9AboveEma22 === false ? "sa-dt-down" : ""}>{trend.ema9AboveEma22 ? "Up" : trend.ema9AboveEma22 === false ? "Down" : "-"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="sa-card sa-daytrade-panel vwap">
          <h2>VWAP Bands</h2>
          <div className="sa-daytrade-table-scroll">
            <table className="sa-daytrade-table">
              <thead><tr><th>TF</th><th>Lower</th><th>VWAP</th><th>Upper</th><th>Band %</th><th>Zone</th></tr></thead>
              <tbody>
                {rows.map((row) => {
                  const tech = row.technicals || {};
                  return (
                    <tr key={`vwap-${row.timeframe}`}>
                      <td>{row.timeframe}</td>
                      <td>{formatCompactNumber(tech.vwapLowerBand, 2)}</td>
                      <td>{formatCompactNumber(tech.vwap, 2)}</td>
                      <td>{formatCompactNumber(tech.vwapUpperBand, 2)}</td>
                      <td>{formatCompactNumber(tech.vwapBandPercent, 2)}</td>
                      <td>{String(tech.vwapBandPosition || "-").replace(/_/g, " ").toLowerCase()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="sa-card sa-daytrade-panel rsi">
          <h2>RSI Rotation</h2>
          <div className="sa-daytrade-table-scroll">
            <table className="sa-daytrade-table">
              <thead><tr><th>TF</th><th>RSI</th><th>Zone</th><th>Long Room</th><th>Short Room</th></tr></thead>
              <tbody>
                {rows.map((row) => {
                  const rotation = row.trend?.rsiRotation || {};
                  const rsi = Number(row.technicals?.rsi14);
                  return (
                    <tr key={`rsi-${row.timeframe}`}>
                      <td>{row.timeframe}</td>
                      <td className={rsi <= 35 ? "sa-dt-up" : rsi >= 65 ? "sa-dt-down" : ""}>{formatCompactNumber(row.technicals?.rsi14, 1)}</td>
                      <td>{String(rotation.latestZone || "-").replace(/_/g, " ").toLowerCase()}</td>
                      <td>{formatCompactNumber(rotation.longRoomToOverbought, 1)}</td>
                      <td>{formatCompactNumber(rotation.shortRoomToOversold, 1)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {oscillatorPanels.map(renderOscillatorPanel)}

        <div className="sa-card sa-daytrade-panel context">
          <h2>Context</h2>
          <div className="sa-daytrade-context-list">
            {rows.map((row) => {
              const tech = row.technicals || {};
              const pullback = row.trend?.emaPullback || {};
              return (
                <div className="sa-daytrade-context-row" key={`context-${row.timeframe}`}>
                  <strong>{row.timeframe}</strong>
                  <span>ATR {formatCompactNumber(tech.atr14, 2)}</span>
                  <span>RVOL {formatCompactNumber(tech.relativeVolume, 2)}</span>
                  <span>Change {formatCompactNumber(tech.dayChangePercent, 2)}%</span>
                  <span>{pullback.currentlyTestingEma22 ? `Testing 22 #${pullback.currentPullbackNumber || "-"}` : pullback.lastPullbackNumber ? `Last 22 #${pullback.lastPullbackNumber}` : "No 22 pullback"}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}

function TickerPage({ defaultSymbol = "SPX" }) {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const routeSymbol = normalizeTickerSymbol(symbol || defaultSymbol || "SPX") || "SPX";
  const initialQuery = new URLSearchParams(location.search);
  const [symbolInput, setSymbolInput] = useState(routeSymbol);
  const [timeframe, setTimeframe] = useState(initialQuery.get("timeframe") || "1m");
  const [chartType, setChartType] = useState(initialQuery.get("chartType") || "candles");
  const [analysis, setAnalysis] = useState(null);
  const [scores, setScores] = useState(null);
  const [liveBusy, setLiveBusy] = useState(false);
  const [backtest, setBacktest] = useState(null);
  const [backtestBusy, setBacktestBusy] = useState(false);
  const [liveRefreshMode, setLiveRefreshMode] = useState("smart");
  const [trackingSide, setTrackingSide] = useState("NONE");
  const [alerts, setAlerts] = useState([]);
  const [alertEvents, setAlertEvents] = useState([]);
  const [alertRuntime, setAlertRuntime] = useState(null);
  const [alertDefaults, setAlertDefaults] = useState(null);
  const [alertBusy, setAlertBusy] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertDraft, setAlertDraft] = useState({
    side: "LONG",
    comparator: ">=",
    threshold: "80",
    deliveryChannels: ["EMAIL"],
    smsCarrierId: "verizon",
    deliveryWindowStart: "",
    deliveryWindowEnd: "",
    deliveryTimezone: "America/New_York",
    deliveryDays: "",
  });
  const [clockNow, setClockNow] = useState(Date.now());
  const [lastLiveRefreshAt, setLastLiveRefreshAt] = useState(null);
  const [lastLiveRefreshDurationMs, setLastLiveRefreshDurationMs] = useState(null);
  const [backtestParams, setBacktestParams] = useState({
    timeframe: "1h",
    outputSize: "1200",
    scoreAnchorTimeframe: "1m",
    oneHourBoundaryEntries: "true",
    allowEmaScalpAwayFromOneHour: "true",
    oneTradePerSetupCycle: "true",
    scoreFadeFromPeak: "15",
  });
  const [message, setMessage] = useState("");
  const liveRequestRef = useRef(0);
  const liveBusyRef = useRef(false);
  const nextLiveRefreshAtRef = useRef(0);

  useEffect(() => {
    setSymbolInput(routeSymbol);
  }, [routeSymbol]);

  const candleClock = useMemo(() => buildCandleClock(timeframe, clockNow), [timeframe, clockNow]);
  const liveRefreshDelay = useMemo(() => liveRefreshDelayMs(liveRefreshMode, candleClock), [liveRefreshMode, candleClock]);

  const loadAlerts = useCallback(async () => {
    try {
      const data = await fetchJson("/stock-analyst/alerts");
      setAlerts(data.alerts || []);
      setAlertEvents(data.events || []);
      setAlertRuntime(data.runtime || null);
      setAlertDefaults(data.defaults || null);
      rememberDisplayTimezone(data.defaults?.deliveryTimezone);
      if (data.defaults?.smsCarrierId) {
        setAlertDraft((current) => ({
          ...current,
          smsCarrierId: current.smsCarrierId || data.defaults.smsCarrierId,
          deliveryWindowStart: current.deliveryWindowStart || data.defaults.deliveryWindowStart || "",
          deliveryWindowEnd: current.deliveryWindowEnd || data.defaults.deliveryWindowEnd || "",
          deliveryTimezone: current.deliveryTimezone || data.defaults.deliveryTimezone || "America/New_York",
          deliveryDays: current.deliveryDays || data.defaults.deliveryDays || "",
        }));
      }
    } catch (error) {
      setAlertMessage(error.message);
    }
  }, []);

  const loadLiveScore = useCallback(async ({ reset = false, noCache = false } = {}) => {
    if (liveBusyRef.current && !reset) return;
    const requestId = liveRequestRef.current + 1;
    liveRequestRef.current = requestId;
    liveBusyRef.current = true;
    const startedAt = Date.now();
    if (reset) {
      setAnalysis(null);
      setScores(null);
    }
    setMessage("");
    setLiveBusy(true);
    const outputSize = chartOutputSizeForTimeframe(timeframe);
    const query = new URLSearchParams({
      timeframe,
      outputSize: String(outputSize),
      chartTimeframe: timeframe,
      chartOutputSize: String(outputSize),
      autoSync: "false",
    });
    if (trackingSide !== "NONE") {
      query.set("saveSnapshot", "true");
    }
    if (noCache) query.set("noCache", "true");
    try {
      const data = await fetchJson(`/stock-analyst/live-score/${encodeURIComponent(routeSymbol)}?${query.toString()}`);
      if (requestId !== liveRequestRef.current) return;
      setAnalysis(data.analysis);
      setScores({
        long: data.scores?.long || null,
        short: data.scores?.short || null,
        brokerageAutoSync: data.brokerageAutoSync || null,
        errors: [],
        cache: data.cache || null,
        refreshedAt: data.refreshedAt || null,
        freshness: data.freshness || data.analysis?.freshness || null,
        scoreUsable: data.scoreUsable !== false && !data.stale,
        stale: Boolean(data.stale || data.cache?.stale),
        lastKnown: false,
        warning: data.warning || null,
      });
      setLastLiveRefreshAt(data.refreshedAt || new Date().toISOString());
      setLastLiveRefreshDurationMs(Date.now() - startedAt);
    } catch (error) {
      if (requestId === liveRequestRef.current) {
        const lastKnown = error.response?.lastKnown;
        const failedAt = new Date().toISOString();
        if (lastKnown?.scores) {
          if (lastKnown.analysis) setAnalysis(lastKnown.analysis);
          setScores({
            long: lastKnown.scores?.long || null,
            short: lastKnown.scores?.short || null,
            brokerageAutoSync: lastKnown.brokerageAutoSync || null,
            errors: [error.message],
            cache: lastKnown.cache || null,
            refreshedAt: lastKnown.refreshedAt || lastKnown.cachedAt || null,
            freshness: lastKnown.freshness || lastKnown.analysis?.freshness || null,
            failedAt,
            scoreUsable: false,
            stale: true,
            lastKnown: true,
            warning: error.response?.error || error.message,
          });
          setLastLiveRefreshAt(lastKnown.refreshedAt || lastKnown.cachedAt || null);
        } else {
          setScores((current) => current ? {
            ...current,
            errors: [error.message],
            failedAt,
            scoreUsable: false,
            stale: true,
            lastKnown: true,
            warning: error.message,
            cache: {
              ...(current.cache || {}),
              stale: true,
              error: error.message,
            },
          } : current);
        }
        setMessage(error.message);
      }
    } finally {
      if (requestId === liveRequestRef.current) {
        liveBusyRef.current = false;
        setLiveBusy(false);
      }
    }
  }, [routeSymbol, timeframe, trackingSide]);

  useEffect(() => {
    loadLiveScore({ reset: true });
  }, [loadLiveScore]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  useEffect(() => {
    const timer = window.setInterval(() => setClockNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const initialDelay = liveRefreshDelayMs(liveRefreshMode, buildCandleClock(timeframe, Date.now()));
    if (!initialDelay) {
      nextLiveRefreshAtRef.current = 0;
      return undefined;
    }
    nextLiveRefreshAtRef.current = Date.now() + initialDelay;
    const timer = window.setInterval(() => {
      const currentClock = buildCandleClock(timeframe, Date.now());
      const currentDelay = liveRefreshDelayMs(liveRefreshMode, currentClock);
      if (!currentDelay) return;
      if (Date.now() < nextLiveRefreshAtRef.current) return;
      loadLiveScore({
        noCache: currentClock.secondsToClose !== null && currentClock.secondsToClose !== undefined && currentClock.secondsToClose <= 3,
      });
      nextLiveRefreshAtRef.current = Date.now() + currentDelay;
    }, 1000);
    return () => window.clearInterval(timer);
  }, [liveRefreshMode, timeframe, loadLiveScore]);

  function submitSymbol(event) {
    event.preventDefault();
    const nextSymbol = normalizeTickerSymbol(symbolInput);
    if (!nextSymbol) {
      setMessage("Enter a symbol first.");
      return;
    }
    const query = new URLSearchParams({
      timeframe,
      chartType,
    });
    navigate(`/ticker/${encodeURIComponent(nextSymbol)}?${query.toString()}`);
  }

  function updateTimeframe(nextTimeframe) {
    setTimeframe(nextTimeframe);
    setBacktestParams((current) => ({ ...current, timeframe: nextTimeframe, outputSize: String(chartOutputSizeForTimeframe(nextTimeframe)) }));
    const query = new URLSearchParams({
      timeframe: nextTimeframe,
      chartType,
    });
    if (symbol) navigate(`/ticker/${encodeURIComponent(routeSymbol)}?${query.toString()}`, { replace: true });
  }

  function updateChartType(nextType) {
    setChartType(nextType);
    const query = new URLSearchParams({
      timeframe,
      chartType: nextType,
    });
    if (symbol) navigate(`/ticker/${encodeURIComponent(routeSymbol)}?${query.toString()}`, { replace: true });
  }

  async function runBacktest() {
    setBacktestBusy(true);
    setMessage("");
    try {
      const query = new URLSearchParams();
      Object.entries(backtestParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined && String(value).trim() !== "") {
          query.set(key, String(value).trim());
        }
      });
      const data = await fetchJson(`/stock-analyst/backtest/${encodeURIComponent(symbol)}?${query.toString()}`);
      setBacktest(data.report);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBacktestBusy(false);
    }
  }

  async function createAlert(event) {
    event.preventDefault();
    setAlertBusy(true);
    setAlertMessage("");
    try {
      await fetchJson("/stock-analyst/alerts", {
        method: "POST",
        body: JSON.stringify({
          symbol: routeSymbol,
          side: alertDraft.side,
          comparator: alertDraft.comparator,
          threshold: Number(alertDraft.threshold),
          timeframe: "1m",
          outputSize: 390,
          deliveryChannels: alertDraft.deliveryChannels,
          smsCarrierId: alertDraft.smsCarrierId || alertDefaults?.smsCarrierId || "verizon",
          deliveryWindowStart: alertDraft.deliveryWindowStart || "",
          deliveryWindowEnd: alertDraft.deliveryWindowEnd || "",
          deliveryTimezone: alertDraft.deliveryTimezone || alertDefaults?.deliveryTimezone || "America/New_York",
          deliveryDays: alertDraft.deliveryDays || "",
        }),
      });
      setAlertMessage(`${routeSymbol} ${alertDraft.side} alert saved.`);
      await loadAlerts();
    } catch (error) {
      setAlertMessage(error.message);
    } finally {
      setAlertBusy(false);
    }
  }

  async function updateAlert(alert, changes) {
    setAlertBusy(true);
    setAlertMessage("");
    try {
      await fetchJson(`/stock-analyst/alerts/${encodeURIComponent(alert.alertId)}`, {
        method: "PATCH",
        body: JSON.stringify({
          ...alert,
          ...changes,
        }),
      });
      await loadAlerts();
    } catch (error) {
      setAlertMessage(error.message);
    } finally {
      setAlertBusy(false);
    }
  }

  async function deleteAlert(alert) {
    setAlertBusy(true);
    setAlertMessage("");
    try {
      await fetchJson(`/stock-analyst/alerts/${encodeURIComponent(alert.alertId)}`, {
        method: "DELETE",
      });
      await loadAlerts();
    } catch (error) {
      setAlertMessage(error.message);
    } finally {
      setAlertBusy(false);
    }
  }

  async function runAlertsNow() {
    setAlertBusy(true);
    setAlertMessage("");
    try {
      const data = await fetchJson("/stock-analyst/alerts/run", {
        method: "POST",
        body: JSON.stringify({}),
      });
      setAlertMessage(`Alert check complete: ${data.result?.evaluatedCount || 0} checked, ${data.result?.triggeredCount || 0} triggered.`);
      await loadAlerts();
    } catch (error) {
      setAlertMessage(error.message);
    } finally {
      setAlertBusy(false);
    }
  }

  const longQuickScore = Number(scores?.long?.score);
  const shortQuickScore = Number(scores?.short?.score);
  const hasLongQuickScore = Number.isFinite(longQuickScore);
  const hasShortQuickScore = Number.isFinite(shortQuickScore);
  const quickEdge = hasLongQuickScore && hasShortQuickScore
    ? longQuickScore === shortQuickScore
      ? "Balanced"
      : longQuickScore > shortQuickScore
        ? "Long"
        : "Short"
    : hasLongQuickScore
      ? "Long"
      : hasShortQuickScore
        ? "Short"
        : "Loading";
  const quickEdgeScore = hasLongQuickScore && hasShortQuickScore
    ? Math.max(longQuickScore, shortQuickScore)
    : hasLongQuickScore
      ? longQuickScore
      : hasShortQuickScore
        ? shortQuickScore
        : null;
  const trackedScore = trackingSide === "LONG"
    ? scores?.long
    : trackingSide === "SHORT"
      ? scores?.short
      : null;
  const trackedOppositeScore = trackingSide === "LONG"
    ? scores?.short
    : trackingSide === "SHORT"
      ? scores?.long
      : null;
  const trackedScoreValue = Number(trackedScore?.score);
  const trackedOppositeValue = Number(trackedOppositeScore?.score);
  const trackedAction = trackedScore?.tradeManagement?.action || trackedScore?.liveDecision?.state || "WAIT";
  const trackedActionLabel = trackedScore?.tradeManagement?.label || trackedScore?.liveDecision?.label || "Waiting for a tracked side";
  const microObstacles = scores?.long?.microObstacles || scores?.short?.microObstacles || null;
  const longObstacleScore = Number(microObstacles?.long?.score);
  const shortObstacleScore = Number(microObstacles?.short?.score);
  const hasLongObstacleScore = Number.isFinite(longObstacleScore);
  const hasShortObstacleScore = Number.isFinite(shortObstacleScore);
  const obstacleQuickValue = hasLongObstacleScore || hasShortObstacleScore
    ? `${hasLongObstacleScore ? Math.round(longObstacleScore) : "-"} / ${hasShortObstacleScore ? Math.round(shortObstacleScore) : "-"}`
    : "- / -";
  const liveRefreshLabel = liveRefreshMode === "off"
    ? "Off"
    : liveRefreshMode === "smart"
      ? "Smart"
      : liveRefreshMode === "candle"
        ? "Candle close"
        : liveRefreshMode;
  const lastRefreshLabel = formatTimeOnly(lastLiveRefreshAt);
  const refreshSpeedLabel = lastLiveRefreshDurationMs !== null ? `${(lastLiveRefreshDurationMs / 1000).toFixed(1)}s` : "-";
  const nextRefreshLabel = liveRefreshDelay ? `about ${Math.max(1, Math.round(liveRefreshDelay / 1000))}s` : "manual";
  const marketSessionLabel = analysis?.marketSession?.session
    ? analysis.marketSession.session.replace(/_/g, " ").toLowerCase()
    : "-";
  const extendedHoursLabel = analysis?.extendedHoursRequested
    ? "requested"
    : "regular/default";
  const liveScoreIsStale = Boolean(scores?.stale || scores?.scoreUsable === false);
  const liveScoreAgeLabel = scores?.cache?.ageMs !== null && scores?.cache?.ageMs !== undefined
    ? formatLiveScoreAge(scores.cache.ageMs)
    : null;
  const freshness = scores?.freshness || analysis?.freshness || null;
  const scoreCalculatedAt = freshness?.scoreCalculatedAt || scores?.refreshedAt || lastLiveRefreshAt;
  const scoreLatestBarAt = freshness?.scoreLatestBarAt || analysis?.latestDate || null;
  const chartLatestBarAt = freshness?.chartLatestBarAt || analysis?.chartLatestDate || analysis?.selectedChart?.latestDate || null;
  const scoreBarAgeLabel = formatFreshnessAge(freshness?.scoreBarAgeMs);
  const chartBarAgeLabel = formatFreshnessAge(freshness?.chartBarAgeMs);
  const selectedFrameIsIntraday = !["daily", "weekly", "monthly", "quarterly", "biannual", "yearly"].includes(String(timeframe || "").toLowerCase());
  const marketDataLooksOld = selectedFrameIsIntraday &&
    Number.isFinite(Number(freshness?.chartBarAgeMs ?? freshness?.scoreBarAgeMs)) &&
    Number(freshness?.chartBarAgeMs ?? freshness?.scoreBarAgeMs) > 2 * 60 * 1000;

  return (
    <>
      <section className="sa-card">
        <div className="sa-live-score-header">
          <div>
            <h1>Live Score</h1>
            <p className="sa-muted">Enter the ticker you are watching and read one universal long/short score. The chart timeframe is display-only.</p>
          </div>
          <div className="sa-live-score-side">
            {analysis?.rating && <RatingPill rating={analysis.rating} />}
            <div className="sa-score-quicklook" aria-label="Quick live score view">
              <div className={`sa-score-chip long ${liveScoreIsStale ? "stale" : ""}`}>
                <span>Long</span>
                <strong>{hasLongQuickScore ? Math.round(longQuickScore) : "-"}</strong>
              </div>
              <div className={`sa-score-chip short ${liveScoreIsStale ? "stale" : ""}`}>
                <span>Short</span>
                <strong>{hasShortQuickScore ? Math.round(shortQuickScore) : "-"}</strong>
              </div>
              <div className={`sa-score-chip active ${liveScoreIsStale ? "stale" : ""}`}>
                <span>Edge</span>
                <strong>{quickEdge}</strong>
                {quickEdgeScore !== null && <em>{Math.round(quickEdgeScore)}</em>}
              </div>
              <div className="sa-score-chip obstacle" aria-label="Micro obstacle pressure, long over short">
                <span>Obstacles</span>
                <strong>L/S</strong>
                <em>{obstacleQuickValue}</em>
              </div>
              <div className={`sa-score-chip status ${liveScoreIsStale ? "stale" : "fresh"}`}>
                <span>Status</span>
                <strong>{liveScoreIsStale ? "Last known" : "Live"}</strong>
                <em>{liveScoreIsStale && liveScoreAgeLabel ? `${liveScoreAgeLabel} old` : "current"}</em>
              </div>
            </div>
          </div>
        </div>
        <form className="sa-form-row" onSubmit={submitSymbol} style={{ marginTop: 12 }}>
          <label className="sa-field">Symbol
            <input value={symbolInput} onChange={(event) => setSymbolInput(event.target.value.toUpperCase())} placeholder="SPX" />
          </label>
          <label className="sa-field">Chart timeframe
            <select value={timeframe} onChange={(event) => updateTimeframe(event.target.value)}>
              <option value="1m">1m candles</option>
              <option value="2m">2m candles</option>
              <option value="5m">5m candles</option>
              <option value="10m">10m candles</option>
              <option value="15m">15m candles</option>
              <option value="30m">30m candles</option>
              <option value="1h">1h candles</option>
              <option value="daily">Daily candles</option>
            </select>
          </label>
          <label className="sa-field">Chart style
            <select value={chartType} onChange={(event) => updateChartType(event.target.value)}>
              <option value="candles">Candles</option>
              <option value="line">Line</option>
            </select>
          </label>
          <label className="sa-field">Live refresh
            <select value={liveRefreshMode} onChange={(event) => setLiveRefreshMode(event.target.value)}>
              <option value="smart">Smart near candle close</option>
              <option value="5s">Every 5 seconds</option>
              <option value="15s">Every 15 seconds</option>
              <option value="30s">Every 30 seconds</option>
              <option value="candle">Candle close only</option>
              <option value="off">Manual only</option>
            </select>
          </label>
          <button className="sa-btn" type="submit" disabled={liveBusy}>{liveBusy ? "Scoring..." : "Load Score"}</button>
          <button className="sa-btn secondary" type="button" onClick={() => loadLiveScore({ noCache: true })} disabled={liveBusy}>{liveBusy ? "Working..." : "Refresh Now"}</button>
        </form>
        <div className="sa-live-control-strip">
          <span><strong>Auto:</strong> {liveRefreshLabel}</span>
          <span><strong>Chart candle:</strong> {candleClock.label}</span>
          <span><strong>Next refresh:</strong> {nextRefreshLabel}</span>
          <span><strong>Last refresh:</strong> {lastRefreshLabel}</span>
          <span><strong>Score calculated:</strong> {formatDateTime(scoreCalculatedAt)}</span>
          <span><strong>Latest score bar:</strong> {formatDateTime(scoreLatestBarAt)}{scoreBarAgeLabel ? ` (${scoreBarAgeLabel} old)` : ""}</span>
          <span><strong>Latest chart bar:</strong> {formatDateTime(chartLatestBarAt)}{chartBarAgeLabel ? ` (${chartBarAgeLabel} old)` : ""}</span>
          <span><strong>Response:</strong> {refreshSpeedLabel}</span>
          <span><strong>Session:</strong> {marketSessionLabel}</span>
          <span><strong>Extended hours:</strong> {extendedHoursLabel}</span>
          {liveScoreIsStale && <span className="sa-warning"><strong>Score:</strong> last known only</span>}
          {marketDataLooksOld && <span className="sa-warning"><strong>Market data:</strong> latest bar looks delayed</span>}
          {scores?.cache?.stale
            ? <span className="sa-warning"><strong>Cache:</strong> stale{liveScoreAgeLabel ? ` (${liveScoreAgeLabel} old)` : ""}</span>
            : scores?.cache?.hit && <span><strong>Cache:</strong> warm</span>}
        </div>
        {liveScoreIsStale && (
          <div className="sa-live-score-stale-alert" role="alert">
            <strong>Last known score only.</strong>
            <span>This is not a current entry or add-on signal. Use it only as risk context for managing an existing position until a fresh score returns.</span>
            <span>Last calculated: {formatDateTime(scores?.refreshedAt || lastLiveRefreshAt)}{liveScoreAgeLabel ? ` (${liveScoreAgeLabel} old)` : ""}.</span>
            {scores?.failedAt && <span>Refresh failed: {formatDateTime(scores.failedAt)}.</span>}
            {scores?.warning && <span>{scores.warning}</span>}
          </div>
        )}
        <div className="sa-actions">
          <button className={`sa-btn ${trackingSide === "LONG" ? "" : "secondary"}`} type="button" onClick={() => setTrackingSide("LONG")}>Track Long</button>
          <button className={`sa-btn ${trackingSide === "SHORT" ? "" : "secondary"}`} type="button" onClick={() => setTrackingSide("SHORT")}>Track Short</button>
          <button className="sa-btn secondary" type="button" onClick={() => setTrackingSide("NONE")}>Stop Tracking</button>
        </div>
        {trackedScore && (
          <div className={`sa-live-tracker ${String(trackedAction).toLowerCase()}`}>
            <div>
              <span className="sa-subtle">Tracking {trackingSide}</span>
              <strong>{Number.isFinite(trackedScoreValue) ? Math.round(trackedScoreValue) : "-"}</strong>
            </div>
            <div>
              <span className="sa-subtle">Trade management</span>
              <p>{trackedActionLabel}</p>
            </div>
            <div>
              <span className="sa-subtle">Opposite pressure</span>
              <strong>{Number.isFinite(trackedOppositeValue) ? Math.round(trackedOppositeValue) : "-"}</strong>
            </div>
            <div>
              <span className="sa-subtle">Best hold</span>
              <p>{trackedScore?.holdTime?.recommendedBucket?.bucket || "learning"}</p>
            </div>
          </div>
        )}
        {microObstacles && <MicroObstaclePanel obstacles={microObstacles} trackingSide={trackingSide} />}
        <section className="sa-alert-panel" aria-label="Live score alerts">
          <div>
            <h2>Score Alerts</h2>
            <p className="sa-muted">Add entry or exit score triggers. Server-side checks run from saved/env alerts while the service is awake; the keepalive endpoint can nudge checks without a browser tab.</p>
          </div>
          <form className="sa-form-row" onSubmit={createAlert}>
            <label className="sa-field">Side
              <select value={alertDraft.side} onChange={(event) => setAlertDraft({ ...alertDraft, side: event.target.value })}>
                <option value="LONG">Long</option>
                <option value="SHORT">Short</option>
              </select>
            </label>
            <label className="sa-field">Trigger
              <select value={alertDraft.comparator} onChange={(event) => setAlertDraft({ ...alertDraft, comparator: event.target.value })}>
                <option value=">=">Score at or above</option>
                <option value="<=">Score at or below</option>
              </select>
            </label>
            <label className="sa-field">Score
              <input value={alertDraft.threshold} onChange={(event) => setAlertDraft({ ...alertDraft, threshold: event.target.value })} />
            </label>
            <label className="sa-field">Delivery
              <select value={alertDraft.deliveryChannels.join(",")} onChange={(event) => setAlertDraft({ ...alertDraft, deliveryChannels: event.target.value.split(",").filter(Boolean) })}>
                <option value="EMAIL">Email</option>
                <option value="SMS">Text</option>
                <option value="EMAIL,SMS">Email + text</option>
              </select>
            </label>
            {alertDraft.deliveryChannels.includes("SMS") && (
              <label className="sa-field">Text carrier
                <select value={alertDraft.smsCarrierId || alertDefaults?.smsCarrierId || "verizon"} onChange={(event) => setAlertDraft({ ...alertDraft, smsCarrierId: event.target.value })}>
                  {(alertDefaults?.carriers || [{ carrierId: "verizon", label: "Verizon / Visible / Xfinity Mobile" }]).map((carrier) => (
                    <option key={carrier.carrierId} value={carrier.carrierId}>{carrier.label}</option>
                  ))}
                </select>
              </label>
            )}
            <label className="sa-field">Allowed start
              <input value={alertDraft.deliveryWindowStart || ""} onChange={(event) => setAlertDraft({ ...alertDraft, deliveryWindowStart: event.target.value })} placeholder="09:30" />
            </label>
            <label className="sa-field">Allowed end
              <input value={alertDraft.deliveryWindowEnd || ""} onChange={(event) => setAlertDraft({ ...alertDraft, deliveryWindowEnd: event.target.value })} placeholder="16:00" />
            </label>
            <label className="sa-field">Schedule timezone
              <select value={alertDraft.deliveryTimezone || "America/New_York"} onChange={(event) => setAlertDraft({ ...alertDraft, deliveryTimezone: event.target.value })}>
                {ALERT_TIMEZONE_OPTIONS.map((timezone) => <option key={timezone} value={timezone}>{timezone}</option>)}
              </select>
            </label>
            <label className="sa-field">Allowed days
              <input value={alertDraft.deliveryDays || ""} onChange={(event) => setAlertDraft({ ...alertDraft, deliveryDays: event.target.value })} placeholder="Mon,Tue,Wed,Thu,Fri" />
            </label>
            <button className="sa-btn" type="submit" disabled={alertBusy}>{alertBusy ? "Saving..." : `Alert on ${routeSymbol}`}</button>
            <button className="sa-btn secondary" type="button" onClick={runAlertsNow} disabled={alertBusy}>{alertBusy ? "Checking..." : "Run Alerts Now"}</button>
          </form>
          <div className="sa-live-control-strip">
            <span><strong>Runtime:</strong> {alertRuntime?.enabled ? "on" : "off"}</span>
            <span><strong>Poll:</strong> {alertRuntime?.pollIntervalMs ? `${Math.round(alertRuntime.pollIntervalMs / 1000)}s` : "-"}</span>
            <span><strong>Email target:</strong> {alertDefaults?.emailToConfigured ? "configured" : "not set"}</span>
            <span><strong>Text target:</strong> {alertDefaults?.smsToConfigured ? "configured" : "not set"}</span>
            {alertDefaults?.smsCarrierId && <span><strong>Default text carrier:</strong> {alertDefaults.smsCarrierId}</span>}
          </div>
          {alertMessage && <p className={alertMessage.toLowerCase().includes("failed") || alertMessage.toLowerCase().includes("required") ? "sa-error" : "sa-muted"}>{alertMessage}</p>}
          <div className="sa-alert-list">
            {alerts.slice(0, 8).map((alert) => (
              <div className="sa-alert-row" key={alert.alertId}>
                <div>
                  <strong>{alert.symbol} {alert.side} {alert.comparator} {alert.threshold}</strong>
                  <span className="sa-subtle">
                    Last {alert.lastScore ?? "-"} {alert.lastCheckedAt ? `at ${formatDateTime(alert.lastCheckedAt)}` : ""}
                    {alert.lastAction ? ` | ${alert.lastAction.replace(/_/g, " ")}` : ""}
                  </span>
                  {alert.smsCarrierLabel && alert.deliveryChannels?.includes("SMS") && <span className="sa-subtle">Text carrier: {alert.smsCarrierLabel}</span>}
                  {(alert.deliveryWindowStart || alert.deliveryWindowEnd || alert.deliveryTimezone || alert.deliveryDays) && (
                    <span className="sa-subtle">Window: {alert.deliveryWindowStart || "00:00"}-{alert.deliveryWindowEnd || "24:00"} {alert.deliveryTimezone || "America/New_York"} {alert.deliveryDays || ""}</span>
                  )}
                  {alert.lastError && <span className="sa-subtle sa-warning">{alert.lastError.message || String(alert.lastError)}</span>}
                </div>
                <div className="sa-actions">
                  <button className="sa-btn secondary" type="button" onClick={() => updateAlert(alert, { enabled: !alert.enabled })} disabled={alertBusy}>{alert.enabled ? "Pause" : "Enable"}</button>
                  <button className="sa-btn danger" type="button" onClick={() => deleteAlert(alert)} disabled={alertBusy}>Delete</button>
                </div>
              </div>
            ))}
            {!alerts.length && <p className="sa-muted">No alerts yet. Add one for the symbol you are watching.</p>}
          </div>
          {alertEvents.length > 0 && (
            <div className="sa-alert-events">
              <h3>Recent alert fires</h3>
              {alertEvents.slice(0, 5).map((event) => (
                <p className="sa-muted" key={event.eventId}>
                  {formatDateTime(event.createdAt)} - {event.symbol} {event.side} score {event.score} ({event.deliveryStatus || "pending"})
                </p>
              ))}
            </div>
          )}
        </section>
        {message && <p className="sa-error">{message}</p>}
        {!analysis && !message && <p className="sa-muted">Loading {routeSymbol}...</p>}
        {analysis && (
          <>
            <div className="sa-chart-meta" aria-label="Chart data source">
              <span><strong>Symbol:</strong> {analysis.symbol}</span>
              <span><strong>Latest:</strong> {analysis.chartLatestPrice ?? analysis.latestPrice} on {formatDateTime(analysis.chartLatestDate || analysis.latestDate)}</span>
              <span><strong>Universal score:</strong> {analysis.scoreFrame?.timeframe || analysis.timeframe || "1m"} + all frames</span>
              <span><strong>Chart frame:</strong> {analysis.chartTimeframe || timeframe}</span>
              <span><strong>Data source:</strong> {formatDataSource(analysis.chartProvider || analysis.provider)}</span>
              <span><strong>Market session:</strong> {marketSessionLabel}</span>
              <span><strong>Bars:</strong> {analysis.selectedChart?.chart?.length || analysis.chart?.length || 0}</span>
              {analysis.selectedChart?.sourceTimeframe && <span><strong>Source frame:</strong> {analysis.selectedChart.sourceTimeframe}</span>}
              {analysis.chartWarning && <span className="sa-warning">{analysis.chartWarning}</span>}
              {analysis.warning && analysis.warning !== analysis.chartWarning && <span className="sa-warning">{analysis.warning}</span>}
            </div>
            <PriceChart bars={analysis.selectedChart?.chart || analysis.chart} chartType={chartType} />
          </>
        )}
      </section>
      {analysis && (
        <>
      <section className="sa-grid-two" style={{ marginTop: 16 }}>
        <div className="sa-card">
          <h2>Indicators</h2>
          {Object.entries(analysis.technicals || {}).map(([key, value]) => <p key={key}><strong>{key}:</strong> {value ?? "-"}</p>)}
        </div>
        <div className="sa-card">
          <h2>Trade Plan</h2>
          <p><strong>Posture:</strong> {analysis.tradePlan?.posture}</p>
          <p><strong>Invalidation:</strong> {analysis.tradePlan?.invalidation ?? "-"}</p>
          <p><strong>First target:</strong> {analysis.tradePlan?.firstTarget ?? "-"}</p>
          <p><strong>Earnings:</strong> {analysis.earningsRisk?.nearest ? `${analysis.earningsRisk.nearest.date} ${analysis.earningsRisk.nearest.time || ""}` : "No known earnings in window"}</p>
          <ul>{(analysis.tradePlan?.notes || []).map((note) => <li key={note}>{note}</li>)}</ul>
        </div>
      </section>
      <section className="sa-card" style={{ marginTop: 16 }}>
        <h2>Multi-Timeframe Levels</h2>
        <p className="sa-muted">These are the levels the live score can watch as intraday bars become available.</p>
        <div className="sa-table-wrap">
          <table className="sa-table">
            <thead>
              <tr>
                <th>Timeframe</th>
                <th>Bars</th>
                <th>Last</th>
                <th>EMA 9</th>
                <th>EMA 22</th>
                <th>9/22 Trend</th>
                <th>Pullback</th>
                <th>RSI</th>
                <th>VWAP</th>
                <th>Momentum</th>
                <th>Provider</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(analysis.multiTimeframe?.timeframes || {}).map(([timeframe, row]) => (
                <tr key={timeframe}>
                  <td><strong>{timeframe}</strong><span className="sa-subtle">{formatDateTime(row.latestDate)}</span></td>
                  <td>{row.barCount || 0}</td>
                  <td>{row.latestPrice ?? "-"}</td>
                  <td>{row.technicals?.ema9 ?? "-"}</td>
                  <td>{row.technicals?.ema22 ?? "-"}<span className="sa-subtle">{row.trend?.aboveEma22 ? "22 support area" : "22 resistance/unknown"}</span></td>
                  <td>
                    {row.trend?.ema9AboveEma22 ? <span className="sa-pill best">Uptrend</span> : row.trend?.ema9AboveEma22 === false ? <span className="sa-pill avoid">Downtrend</span> : <span className="sa-pill neutral">Unknown</span>}
                    {row.trend?.ema9CrossedAboveEma22 && <span className="sa-subtle">Fresh bullish cross</span>}
                    {row.trend?.ema9CrossedBelowEma22 && <span className="sa-subtle">Fresh bearish cross</span>}
                  </td>
                  <td>
                    {row.trend?.emaPullback?.currentlyTestingEma22
                      ? `Testing #${row.trend.emaPullback.currentPullbackNumber || row.trend.emaPullback.lastPullbackNumber || "-"}`
                      : row.trend?.emaPullback?.lastPullbackNumber
                        ? `Last #${row.trend.emaPullback.lastPullbackNumber}`
                        : "-"}
                    {row.trend?.emaPullback?.secondOrLaterPullback && <span className="sa-subtle">Second-or-later pullback</span>}
                    {row.trend?.emaPullback?.brokenThroughEma22 && <span className="sa-subtle">Broke through 22</span>}
                  </td>
                  <td><RsiContextCell row={row} /></td>
                  <td><VwapContextCell row={row} /></td>
                  <td><MomentumContextCell row={row} /></td>
                  <td>{formatDataSource(row.provider)}{row.warning && <span className="sa-subtle">{row.warning}</span>}</td>
                </tr>
              ))}
              {Object.keys(analysis.multiTimeframe?.timeframes || {}).length === 0 && (
                <tr><td colSpan="11">No multi-timeframe bars returned yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
      <section className="sa-card" style={{ marginTop: 16 }}>
        <h2>Trade Score</h2>
        <p className="sa-muted">Score is a current setup estimate and live trade-management gauge. It becomes more personal as brokerage trades are journaled with outcomes.</p>
        {scores?.brokerageAutoSync && (
          <p className="sa-muted">
            Brokerage sync before score: imported {scores.brokerageAutoSync.importedCount || 0}, skipped {scores.brokerageAutoSync.skippedExistingCount || 0}.
          </p>
        )}
        {scores?.errors?.length > 0 && scores.errors.map((error) => <p className="sa-error" key={error}>{error}</p>)}
        <div className="sa-grid">
          <div className="sa-kpi">
            <span>Long score</span>
            <strong>{scores?.long?.score ?? "-"}</strong>
            <span className="sa-subtle">{scores?.long?.mode?.replace(/_/g, " ") || ""}</span>
            <span className="sa-subtle">Best hold: {scores?.long?.holdTime?.recommendedBucket?.bucket || "learning"}</span>
            {scores?.long?.tradeManagement && (
              <>
                <TradeManagementPill action={scores.long.tradeManagement.action} label={scores.long.tradeManagement.label} />
                <span className="sa-subtle">
                  {scores.long.tradeManagement.previousDelta !== null && scores.long.tradeManagement.previousDelta !== undefined ? `Score change ${scores.long.tradeManagement.previousDelta > 0 ? "+" : ""}${scores.long.tradeManagement.previousDelta}` : "Building score history"}
                </span>
              </>
            )}
          </div>
          <div className="sa-kpi">
            <span>Short score</span>
            <strong>{scores?.short?.score ?? "-"}</strong>
            <span className="sa-subtle">{scores?.short?.mode?.replace(/_/g, " ") || ""}</span>
            <span className="sa-subtle">Best hold: {scores?.short?.holdTime?.recommendedBucket?.bucket || "learning"}</span>
            {scores?.short?.tradeManagement && (
              <>
                <TradeManagementPill action={scores.short.tradeManagement.action} label={scores.short.tradeManagement.label} />
                <span className="sa-subtle">
                  {scores.short.tradeManagement.previousDelta !== null && scores.short.tradeManagement.previousDelta !== undefined ? `Score change ${scores.short.tradeManagement.previousDelta > 0 ? "+" : ""}${scores.short.tradeManagement.previousDelta}` : "Building score history"}
                </span>
              </>
            )}
          </div>
          <div className="sa-kpi">
            <span>Samples used</span>
            <strong>{scores?.long?.empirical?.sampleCount ?? 0}</strong>
            <span className="sa-subtle">Minimum {scores?.long?.sampleRequirement ?? 5}</span>
          </div>
          <div className="sa-kpi">
            <span>Symbol learning</span>
            <strong>{scores?.long?.symbolLearning?.state?.replace(/_/g, " ") || "Learning"}</strong>
            <span className="sa-subtle">
              Long {Math.round((scores?.long?.symbolLearning?.symbolInfluence || 0) * 100)}% influence
            </span>
            <span className="sa-subtle">
              Short {Math.round((scores?.short?.symbolLearning?.symbolInfluence || 0) * 100)}% influence
            </span>
          </div>
        </div>
        {(scores?.long?.holdTime?.buckets?.length > 0 || scores?.short?.holdTime?.buckets?.length > 0) && (
          <div className="sa-grid-two" style={{ marginTop: 16 }}>
            {[
              ["Long hold-time history", scores?.long?.holdTime?.buckets || []],
              ["Short hold-time history", scores?.short?.holdTime?.buckets || []],
            ].map(([title, buckets]) => (
              <div className="sa-list" key={title}>
                <h3>{title}</h3>
                {buckets.length === 0 && <p className="sa-muted">No timed closed samples yet.</p>}
                {buckets.slice(0, 5).map((bucket) => (
                  <div className="sa-list-item" key={`${title}-${bucket.bucket}`}>
                    <strong>{bucket.bucket}</strong>
                    <p className="sa-muted">
                      Win rate {bucket.winRate}% on {bucket.sampleCount} sample(s), avg P/L {formatMoney(bucket.averageProfitLoss)}.
                    </p>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
        {scores?.long && (
          <details style={{ marginTop: 14 }}>
            <summary>Current feature snapshot</summary>
            <pre className="sa-code">{JSON.stringify(scores.long.features, null, 2)}</pre>
          </details>
        )}
      </section>
      <section className="sa-card" style={{ marginTop: 16 }}>
        <h2>Score Backtest</h2>
        <p className="sa-muted">Research replay only: the universal score is evaluated at bar close, then entries/exits execute on the next execution bar.</p>
        <div className="sa-form-row">
          <label className="sa-field">Execution timeframe
            <select value={backtestParams.timeframe} onChange={(event) => setBacktestParams({ ...backtestParams, timeframe: event.target.value })}>
              <option value="1m">1m</option>
              <option value="5m">5m</option>
              <option value="15m">15m</option>
              <option value="30m">30m</option>
              <option value="1h">1h</option>
              <option value="daily">Daily</option>
            </select>
          </label>
          <label className="sa-field">Score anchor
            <select value={backtestParams.scoreAnchorTimeframe || "1m"} onChange={(event) => setBacktestParams({ ...backtestParams, scoreAnchorTimeframe: event.target.value })}>
              <option value="1m">1m universal</option>
              <option value="2m">2m universal</option>
              <option value="5m">5m universal</option>
              <option value="15m">15m universal</option>
              <option value="1h">1h universal</option>
            </select>
          </label>
          <label className="sa-field">Bars
            <input value={backtestParams.outputSize} onChange={(event) => setBacktestParams({ ...backtestParams, outputSize: event.target.value })} />
          </label>
          <label className="sa-field">Long entry
            <input value={backtestParams.longEntryScore || ""} onChange={(event) => setBacktestParams({ ...backtestParams, longEntryScore: event.target.value })} placeholder="80" />
          </label>
          <label className="sa-field">Short entry
            <input value={backtestParams.shortEntryScore || ""} onChange={(event) => setBacktestParams({ ...backtestParams, shortEntryScore: event.target.value })} placeholder="80" />
          </label>
          <label className="sa-field">Exit score
            <input value={backtestParams.exitScore || ""} onChange={(event) => setBacktestParams({ ...backtestParams, exitScore: event.target.value })} placeholder="55" />
          </label>
          <label className="sa-field">Score fade from peak
            <input value={backtestParams.scoreFadeFromPeak || ""} onChange={(event) => setBacktestParams({ ...backtestParams, scoreFadeFromPeak: event.target.value })} placeholder="15" />
          </label>
          <label className="sa-field">Entry timing
            <select value={backtestParams.oneHourBoundaryEntries || "true"} onChange={(event) => setBacktestParams({ ...backtestParams, oneHourBoundaryEntries: event.target.value })}>
              <option value="true">1h start/end or EMA scalp</option>
              <option value="false">Any score trigger</option>
            </select>
          </label>
          <label className="sa-field">Cycle memory
            <select value={backtestParams.oneTradePerSetupCycle || "true"} onChange={(event) => setBacktestParams({ ...backtestParams, oneTradePerSetupCycle: event.target.value })}>
              <option value="true">One entry per setup cycle</option>
              <option value="false">Allow repeat entries</option>
            </select>
          </label>
          <button className="sa-btn" onClick={runBacktest} disabled={backtestBusy}>{backtestBusy ? "Testing..." : "Run Backtest"}</button>
        </div>
        {backtest?.warning && <p className="sa-warning">{backtest.warning}</p>}
        {backtest && (
          <>
            <div className="sa-grid" style={{ marginTop: 16 }}>
              <div className="sa-kpi"><span>Trades</span><strong>{backtest.summary?.tradeCount ?? 0}</strong><span className="sa-subtle">{formatDataSource(backtest.provider)} exec {backtest.timeframe}</span></div>
              <div className="sa-kpi"><span>Win rate</span><strong>{backtest.summary?.winRate ?? "-"}%</strong><span className="sa-subtle">{backtest.summary?.wins ?? 0} wins / {backtest.summary?.losses ?? 0} losses</span></div>
              <div className="sa-kpi"><span>Total return</span><strong>{formatPercent(backtest.summary?.totalProfitPercent)}</strong><span className="sa-subtle">Before commissions/taxes</span></div>
              <div className="sa-kpi"><span>Profit factor</span><strong>{backtest.summary?.profitFactor ?? "-"}</strong><span className="sa-subtle">Slippage {backtest.parameters?.slippageBps ?? 0} bps</span></div>
            </div>
            <p className="sa-muted">{backtest.methodology}</p>
            <p className="sa-muted">Universal score anchor: {backtest.parameters?.scoreAnchorTimeframe || backtest.scoreAnchorTimeframe || "1m"}; score context: {(backtest.parameters?.scoreTimeframes || []).join(", ") || "configured timeframes"}.</p>
            <p className="sa-muted">Rules: {backtest.parameters?.oneHourBoundaryEntries ? "one-hour boundary entries" : "any score entry"}; {backtest.parameters?.allowEmaScalpAwayFromOneHour ? "EMA scalp exceptions allowed" : "no EMA scalp exception"}; {backtest.parameters?.oneTradePerSetupCycle ? "one entry per VWAP/RSI setup cycle" : "repeat cycle entries allowed"}; peak score fade {backtest.parameters?.scoreFadeFromPeak ?? "-"}.</p>
            <div className="sa-table-wrap">
              <table className="sa-table">
                <thead>
                  <tr>
                    <th>Opened</th>
                    <th>Side</th>
                    <th>Entry</th>
                    <th>Closed</th>
                    <th>Exit</th>
                    <th>Reason</th>
                    <th>Return</th>
                  </tr>
                </thead>
                <tbody>
                  {(backtest.trades || []).slice(-12).reverse().map((trade) => (
                    <tr key={`${trade.openedAt}-${trade.side}-${trade.closedAt}`}>
                      <td>{formatDateTime(trade.openedAt)}<span className="sa-subtle">L {trade.entryLongScore} / S {trade.entryShortScore}</span><span className="sa-subtle">{trade.entryTimingReason || ""}</span></td>
                      <td>{trade.side}</td>
                      <td>{trade.entryPrice}</td>
                      <td>{formatDateTime(trade.closedAt)}<span className="sa-subtle">L {trade.exitLongScore} / S {trade.exitShortScore}</span><span className="sa-subtle">Peak {trade.peakScore ?? "-"}</span></td>
                      <td>{trade.exitPrice}</td>
                      <td>{String(trade.exitReason || "").replace(/_/g, " ")}</td>
                      <td>{formatPercent(trade.profitPercent)}</td>
                    </tr>
                  ))}
                  {(!backtest.trades || backtest.trades.length === 0) && (
                    <tr><td colSpan="7">No trades triggered under these thresholds.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
        </>
      )}
    </>
  );
}

function WatchlistPage() {
  const [state, setState] = useState(null);
  const [symbol, setSymbol] = useState("");
  const [scoreReport, setScoreReport] = useState(null);
  const [scoreBusy, setScoreBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [activeWatchlistName, setActiveWatchlistName] = useState("All");

  async function load() {
    const data = await fetchJson("/stock-analyst/state");
    setState(data.state);
  }

  async function loadScores() {
    setScoreBusy(true);
    setMessage("");
    try {
      const data = await fetchJson("/stock-analyst/watchlist/scores");
      setScoreReport(data);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setScoreBusy(false);
    }
  }

  useEffect(() => {
    load();
    loadScores();
  }, []);

  async function add() {
    await fetchJson("/stock-analyst/watchlist", { method: "POST", body: JSON.stringify({ symbol }) });
    setSymbol("");
    await load();
    await loadScores();
  }

  async function remove(itemSymbol) {
    await fetchJson(`/stock-analyst/watchlist/${encodeURIComponent(itemSymbol)}`, { method: "DELETE" });
    await load();
    await loadScores();
  }

  async function resetEarningsWatchlists() {
    setScoreBusy(true);
    setMessage("");
    try {
      const data = await fetchJson("/stock-analyst/watchlist?scope=earnings", { method: "DELETE" });
      setMessage(`Reset earnings watchlists. Removed ${data.removedCount || 0} symbol(s).`);
      setActiveWatchlistName("All");
      await load();
      await loadScores();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setScoreBusy(false);
    }
  }

  const savedWatchlistItems = state?.watchlist || [];
  const watchlistNames = ["All", ...new Set(savedWatchlistItems.map((item) => item.watchlistName || item.tradeDay || "General Watchlist").filter(Boolean))];
  const filterByActiveWatchlist = (item = {}) => (
    activeWatchlistName === "All" ||
    (item.watchlistName || item.tradeDay || "General Watchlist") === activeWatchlistName
  );
  const filteredScoreRows = (scoreReport?.rows || []).filter(filterByActiveWatchlist);
  const filteredSavedItems = savedWatchlistItems.filter(filterByActiveWatchlist);

  return (
    <>
      <section className="sa-card">
        <h1>Watchlist</h1>
        <p className="sa-muted">Daily scans find candidates. Watchlist scoring keeps the active list clean: long score, short score, watch state, trade-management pressure, nearest level, and best learned hold window.</p>
        <div className="sa-actions">
          <input value={symbol} onChange={(event) => setSymbol(event.target.value)} placeholder="Symbol" />
          <button className="sa-btn" onClick={add}>Add</button>
          <button className="sa-btn secondary" onClick={loadScores} disabled={scoreBusy}>{scoreBusy ? "Scoring..." : "Refresh Watchlist Scores"}</button>
          <button className="sa-btn danger" onClick={resetEarningsWatchlists} disabled={scoreBusy}>Reset Earnings Watchlists</button>
        </div>
        <div className="sa-watchlist-tabs" style={{ marginTop: 14 }}>
          {watchlistNames.map((name) => (
            <button
              key={name}
              className={`sa-watchlist-tab ${activeWatchlistName === name ? "is-active" : ""}`}
              type="button"
              onClick={() => setActiveWatchlistName(name)}
            >
              {name}
            </button>
          ))}
        </div>
        {message && <p className={message.startsWith("Reset") ? "sa-muted" : "sa-error"}>{message}</p>}
      </section>

      <section className="sa-card" style={{ marginTop: 16 }}>
        <h2>Live Watch State</h2>
        <p className="sa-muted">
          Last scored: {formatDateTime(scoreReport?.generatedAt)}
          {scoreReport?.brokerageAutoSync && `; brokerage sync imported ${scoreReport.brokerageAutoSync.importedCount || 0}`}
        </p>
        <div className="sa-table-wrap">
          <table className="sa-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Long</th>
                <th>Short</th>
                <th>Obstacles</th>
                <th>Watch State</th>
                <th>Management</th>
                <th>Nearest Level</th>
                <th>Best Hold</th>
                <th>Alert</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredScoreRows.map((row) => (
                <tr key={row.symbol}>
                  <td>
                    <Link to={`/ticker/${row.symbol}`}><strong>{row.symbol}</strong></Link>
                    <span className="sa-subtle">{row.latestPrice ? `Last ${row.latestPrice}` : ""}</span>
                    <span className="sa-subtle">{row.watchlistName || ""}</span>
                  </td>
                  <td>{row.longScore ?? "-"}</td>
                  <td>{row.shortScore ?? "-"}</td>
                  <td>
                    <span className="sa-subtle">L {row.microObstacles?.long?.score ?? "-"} / S {row.microObstacles?.short?.score ?? "-"}</span>
                    <span className="sa-subtle">{row.microObstacles?.spread?.dominantObstacleSide || ""}</span>
                  </td>
                  <td>
                    <WatchStatePill state={row.watchState} label={row.watchLabel} />
                    <span className="sa-subtle">{row.activeSide} {row.activeScore ?? "-"}</span>
                  </td>
                  <td>
                    <TradeManagementPill action={row.tradeManagement?.action} label={row.tradeManagement?.label} />
                    <span className="sa-subtle">
                      {row.tradeManagement?.previousDelta !== null && row.tradeManagement?.previousDelta !== undefined ? `Change ${row.tradeManagement.previousDelta > 0 ? "+" : ""}${row.tradeManagement.previousDelta}` : "No prior score"}
                    </span>
                  </td>
                  <td>
                    {row.nearestLevel?.label || "-"}
                    <span className="sa-subtle">
                      {row.nearestLevel?.value ?? ""}
                      {row.nearestLevel?.distancePercent !== null && row.nearestLevel?.distancePercent !== undefined ? ` (${formatPercent(row.nearestLevel.distancePercent)})` : ""}
                    </span>
                  </td>
                  <td>{row.bestHold || "learning"}</td>
                  <td>{row.alertTriggered ? <span className="sa-pill best">Alert</span> : <span className="sa-pill neutral">Quiet</span>}</td>
                  <td><button className="sa-btn secondary" onClick={() => remove(row.symbol)}>Remove</button></td>
                </tr>
              ))}
              {scoreReport && filteredScoreRows.length === 0 && (
                <tr><td colSpan="10">No symbols in {activeWatchlistName}.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {scoreReport?.errors?.length > 0 && (
          <details style={{ marginTop: 14 }}>
            <summary>Symbols that could not be scored</summary>
            <pre className="sa-code">{JSON.stringify(scoreReport.errors, null, 2)}</pre>
          </details>
        )}
      </section>

      <section className="sa-card" style={{ marginTop: 16 }}>
        <h2>Saved Symbols</h2>
        <div className="sa-table-wrap" style={{ marginTop: 18 }}>
          <table className="sa-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Watchlist</th>
                <th>Trade Date</th>
                <th>Earnings Date</th>
                <th>Notes</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredSavedItems.map((item) => (
                <tr key={item.symbol}>
                  <td><strong><Link to={`/ticker/${item.symbol}`}>{item.symbol}</Link></strong><span className="sa-subtle">Added {formatDateTime(item.addedAt)}</span></td>
                  <td>{item.watchlistName || item.tradeDay || "General Watchlist"}</td>
                  <td>{item.tradeDate || "-"}</td>
                  <td>{item.earningsDate || "-"}</td>
                  <td>{item.notes ? <span className="sa-subtle">{item.notes}</span> : "-"}</td>
                  <td><button className="sa-btn secondary" onClick={() => remove(item.symbol)}>Remove</button></td>
                </tr>
              ))}
              {filteredSavedItems.length === 0 && (
                <tr><td colSpan="6">No saved symbols in {activeWatchlistName}.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function tradeTimeMs(trade = {}) {
  const time = Date.parse(trade.openedAt || trade.closedAt || trade.createdAt || trade.importedAt || "");
  return Number.isFinite(time) ? time : 0;
}

function sortTradesChronological(trades = []) {
  return [...trades].sort((left, right) => tradeTimeMs(left) - tradeTimeMs(right));
}

function tradeHasSavedNotes(trade = {}) {
  return Boolean(
    trade.journalNotes?.setupNotes ||
    trade.journalNotes?.ruleNotes ||
    trade.journalNotes?.outcomeNotes ||
    trade.journalNotes?.lessonNotes ||
    trade.journalNotes?.mindsetNotes
  );
}

function tradeIdentityValues(trade = {}) {
  return [
    trade.importId,
    trade.sourceKey,
    trade.brokerageImportId,
    trade.brokerageSourceKey,
  ].map((value) => String(value || "").trim()).filter(Boolean);
}

function tradeMatchesSelectedId(trade = {}, selectedId = "") {
  const id = String(selectedId || "").trim();
  if (!id) return false;
  return tradeIdentityValues(trade).includes(id);
}

function mergeTradeReviewCopy(primary = {}, fallback = {}) {
  if (!fallback) return primary;
  return {
    ...primary,
    journalNotes: primary.journalNotes || fallback.journalNotes || null,
    followedRules: primary.followedRules ?? fallback.followedRules ?? null,
    userReviewedAt: primary.userReviewedAt || fallback.userReviewedAt || null,
  };
}

function tradeRoleLabel(trade = {}) {
  const role = String(trade.tradeGroup?.role || "").toUpperCase();
  if (role === "MAIN") return "Main";
  if (role === "ADD_ON") return "Add-on";
  if (role === "RE_ENTRY") return "Re-entry";
  return "";
}

function manualJournalEntryIsVisible(entry = {}) {
  if (entry.source === "brokerage_auto_sync") return false;
  const symbol = String(entry.symbol || "").trim();
  const hasText = [
    entry.thesis,
    entry.plan,
    entry.invalidation,
    entry.outcome,
    entry.notes,
  ].some((value) => String(value || "").trim());
  return Boolean(symbol || hasText);
}

function JournalPage() {
  const [journalData, setJournalData] = useState(null);
  const [dailyReviewDate, setDailyReviewDate] = useState(localDateInputValue());
  const [dailyReview, setDailyReview] = useState(null);
  const [dailySync, setDailySync] = useState(null);
  const [csvImport, setCsvImport] = useState({
    text: "",
    name: "",
    accountKey: "futures",
  });
  const [dailyReviewForm, setDailyReviewForm] = useState({
    reportedProfitLoss: "",
    dayNotes: "",
    executionNotes: "",
    lessons: "",
    planForNextSession: "",
  });
  const [form, setForm] = useState({ symbol: "", bias: "watch", thesis: "", plan: "", invalidation: "", outcome: "" });
  const [selectedTradeId, setSelectedTradeId] = useState("");
  const [notesDraft, setNotesDraft] = useState({
    setupNotes: "",
    ruleNotes: "",
    outcomeNotes: "",
    lessonNotes: "",
    mindsetNotes: "",
    followedRules: "",
  });
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  async function load() {
    const data = await fetchJson("/stock-analyst/journal/learning?limit=1000");
    setJournalData(data);
    await loadDailyReview(dailyReviewDate, { selectFirstTrade: !selectedTradeId });
  }

  async function loadDailyReview(date = dailyReviewDate, options = {}) {
    const data = await fetchJson(`/stock-analyst/journal/daily-review?date=${encodeURIComponent(date)}`);
    setDailyReview(data);
    const review = data.review || {};
    setDailyReviewForm({
      reportedProfitLoss: review.reportedProfitLoss ?? "",
      dayNotes: review.dayNotes || "",
      executionNotes: review.executionNotes || "",
      lessons: review.lessons || "",
      planForNextSession: review.planForNextSession || "",
    });
    if (options.selectFirstTrade && Array.isArray(data.trades) && data.trades.length) {
      selectTrade(data.trades[0]);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const importedTrades = journalData?.brokerageJournalImports || [];
  function isDisplayableImportedTrade(trade = {}) {
    const symbol = String(trade.symbol || trade.assetSymbol || trade.contractSymbol || trade.underlyingSymbol || "").trim();
    return Boolean(symbol && (trade.importId || trade.sourceKey));
  }

  const displayableImportedTrades = sortTradesChronological(importedTrades.filter(isDisplayableImportedTrade));
  const importedTradeByIdentity = new Map();
  displayableImportedTrades.forEach((trade) => {
    tradeIdentityValues(trade).forEach((key) => {
      if (!importedTradeByIdentity.has(key)) importedTradeByIdentity.set(key, trade);
    });
  });
  const selectedDayTrades = sortTradesChronological((dailyReview?.trades || [])
    .filter(isDisplayableImportedTrade)
    .map((trade) => {
      const fallback = tradeIdentityValues(trade)
        .map((key) => importedTradeByIdentity.get(key))
        .find(Boolean);
      return mergeTradeReviewCopy(trade, fallback);
    }));
  const selectedDayTradeIds = new Set(selectedDayTrades.flatMap(tradeIdentityValues));
  const selectableTrades = [
    ...selectedDayTrades,
    ...displayableImportedTrades.filter((trade) => !tradeIdentityValues(trade).some((key) => selectedDayTradeIds.has(key))),
  ];
  const selectedTrade = selectableTrades.find((trade) => tradeMatchesSelectedId(trade, selectedTradeId)) || selectedDayTrades[0] || displayableImportedTrades[0] || null;
  const selectedTradeResolvedId = selectedTrade ? tradeIdentityValues(selectedTrade)[0] || "" : "";
  const selectedTradeNotesUpdatedAt = selectedTrade?.journalNotes?.updatedAt || "";

  function notesFromTrade(trade = {}) {
    const notes = trade.journalNotes || {};
    return {
      setupNotes: notes.setupNotes || "",
      ruleNotes: notes.ruleNotes || "",
      outcomeNotes: notes.outcomeNotes || "",
      lessonNotes: notes.lessonNotes || "",
      mindsetNotes: notes.mindsetNotes || "",
      followedRules: notes.followedRules === true ? "true" : notes.followedRules === false ? "false" : "",
    };
  }

  function selectTrade(trade) {
    if (!trade) return;
    setSelectedTradeId(trade.importId || trade.sourceKey || "");
    setNotesDraft(notesFromTrade(trade));
    setMessage("");
  }

  useEffect(() => {
    if (selectedTrade) setNotesDraft(notesFromTrade(selectedTrade));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTradeId, selectedTradeResolvedId, selectedTradeNotesUpdatedAt]);

  async function submit(event) {
    event.preventDefault();
    await fetchJson("/stock-analyst/journal", { method: "POST", body: JSON.stringify(form) });
    setForm({ symbol: "", bias: "watch", thesis: "", plan: "", invalidation: "", outcome: "" });
    await load();
  }

  async function saveSelectedTradeNotes(event) {
    event.preventDefault();
    const selectedTradeKey = selectedTrade?.importId || selectedTrade?.sourceKey;
    if (!selectedTradeKey) return;
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson(`/stock-analyst/journal/brokerage-import/${encodeURIComponent(selectedTradeKey)}/notes`, {
        method: "POST",
        body: JSON.stringify({
          ...notesDraft,
          sourceKey: selectedTrade.sourceKey,
          symbol: selectedTrade.symbol,
          accountKey: selectedTrade.accountKey,
          openedAt: selectedTrade.openedAt,
          closedAt: selectedTrade.closedAt,
        }),
      });
      setMessage(`${data.import.symbol} trade notes saved.`);
      setSelectedTradeId(data.import.importId || data.import.sourceKey || selectedTradeKey);
      const refreshed = await fetchJson("/stock-analyst/journal/learning?limit=1000");
      setJournalData(refreshed);
      await loadDailyReview(dailyReviewDate, { selectFirstTrade: false });
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function saveDailyReview(event) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson(`/stock-analyst/journal/daily-review/${encodeURIComponent(dailyReviewDate)}/notes`, {
        method: "POST",
        body: JSON.stringify(dailyReviewForm),
      });
      setDailyReview(data);
      setMessage(`Daily review saved for ${dailyReviewDate}.`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function syncDailyBrokerage() {
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson("/stock-analyst/journal/auto-sync", {
        method: "POST",
        body: JSON.stringify({
          scope: "all",
          learnModel: false,
        }),
      });
      setDailySync(data.sync);
      setMessage(`Brokerage sync complete. Imported ${data.sync.importedCount || 0}; skipped ${data.sync.skippedExistingCount || 0}. Reloaded ${dailyReviewDate}.`);
      const refreshed = await fetchJson("/stock-analyst/journal/learning?limit=1000");
      setJournalData(refreshed);
      await loadDailyReview(dailyReviewDate, { selectFirstTrade: true });
    } catch (error) {
      setDailySync({
        syncedAt: new Date().toISOString(),
        error: error.message,
      });
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function selectCsvImportFile(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setCsvImport((current) => ({
      ...current,
      text,
      name: file.name,
    }));
    setMessage(`${file.name} loaded. Click Import Webull CSV to add missing broker rows.`);
  }

  async function importWebullCsv() {
    if (!csvImport.text.trim()) {
      setMessage("Choose a Webull order-history CSV first.");
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson("/stock-analyst/journal/import-webull-csv", {
        method: "POST",
        body: JSON.stringify({
          csvText: csvImport.text,
          sourceName: csvImport.name || "webull-orders.csv",
          accountKey: csvImport.accountKey || "futures",
          learnModel: false,
        }),
      });
      setDailySync(data.sync);
      setMessage(`CSV import complete. Reconstructed ${data.sync.runs?.[0]?.candidateCount || 0} trade candidate(s); imported ${data.sync.importedCount || 0}; skipped ${data.sync.skippedExistingCount || 0}.`);
      const refreshed = await fetchJson("/stock-analyst/journal/learning?limit=1000");
      setJournalData(refreshed);
      await loadDailyReview(dailyReviewDate, { selectFirstTrade: true });
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function absorbModelState() {
    setBusy(true);
    setMessage("");
    try {
      const result = await fetchJson("/stock-analyst/model-state/absorb", {
        method: "POST",
        body: JSON.stringify({ reason: "journal_page_absorb" }),
      });
      setMessage(result.changed
        ? `Model updated to v${result.summary.version} with ${result.addedCount} new sample${result.addedCount === 1 ? "" : "s"}.`
        : `Model unchanged at v${result.summary.version}; no new eligible samples.`);
      await load();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function rebuildModelState() {
    setBusy(true);
    setMessage("");
    try {
      const result = await fetchJson("/stock-analyst/model-state/rebuild", {
        method: "POST",
        body: JSON.stringify({ reason: "journal_page_rebuild_after_review" }),
      });
      setMessage(`Model rebuilt as v${result.summary.version} from ${result.rebuiltSampleCount} eligible sample${result.rebuiltSampleCount === 1 ? "" : "s"}.`);
      await load();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function restoreKnownTradeNotes() {
    setBusy(true);
    setMessage("");
    try {
      const result = await fetchJson("/stock-analyst/journal/restore-known-notes", {
        method: "POST",
        body: JSON.stringify({}),
      });
      setMessage(`Known trade notes restored: ${result.restoredCount || 0} trade${result.restoredCount === 1 ? "" : "s"} updated.`);
      await load();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function rollbackModelState(snapshot) {
    if (!snapshot?.version) return;
    setBusy(true);
    setMessage("");
    try {
      const result = await fetchJson("/stock-analyst/model-state/rollback", {
        method: "POST",
        body: JSON.stringify({
          snapshotId: snapshot.snapshotId,
          reason: `journal_page_rollback_to_v${snapshot.version}`,
        }),
      });
      setMessage(`Model rolled back from v${result.rolledBackFromVersion} to snapshot v${result.rolledBackToVersion}; current model is v${result.summary.version}.`);
      await load();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  const tradeRows = (selectedDayTrades.length ? selectedDayTrades : displayableImportedTrades)
    .filter((trade) => trade.status !== "OPEN" || trade.profitLoss !== null);
  const visibleManualEntries = (journalData?.journal || []).filter(manualJournalEntryIsVisible);
  const modelState = journalData?.modelState || {};

  return (
    <>
      <section className="sa-card">
        <h1>Journal</h1>
        <p className="sa-muted">Every imported trade becomes a permanent review item here. Open a trade, write what you saw and felt, then save it to build your reference library.</p>
        {message && <p className={message.toLowerCase().includes("saved") ? "sa-muted" : "sa-error"}>{message}</p>}
        <div className="sa-grid" style={{ marginTop: 16 }}>
          <div className="sa-kpi"><span>Imported trades</span><strong>{journalData?.summary?.brokerageImports ?? "-"}</strong></div>
          <div className="sa-kpi"><span>Closed samples</span><strong>{journalData?.summary?.closedLearningSamples ?? "-"}</strong></div>
          <div className="sa-kpi"><span>Wins / Losses</span><strong>{journalData?.summary ? `${journalData.summary.wins} / ${journalData.summary.losses}` : "-"}</strong></div>
          <div className="sa-kpi"><span>Model version</span><strong>{modelState.version ?? 0}</strong></div>
          <div className="sa-kpi"><span>Model samples</span><strong>{modelState.sampleCount ?? 0}</strong></div>
          <div className="sa-kpi"><span>Model win rate</span><strong>{modelState.winRate !== null && modelState.winRate !== undefined ? `${modelState.winRate}%` : "-"}</strong></div>
        </div>
        <div className="sa-actions">
          <button className="sa-btn secondary" type="button" onClick={load}>Refresh Journal</button>
          <Link className="sa-btn secondary" to="/trades">Sync New Trades</Link>
          <button className="sa-btn secondary" type="button" onClick={absorbModelState} disabled={busy}>Absorb Model Samples</button>
          <button className="sa-btn secondary" type="button" onClick={rebuildModelState} disabled={busy}>Rebuild Model From Journal</button>
          <button className="sa-btn secondary" type="button" onClick={restoreKnownTradeNotes} disabled={busy}>Restore Known Notes</button>
          <a className="sa-btn secondary" href="/stock-analyst/model-state/export">Export Model JSON</a>
        </div>
        <p className="sa-muted">
          Scoring uses the durable model state once a model version exists. New broker rows stay out of the score until absorbed, so a bad parse can be fixed or rolled back before it changes future scores.
        </p>
        {Array.isArray(modelState.snapshots) && modelState.snapshots.length > 0 && (
          <div className="sa-list" style={{ marginTop: 12 }}>
            {modelState.snapshots.slice(0, 5).map((snapshot) => (
              <div className="sa-list-item" key={snapshot.snapshotId}>
                <strong>Snapshot v{snapshot.version}</strong>
                <span className="sa-subtle">{snapshot.createdAt} / {snapshot.sampleCount} samples / {snapshot.reason}</span>
                <button className="sa-btn secondary" type="button" onClick={() => rollbackModelState(snapshot)} disabled={busy || snapshot.version === modelState.version}>Rollback to this</button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="sa-card" style={{ marginTop: 16 }}>
        <div className="sa-section-heading">
          <div>
            <h2>Daily Review</h2>
            <p className="sa-muted">Use this after each trading day to reconcile broker P/L, review the trade cluster, and save the high-level lesson.</p>
          </div>
          <div className="sa-actions">
            <input
              type="date"
              value={dailyReviewDate}
              onChange={(event) => setDailyReviewDate(event.target.value)}
              onBlur={() => loadDailyReview(dailyReviewDate, { selectFirstTrade: true })}
            />
            <button className="sa-btn secondary" type="button" onClick={() => loadDailyReview(dailyReviewDate, { selectFirstTrade: true })}>Load Day</button>
            <button className="sa-btn secondary" type="button" onClick={syncDailyBrokerage} disabled={busy}>Sync Brokerage Day</button>
          </div>
        </div>
        {dailyReview?.warnings?.length > 0 && (
          <div className="sa-warning-list">
            {dailyReview.warnings.map((warning) => <p key={warning}>{warning}</p>)}
          </div>
        )}
        <div className="sa-grid" style={{ marginTop: 16 }}>
          <div className="sa-kpi"><span>Imported P/L</span><strong>{formatMoney(dailyReview?.summary?.profitLoss)}</strong></div>
          <div className="sa-kpi"><span>Reported P/L</span><strong>{formatMoney(dailyReview?.summary?.reconciliation?.reportedProfitLoss)}</strong></div>
          <div className="sa-kpi"><span>Reconciliation</span><strong>{dailyReview?.summary?.reconciliation?.status || "-"}</strong><span>{dailyReview?.summary?.reconciliation?.gap !== null && dailyReview?.summary?.reconciliation?.gap !== undefined ? `Gap ${formatMoney(dailyReview.summary.reconciliation.gap)}` : "No broker total saved"}</span></div>
          <div className="sa-kpi"><span>Trades</span><strong>{dailyReview?.summary?.tradeCount ?? "-"}</strong><span>{dailyReview?.summary ? `${dailyReview.summary.wins} wins / ${dailyReview.summary.losses} losses` : ""}</span></div>
          <div className="sa-kpi"><span>Research-only P/L</span><strong>{formatMoney(dailyReview?.summary?.researchOnlyProfitLoss)}</strong><span>{dailyReview?.summary?.researchOnlyCount ?? 0} option/future rows</span></div>
          <div className="sa-kpi"><span>Core-model P/L</span><strong>{formatMoney(dailyReview?.summary?.coreModelProfitLoss)}</strong><span>{dailyReview?.summary?.coreModelCount ?? 0} eligible rows</span></div>
        </div>
        {dailyReview?.diagnostics && (
          <p className="sa-muted">
            Stored rows for this day: {dailyReview.diagnostics.rawBrokerageImportsForDate}; displayed after validation: {dailyReview.diagnostics.displayedBrokerageImportsForDate}
            {dailyReview.diagnostics.hiddenByDedupeOrValidation > 0 ? `; hidden: ${dailyReview.diagnostics.hiddenByDedupeOrValidation}` : ""}.
          </p>
        )}
        {dailySync && (
          <div className="sa-list" style={{ marginTop: 16 }}>
            <div className="sa-list-item">
              <strong>Brokerage Sync Check</strong>
              {dailySync.error ? (
                <p className="sa-error">{dailySync.error}</p>
              ) : (
                <p className="sa-muted">
                  {formatDateTime(dailySync.syncedAt)} - imported {dailySync.importedCount || 0}, skipped {dailySync.skippedExistingCount || 0}, accounts checked {dailySync.accountCount || 1}.
                </p>
              )}
              {(dailySync.runs || []).map((run) => (
                <details key={run.syncRunId} className="sa-sync-detail">
                  <summary>
                    {run.account?.label || "Account"}: scanned {run.scannedOrders || 0} orders, built {run.candidateCount || 0} trade candidates, imported {run.importedCount || 0}
                  </summary>
                  <pre className="sa-code">{JSON.stringify({
                    orderHistory: {
                      ok: run.orderHistoryOk,
                      rows: run.orderHistoryTotalRows,
                      pages: run.orderHistoryPageCount,
                      selectedPath: run.orderHistorySelectedPath,
                      warning: run.orderHistoryPaginationWarning,
                    },
                    openOrders: {
                      ok: run.openOrdersOk,
                      rows: run.openOrdersTotalRows,
                      pages: run.openOrdersPageCount,
                      selectedPath: run.openOrdersSelectedPath,
                      warning: run.openOrdersPaginationWarning,
                    },
                    candidateSummaries: run.candidateSummaries || [],
                    providerOrderSummaries: run.providerOrderSummaries || [],
                    unmatchedOrders: run.unmatchedOrders || [],
                  }, null, 2)}</pre>
                </details>
              ))}
            </div>
          </div>
        )}
        <div className="sa-list" style={{ marginTop: 16 }}>
          <div className="sa-list-item">
            <strong>Webull CSV Recovery Import</strong>
            <p className="sa-muted">
              Use this when the API does not return every filled order. Export Webull order history as CSV, choose it here, then review the reconstructed trades before absorbing model samples.
            </p>
            <div className="sa-actions">
              <input type="file" accept=".csv,text/csv" onChange={selectCsvImportFile} />
              <input
                className="sa-inline-select"
                value={csvImport.accountKey}
                onChange={(event) => setCsvImport({ ...csvImport, accountKey: event.target.value })}
                placeholder="Account key"
                title="Use futures for MES/MNQ/ES/NQ imports."
              />
              <button className="sa-btn secondary" type="button" onClick={importWebullCsv} disabled={busy || !csvImport.text}>
                Import Webull CSV
              </button>
            </div>
            {csvImport.name && <p className="sa-muted">Loaded file: {csvImport.name}</p>}
          </div>
        </div>
        <form onSubmit={saveDailyReview} style={{ marginTop: 16 }}>
          <div className="sa-grid-two">
            <label className="sa-field">Broker-reported day P/L
              <input
                type="number"
                step="0.01"
                value={dailyReviewForm.reportedProfitLoss}
                onChange={(event) => setDailyReviewForm({ ...dailyReviewForm, reportedProfitLoss: event.target.value })}
                placeholder="623.00"
              />
            </label>
            <label className="sa-field">Plan for next session
              <input
                value={dailyReviewForm.planForNextSession}
                onChange={(event) => setDailyReviewForm({ ...dailyReviewForm, planForNextSession: event.target.value })}
                placeholder="What should tomorrow focus on?"
              />
            </label>
          </div>
          <label className="sa-field">Day notes
            <textarea value={dailyReviewForm.dayNotes} onChange={(event) => setDailyReviewForm({ ...dailyReviewForm, dayNotes: event.target.value })} placeholder="What happened today? What were the major setups?" />
          </label>
          <label className="sa-field">Execution notes
            <textarea value={dailyReviewForm.executionNotes} onChange={(event) => setDailyReviewForm({ ...dailyReviewForm, executionNotes: event.target.value })} placeholder="Entries, exits, adds, trims, hesitation, or discipline." />
          </label>
          <label className="sa-field">Lessons
            <textarea value={dailyReviewForm.lessons} onChange={(event) => setDailyReviewForm({ ...dailyReviewForm, lessons: event.target.value })} placeholder="What should the model/operator remember?" />
          </label>
          <button className="sa-btn" disabled={busy}>{busy ? "Saving..." : "Save Daily Review"}</button>
        </form>
        <div className="sa-table-wrap sa-scroll-table" style={{ marginTop: 16 }}>
          <table className="sa-table">
            <thead>
              <tr>
                <th>Trade</th>
                <th>Side</th>
                <th>Status</th>
                <th>Entry</th>
                <th>Exit</th>
                <th>P/L</th>
                <th>Score</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {selectedDayTrades.map((trade) => {
                const hasNotes = tradeHasSavedNotes(trade);
                const selected = selectedTrade?.importId === trade.importId;
                return (
                  <tr key={trade.importId || trade.sourceKey} className={selected ? "is-selected" : ""}>
                    <td>
                      <button className="sa-link-button" type="button" onClick={() => selectTrade(trade)}>
                        <strong>{trade.daySequence ? `${trade.daySequence}. ` : ""}{trade.symbol || "-"}</strong>
                      </button>
                      {tradeRoleLabel(trade) && <span className="sa-pill watch">{tradeRoleLabel(trade)}</span>}
                      {trade.instrumentType === "OPTION" && <span className="sa-pill neutral">Option</span>}
                      {trade.instrumentType === "FUTURE" && <span className="sa-pill neutral">Future</span>}
                      {trade.scoreProxySymbol && <span className="sa-subtle">Score proxy {trade.scoreProxySymbol}</span>}
                      <span className="sa-subtle">{formatDateTime(trade.openedAt || trade.createdAt || trade.importedAt)}</span>
                    </td>
                    <td>{trade.side || "-"}</td>
                    <td><StatusBadge status={trade.status} /></td>
                    <td>{formatMoney(trade.entryPrice)}</td>
                    <td>{formatMoney(trade.exitPrice)}</td>
                    <td>{formatMoney(trade.profitLoss)}</td>
                    <td>{trade.scoreAtImport ?? "-"}</td>
                    <td>{hasNotes ? <span className="sa-pill best">Saved</span> : <span className="sa-pill neutral">Blank</span>}</td>
                  </tr>
                );
              })}
              {selectedDayTrades.length === 0 && (
                <tr><td colSpan="8">No imported trades for this date yet. Sync brokerage trades, then reload the day.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="sa-card" style={{ marginTop: 16 }}>
        <div className="sa-journal-layout">
          <div className="sa-table-wrap">
            <h2>{selectedDayTrades.length ? `Trade History For ${dailyReviewDate}` : "Trade History"}</h2>
            <p className="sa-muted">Rows are sorted by entry time. Pick a different date in Daily Review to load that day for journaling.</p>
            <div className="sa-scroll-table">
            <table className="sa-table sa-journal-table">
              <thead>
                <tr>
                  <th>Trade</th>
                  <th>Side</th>
                  <th>Status</th>
                  <th>Entry</th>
                  <th>Exit</th>
                  <th>P/L</th>
                  <th>Score</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {tradeRows.map((trade) => {
                  const selected = selectedTrade?.importId === trade.importId;
                  const hasNotes = tradeHasSavedNotes(trade);
                  return (
                    <tr key={trade.importId || trade.sourceKey} className={selected ? "is-selected" : ""}>
                      <td>
                        <button className="sa-link-button" type="button" onClick={() => selectTrade(trade)}>
                          <strong>{trade.symbol || "-"}</strong>
                        </button>
                        {tradeRoleLabel(trade) && <span className="sa-pill watch">{tradeRoleLabel(trade)}</span>}
                        {trade.instrumentType === "OPTION" && <span className="sa-pill neutral">Option</span>}
                        {trade.instrumentType === "FUTURE" && <span className="sa-pill neutral">Future</span>}
                        {trade.excludedFromCoreModel && <span className="sa-pill neutral">Research only</span>}
                        <span className="sa-subtle">{formatDateTime(trade.openedAt || trade.createdAt || trade.importedAt)}</span>
                        {trade.assetSymbol && trade.assetSymbol !== trade.symbol && <span className="sa-subtle">{trade.optionType ? `${trade.optionType} ` : ""}{trade.assetSymbol}</span>}
                      </td>
                      <td>{trade.side || "-"}</td>
                      <td><StatusBadge status={trade.status} /></td>
                      <td>{formatMoney(trade.entryPrice)}</td>
                      <td>{formatMoney(trade.exitPrice)}</td>
                      <td>{formatMoney(trade.profitLoss)}</td>
                      <td>{trade.scoreAtImport ?? "-"}</td>
                      <td>{hasNotes ? <span className="sa-pill best">Saved</span> : <span className="sa-pill neutral">Blank</span>}</td>
                    </tr>
                  );
                })}
                {tradeRows.length === 0 && (
                  <tr><td colSpan="8">No imported trades yet. Go to Trades and sync brokerage history.</td></tr>
                )}
              </tbody>
            </table>
            </div>
          </div>

          <div className="sa-journal-detail">
            <h2>Trade Notes</h2>
            {selectedTrade ? (
              <form onSubmit={saveSelectedTradeNotes}>
                <div className="sa-list-item">
                  <strong>{selectedTrade.symbol} {selectedTrade.side} {selectedTrade.status}</strong>
                  <p className="sa-muted">
                    {formatDateTime(selectedTrade.openedAt)} to {formatDateTime(selectedTrade.closedAt)}
                    {" / "}P/L {formatMoney(selectedTrade.profitLoss)}
                    {" / "}Score {selectedTrade.scoreAtImport ?? "not captured"}
                  </p>
                  {selectedTrade.instrumentType === "OPTION" && (
                    <p className="sa-muted">Option thesis: {selectedTrade.optionType || "option"} {selectedTrade.assetSymbol || selectedTrade.contractSymbol || ""}</p>
                  )}
                  {selectedTrade.instrumentType === "FUTURE" && (
                    <p className="sa-muted">
                      Futures research trade: tracked for notes and review, excluded from core stock-score learning.
                      {selectedTrade.futuresPointValue ? ` Point value used: $${selectedTrade.futuresPointValue}/point.` : ""}
                    </p>
                  )}
                  {selectedTrade.excludedFromCoreModel && selectedTrade.instrumentType !== "FUTURE" && (
                    <p className="sa-muted">{selectedTrade.exclusionReason || "This trade is tracked for research and excluded from core score learning."}</p>
                  )}
                </div>
                <label className="sa-field">What qualified the setup?
                  <textarea value={notesDraft.setupNotes} onChange={(event) => setNotesDraft({ ...notesDraft, setupNotes: event.target.value })} placeholder="Example: SPY pressure was stacked short, RSI had already hit the right extreme, and the score was 94 short." />
                </label>
                <label className="sa-field">Rules and indicator context
                  <textarea value={notesDraft.ruleNotes} onChange={(event) => setNotesDraft({ ...notesDraft, ruleNotes: event.target.value })} placeholder="VWAP band, RSI rotation, 9/22 EMA, candle timing, pressure tracker, score fade, etc." />
                </label>
                <label className="sa-field">Outcome
                  <textarea value={notesDraft.outcomeNotes} onChange={(event) => setNotesDraft({ ...notesDraft, outcomeNotes: event.target.value })} placeholder="What happened after entry? Did the score confirm exit? Did pressure rise?" />
                </label>
                <label className="sa-field">Lesson for next time
                  <textarea value={notesDraft.lessonNotes} onChange={(event) => setNotesDraft({ ...notesDraft, lessonNotes: event.target.value })} placeholder="What should future-you remember?" />
                </label>
                <label className="sa-field">Mindset / execution notes
                  <textarea value={notesDraft.mindsetNotes} onChange={(event) => setNotesDraft({ ...notesDraft, mindsetNotes: event.target.value })} placeholder="Stress, hesitation, chasing, patience, confidence, liquidity, etc." />
                </label>
                <label className="sa-field">Followed rules?
                  <select value={notesDraft.followedRules} onChange={(event) => setNotesDraft({ ...notesDraft, followedRules: event.target.value })}>
                    <option value="">Not marked</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </label>
                <button className="sa-btn" disabled={busy}>{busy ? "Saving..." : "Save Trade Notes"}</button>
              </form>
            ) : (
              <p className="sa-muted">Select a trade to add notes.</p>
            )}
          </div>
        </div>
      </section>

      <section className="sa-card" style={{ marginTop: 16 }}>
        <h2>Manual Journal Entry</h2>
        <p className="sa-muted">Use this for research notes that are not tied to an imported brokerage trade.</p>
        <form onSubmit={submit}>
          <div className="sa-grid-two">
            <label className="sa-field">Symbol<input value={form.symbol} onChange={(event) => setForm({ ...form, symbol: event.target.value })} /></label>
            <label className="sa-field">Bias<select value={form.bias} onChange={(event) => setForm({ ...form, bias: event.target.value })}><option>watch</option><option>long</option><option>avoid</option><option>earnings strategy</option></select></label>
          </div>
          <label className="sa-field">Thesis<textarea value={form.thesis} onChange={(event) => setForm({ ...form, thesis: event.target.value })} /></label>
          <label className="sa-field">Plan<textarea value={form.plan} onChange={(event) => setForm({ ...form, plan: event.target.value })} /></label>
          <div className="sa-grid-two">
            <label className="sa-field">Invalidation<input value={form.invalidation} onChange={(event) => setForm({ ...form, invalidation: event.target.value })} /></label>
            <label className="sa-field">Outcome<input value={form.outcome} onChange={(event) => setForm({ ...form, outcome: event.target.value })} /></label>
          </div>
          <button className="sa-btn">Save Entry</button>
        </form>
      </section>
      <section className="sa-card" style={{ marginTop: 16 }}>
        <h2>Recent Manual Entries</h2>
        <div className="sa-list">
          {visibleManualEntries.map((entry) => (
            <div className="sa-list-item" key={entry.entryId}>
              <strong>{entry.symbol} - {entry.bias}</strong>
              <p>{entry.thesis || "No thesis recorded."}</p>
              <p className="sa-muted">{formatDateTime(entry.createdAt)}</p>
            </div>
          ))}
          {visibleManualEntries.length === 0 && (
            <p className="sa-muted">No standalone manual journal entries yet.</p>
          )}
        </div>
      </section>
    </>
  );
}

function localDateTimeInputValue(date = new Date()) {
  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function localDateInputValue(date = new Date()) {
  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 10);
}

function buildManualTradeForm(accountKey = "") {
  return {
    accountKey,
    symbol: "",
    side: "LONG",
    status: "CLOSED",
    entryPrice: "",
    exitPrice: "",
    quantity: "",
    profitLoss: "",
    openedAt: localDateTimeInputValue(),
    closedAt: localDateTimeInputValue(),
    setupNotes: "",
    ruleNotes: "",
    outcomeNotes: "",
    followedRules: "",
  };
}

function buildManualCashForm(accountKey = "") {
  return {
    accountKey,
    type: "DEPOSIT",
    amount: "",
    currency: "USD",
    occurredAt: localDateTimeInputValue(),
    notes: "",
  };
}

function BrokeragePage() {
  const [overview, setOverview] = useState(null);
  const [diagnostics, setDiagnostics] = useState(null);
  const [accountReveal, setAccountReveal] = useState(null);
  const [activity, setActivity] = useState(null);
  const [allAccounts, setAllAccounts] = useState(null);
  const [journalLearning, setJournalLearning] = useState(null);
  const [autoSync, setAutoSync] = useState(null);
  const [revealPassword, setRevealPassword] = useState("");
  const [copyMessage, setCopyMessage] = useState("");
  const [accountKey, setAccountKey] = useState("");
  const [manualTrade, setManualTrade] = useState(buildManualTradeForm());
  const [manualCash, setManualCash] = useState(buildManualCashForm());
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const autoSyncStarted = useRef(false);

  async function loadOverview(nextAccountKey = accountKey) {
    setBusy(true);
    setMessage("");
    try {
      const params = new URLSearchParams();
      if (nextAccountKey) params.set("accountKey", nextAccountKey);
      const data = await fetchJson(`/stock-analyst/brokerage/overview${params.toString() ? `?${params.toString()}` : ""}`);
      setOverview(data.overview);
      setDiagnostics(null);
      setAccountReveal(null);
      setActivity(null);
      setCopyMessage("");
      if (!nextAccountKey && data.overview?.status?.selectedAccount?.accountKey) {
        setAccountKey(data.overview.status.selectedAccount.accountKey);
        setManualTrade((current) => ({ ...current, accountKey: current.accountKey || data.overview.status.selectedAccount.accountKey }));
        setManualCash((current) => ({ ...current, accountKey: current.accountKey || data.overview.status.selectedAccount.accountKey }));
      }
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function loadActivity() {
    setBusy(true);
    setMessage("");
    try {
      const params = new URLSearchParams({
        types: "TRADE,DEPOSIT,WITHDRAW",
      });
      if (accountKey) params.set("accountKey", accountKey);
      const data = await fetchJson(`/stock-analyst/brokerage/activity?${params.toString()}`);
      setActivity(data.activity);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function loadAllAccounts() {
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson("/stock-analyst/brokerage/all-accounts?includeActivity=true");
      setAllAccounts(data.report);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function loadLearning() {
    try {
      const data = await fetchJson("/stock-analyst/journal/learning");
      setJournalLearning(data);
    } catch (error) {
      setMessage(error.message);
    }
  }

  async function syncBrokerageJournal({ quiet = false, scope = "all" } = {}) {
    if (!quiet) {
      setBusy(true);
      setMessage("");
    }
    try {
      const data = await fetchJson("/stock-analyst/journal/auto-sync", {
        method: "POST",
        body: JSON.stringify({
          scope,
          accountKey,
        }),
      });
      setAutoSync(data.sync);
      if (!quiet) {
        setMessage(`Brokerage sync complete. Imported ${data.sync.importedCount || 0} trade record(s); skipped ${data.sync.skippedExistingCount || 0} already-synced record(s).`);
      }
      await loadLearning();
    } catch (error) {
      setAutoSync({
        syncedAt: new Date().toISOString(),
        error: error.message,
      });
      if (!quiet) setMessage(error.message);
    } finally {
      if (!quiet) setBusy(false);
    }
  }

  async function saveManualTrade(event) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson("/stock-analyst/journal/manual-trade", {
        method: "POST",
        body: JSON.stringify({
          ...manualTrade,
          accountKey: manualTrade.accountKey || accountKey,
          profitLoss: manualTrade.status === "CLOSED" ? manualTrade.profitLoss : "",
        }),
      });
      setMessage(`${data.entry.symbol} ${data.entry.side.toLowerCase()} trade journaled. Score snapshot: ${data.score.score}.`);
      setManualTrade(buildManualTradeForm(manualTrade.accountKey || accountKey));
      await loadLearning();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function saveManualCash(event) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson("/stock-analyst/journal/manual-cash", {
        method: "POST",
        body: JSON.stringify({
          ...manualCash,
          accountKey: manualCash.accountKey || accountKey,
        }),
      });
      setMessage(`${data.entry.type} cash event journaled.`);
      setManualCash(buildManualCashForm(manualCash.accountKey || accountKey));
      await loadLearning();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function journalActivity(item) {
    setBusy(true);
    setMessage("");
    try {
      await fetchJson("/stock-analyst/journal/from-brokerage", {
        method: "POST",
        body: JSON.stringify({
          item,
          side: item.side || "LONG",
          notes: "Imported from brokerage activity.",
        }),
      });
      setMessage(`${item.symbol || "Activity"} imported into journal.`);
      await loadLearning();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function revealAccountEnvSnippet(event) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    setCopyMessage("");
    try {
      const data = await fetchJson("/stock-analyst/brokerage/diagnostics/reveal-account-env", {
        method: "POST",
        body: JSON.stringify({ password: revealPassword }),
      });
      setAccountReveal(data.reveal);
      setRevealPassword("");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function copyAccountEnvSnippet() {
    if (!accountReveal?.envSnippet) return;
    try {
      await navigator.clipboard.writeText(accountReveal.envSnippet);
      setCopyMessage("Copied env snippet.");
    } catch (error) {
      setCopyMessage("Copy failed. Select the text and copy it manually.");
    }
  }

  async function loadDiagnostics() {
    setBusy(true);
    setMessage("");
    try {
      const params = new URLSearchParams();
      if (accountKey) params.set("accountKey", accountKey);
      const data = await fetchJson(`/stock-analyst/brokerage/diagnostics${params.toString() ? `?${params.toString()}` : ""}`);
      setDiagnostics(data.diagnostics);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    async function bootTradesPage() {
      await loadOverview();
      await loadLearning();
      if (!autoSyncStarted.current) {
        autoSyncStarted.current = true;
        await syncBrokerageJournal({ quiet: true, scope: "all" });
      }
    }
    bootTradesPage().catch((error) => setMessage(error.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const configured = overview?.status?.configured;
  const accounts = useMemo(() => overview?.status?.accounts || [], [overview?.status?.accounts]);
  const accountLabelByKey = useMemo(() => {
    return accounts.reduce((acc, account) => {
      acc[account.accountKey] = account.label || account.accountKey;
      return acc;
    }, {});
  }, [accounts]);
  const latestImportedModelTrades = journalLearning?.brokerageJournalImports || [];
  const selectedAccount = overview?.status?.selectedAccount || null;
  const balance = overview?.balance?.balance || null;
  const currency = balance?.currency || "USD";
  const positions = overview?.positions?.rows || [];
  const openOrders = overview?.openOrders?.rows || [];
  const orderHistory = overview?.orderHistory?.rows || [];
  const sectionErrors = [
    overview?.balance?.ok === false ? `Balance: ${overview.balance.error}` : "",
    overview?.positions?.ok === false ? `Positions: ${overview.positions.error}` : "",
    overview?.openOrders?.ok === false ? `Open orders: ${overview.openOrders.error}` : "",
    overview?.orderHistory?.ok === false ? `Order history: ${overview.orderHistory.error}` : "",
  ].filter(Boolean);
  const overviewHints = overview?.diagnostics?.hints || [];
  const diagnosticHints = diagnostics?.hints || [];

  return (
    <>
      <section className="sa-card">
        <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <h1>Trades</h1>
            <p className="sa-muted">Read-only view of account balance, current positions, open orders, and recent order history.</p>
          </div>
          <div className="sa-actions" style={{ marginTop: 0 }}>
            {accounts.length > 0 && (
              <select
                className="sa-inline-select"
                value={accountKey}
                onChange={(event) => {
                  setAccountKey(event.target.value);
                  setManualTrade((current) => ({ ...current, accountKey: event.target.value }));
                  setManualCash((current) => ({ ...current, accountKey: event.target.value }));
                  loadOverview(event.target.value);
                }}
              >
                {accounts.map((account) => (
                  <option key={account.accountKey} value={account.accountKey}>
                    {account.label} ({account.maskedAccountId})
                  </option>
                ))}
              </select>
            )}
            <button className="sa-btn" onClick={() => loadOverview()} disabled={busy}>{busy ? "Refreshing..." : "Refresh Trades"}</button>
            <button className="sa-btn secondary" onClick={() => syncBrokerageJournal({ scope: "all" })} disabled={busy || !overview}>Sync Trades Into Model</button>
            <button className="sa-btn secondary" onClick={loadActivity} disabled={busy || !overview}>Load Activity</button>
            <button className="sa-btn secondary" onClick={loadAllAccounts} disabled={busy}>Load All Accounts</button>
            <button className="sa-btn secondary" onClick={loadDiagnostics} disabled={busy || !overview}>Run Webull Diagnostics</button>
          </div>
        </div>
        {message && <p className="sa-error">{message}</p>}
        {overview && (
          <>
            <div className="sa-grid" style={{ marginTop: 16 }}>
              <div className="sa-kpi"><span>Brokerage link</span><strong>{configured ? "Ready" : "Missing"}</strong></div>
              <div className="sa-kpi"><span>Selected account</span><strong>{selectedAccount?.label || "-"}</strong><span className="sa-subtle">{selectedAccount?.maskedAccountId || ""}</span></div>
              <div className="sa-kpi"><span>Total equity</span><strong>{formatMoney(balance?.totalEquity, currency)}</strong></div>
              <div className="sa-kpi"><span>Buying power</span><strong>{formatMoney(balance?.buyingPower, currency)}</strong></div>
              <div className="sa-kpi"><span>Cash</span><strong>{formatMoney(balance?.cash, currency)}</strong></div>
              <div className="sa-kpi"><span>Day P/L</span><strong>{formatMoney(balance?.dayPnl, currency)}</strong></div>
              <div className="sa-kpi"><span>Day trades left</span><strong>{balance?.dayTradesLeft || "-"}</strong></div>
            </div>
            {!configured && (
              <p className="sa-error">
                Add <code>WEBULL_APP_KEY</code>, <code>WEBULL_APP_SECRET</code>, and <code>WEBULL_TRADING_ACCOUNTS</code> or <code>WEBULL_TRADING_ACCOUNT_ID</code> to read live brokerage data.
              </p>
            )}
            {sectionErrors.length > 0 && (
              <div className="sa-list" style={{ marginTop: 16 }}>
                {sectionErrors.map((error) => <div className="sa-list-item sa-warning" key={error}>{error}</div>)}
              </div>
            )}
            {overviewHints.length > 0 && (
              <div className="sa-list" style={{ marginTop: 16 }}>
                {overviewHints.map((hint) => <div className="sa-list-item" key={hint}>{hint}</div>)}
              </div>
            )}
            {diagnostics && (
              <div className="sa-list" style={{ marginTop: 16 }}>
                <div className="sa-list-item">
                  <strong>Account-list diagnostic:</strong> {diagnostics.accountList?.ok ? "Connected" : "Failed"}
                  {diagnostics.accountList?.error && <p className="sa-error">{diagnostics.accountList.error}</p>}
                </div>
                {(diagnostics.accountList?.providerAccounts || []).map((account, index) => (
                  <div className="sa-list-item" key={`provider-account-${index}`}>
                    <strong>Provider account candidate</strong>
                    <p className="sa-muted">
                      {account.maskedAccountId || "No account id detected"}
                      {account.nickname ? ` - ${account.nickname}` : ""}
                      {account.accountType ? ` - ${account.accountType}` : ""}
                      {account.status ? ` - ${account.status}` : ""}
                    </p>
                  </div>
                ))}
                {(diagnostics.accountList?.accountIdCandidates || []).map((candidate) => (
                  <div className="sa-list-item" key={`${candidate.fieldPath}-${candidate.maskedValue}`}>
                    <strong>Account ID field candidate</strong>
                    <p className="sa-muted">{candidate.fieldPath}: {candidate.maskedValue}</p>
                  </div>
                ))}
                {diagnosticHints.map((hint) => <div className="sa-list-item" key={hint}>{hint}</div>)}
                {(diagnostics.accountMatches || []).map((account) => (
                  <div className="sa-list-item" key={account.accountKey}>
                    <strong>{account.label}</strong>
                    <p className="sa-muted">{account.maskedAccountId} {account.listedByProvider ? "was returned by Webull account list." : "was not found in Webull account list."}</p>
                  </div>
                ))}
                <div className="sa-list-item">
                  <strong>Need the full account IDs?</strong>
                  <p className="sa-muted">Confirm your admin password to generate a ready-to-paste Render env snippet from Webull account-list.</p>
                  <form onSubmit={revealAccountEnvSnippet} className="sa-actions">
                    <input
                      type="password"
                      value={revealPassword}
                      onChange={(event) => setRevealPassword(event.target.value)}
                      placeholder="Admin password"
                      autoComplete="current-password"
                    />
                    <button className="sa-btn secondary" disabled={busy || !revealPassword}>Reveal Env Snippet</button>
                  </form>
                </div>
                {accountReveal?.envSnippet && (
                  <div className="sa-list-item">
                    <strong>Render env snippet</strong>
                    <textarea className="sa-code-textarea" readOnly value={accountReveal.envSnippet} />
                    <div className="sa-actions">
                      <button className="sa-btn secondary" type="button" onClick={copyAccountEnvSnippet}>Copy Snippet</button>
                    </div>
                    {copyMessage && <p className="sa-muted">{copyMessage}</p>}
                    <p className="sa-muted">After adding this to Render, remove old <code>WEBULL_TRADING_ACCOUNT_ID</code> / <code>WEBULL_ACCOUNT_ID</code> fallback values.</p>
                  </div>
                )}
              </div>
            )}
            <p className="sa-muted">Last refreshed: {formatDateTime(overview.generatedAt)}. This page is read-only; order placement stays behind the separate preview-and-confirm execution page.</p>
          </>
        )}
      </section>

      <section className="sa-card" style={{ marginTop: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <h2>Journal Learning Summary</h2>
            <p className="sa-muted">The score learns only from closed trades with captured features and a real P/L outcome. Open trades and deposits are tracked, but they do not train the model yet.</p>
          </div>
          <div className="sa-actions" style={{ marginTop: 0 }}>
            <button className="sa-btn secondary" type="button" onClick={loadLearning} disabled={busy}>Refresh Learning</button>
            <button className="sa-btn secondary" type="button" onClick={() => syncBrokerageJournal({ scope: "all" })} disabled={busy || !overview}>Sync Now</button>
          </div>
        </div>
        <div className="sa-grid" style={{ marginTop: 16 }}>
          <div className="sa-kpi"><span>Closed samples</span><strong>{journalLearning?.summary?.closedLearningSamples ?? "-"}</strong></div>
          <div className="sa-kpi"><span>Wins / Losses</span><strong>{journalLearning?.summary ? `${journalLearning.summary.wins} / ${journalLearning.summary.losses}` : "-"}</strong></div>
          <div className="sa-kpi"><span>Cash events</span><strong>{journalLearning?.summary?.cashEvents ?? "-"}</strong></div>
        </div>
        {autoSync && (
          <div className="sa-list" style={{ marginTop: 16 }}>
            <div className="sa-list-item">
              <strong>Last brokerage sync</strong>
              {autoSync.error ? (
                <p className="sa-error">{autoSync.error}</p>
              ) : (
                <p className="sa-muted">
                  {formatDateTime(autoSync.syncedAt)} - imported {autoSync.importedCount || 0}, skipped {autoSync.skippedExistingCount || 0}, accounts checked {autoSync.accountCount || 1}.
                </p>
              )}
              {(autoSync.runs || []).map((run) => (
                <div key={run.syncRunId} className="sa-muted">
                  <p>
                    {run.account?.label || "Account"}: orders {run.scannedOrders || 0}, unmatched orders {run.scannedUnmatchedOrders || 0}, positions {run.scannedPositions || 0}, imported {run.importedCount || 0}
                    {run.orderHistoryError ? `, order history: ${run.orderHistoryError}` : ""}
                    {run.openOrdersError ? `, open orders: ${run.openOrdersError}` : ""}
                  </p>
                  {(run.orderHistoryAttempts || run.openOrdersAttempts || []).length > 0 && (
                    <details>
                      <summary>Show provider endpoint attempts</summary>
                      <pre className="sa-code">{JSON.stringify({
                        orderHistorySelectedPath: run.orderHistorySelectedPath || null,
                        orderHistoryAttempts: run.orderHistoryAttempts || [],
                        openOrdersSelectedPath: run.openOrdersSelectedPath || null,
                        openOrdersAttempts: run.openOrdersAttempts || [],
                      }, null, 2)}</pre>
                    </details>
                  )}
                  {(run.unmatchedOrders || []).length > 0 && (
                    <details>
                      <summary>Show unmatched provider order rows</summary>
                      <pre className="sa-code">{JSON.stringify(run.unmatchedOrders, null, 2)}</pre>
                    </details>
                  )}
                </div>
              ))}
              {!autoSync.error && !autoSync.importedCount && (
                <p className="sa-muted">If this stays at zero after trading, Webull is not returning filled order details through the configured order-history endpoint yet.</p>
              )}
            </div>
          </div>
        )}
        <div className="sa-table-wrap" style={{ marginTop: 16 }}>
          <h3>Latest Auto-Imported Model Trades</h3>
          <p className="sa-muted">These records already came from brokerage orders and are the source of the learning count above. Closed trades with captured features train the score; open positions are tracked until they close.</p>
          <table className="sa-table">
            <thead>
              <tr>
                <th>Imported</th>
                <th>Account</th>
                <th>Symbol</th>
                <th>Side</th>
                <th>Status</th>
                <th>Qty</th>
                <th>Entry</th>
                <th>Exit</th>
                <th>Hold</th>
                <th>P/L</th>
                <th>Score</th>
                <th>Features</th>
              </tr>
            </thead>
            <tbody>
              {latestImportedModelTrades.map((entry) => (
                <tr key={entry.importId || entry.sourceKey}>
                  <td>{formatDateTime(entry.importedAt || entry.createdAt)}</td>
                  <td>{accountLabelByKey[entry.accountKey] || entry.accountKey || "-"}</td>
                  <td>
                    {entry.symbol ? <Link to={`/ticker/${entry.symbol}`}><strong>{entry.symbol}</strong></Link> : "-"}
                    {entry.instrumentType === "OPTION" && <span className="sa-pill neutral">Option</span>}
                    {entry.instrumentType === "FUTURE" && <span className="sa-pill neutral">Future</span>}
                    {entry.excludedFromCoreModel && <span className="sa-pill neutral">Research only</span>}
                    {entry.assetSymbol && entry.assetSymbol !== entry.symbol && (
                      <span className="sa-subtle">{entry.optionType ? `${entry.optionType} ` : ""}{entry.assetSymbol}</span>
                    )}
                  </td>
                  <td>
                    {entry.side || "-"}
                    {entry.instrumentType === "OPTION" && <span className="sa-subtle">Directional thesis</span>}
                    {entry.instrumentType === "FUTURE" && <span className="sa-subtle">Research only</span>}
                  </td>
                  <td><StatusBadge status={entry.status} /></td>
                  <td>{formatNumber(entry.quantity)}</td>
                  <td>{formatMoney(entry.entryPrice, currency)}</td>
                  <td>{formatMoney(entry.exitPrice, currency)}</td>
                  <td>{entry.holdBucket || "unknown"}</td>
                  <td>
                    {formatMoney(entry.profitLoss, currency)}
                    {entry.fees ? <span className="sa-subtle">Fees {formatMoney(entry.fees, currency)}</span> : null}
                  </td>
                  <td>{entry.scoreAtImport ?? "-"}</td>
                  <td>
                    {entry.excludedFromCoreModel ? "Tracked only" : entry.featureCaptured ? "Captured" : "Pending"}
                    {entry.featureError?.message ? <span className="sa-subtle">{entry.featureError.message}</span> : null}
                  </td>
                </tr>
              ))}
              {!busy && latestImportedModelTrades.length === 0 && (
                <tr><td colSpan="12">No imported brokerage trades yet. Use Sync Trades Into Model after Webull returns order history.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="sa-card" style={{ marginTop: 16 }}>
        <h2>Log Today's Trade</h2>
        <p className="sa-muted">Fallback only: use this when auto-sync did not capture the trade. Do not manually save a trade that already appears in Latest Auto-Imported Model Trades.</p>
        <form onSubmit={saveManualTrade} autoComplete="off">
          <div className="sa-grid-two">
            <label className="sa-field">Account
              <select value={manualTrade.accountKey || accountKey} onChange={(event) => setManualTrade({ ...manualTrade, accountKey: event.target.value })}>
                {accounts.length === 0 && <option value="">Selected account</option>}
                {accounts.map((account) => (
                  <option key={account.accountKey} value={account.accountKey}>{account.label} ({account.maskedAccountId})</option>
                ))}
              </select>
            </label>
            <label className="sa-field">Symbol<input value={manualTrade.symbol} onChange={(event) => setManualTrade({ ...manualTrade, symbol: event.target.value.toUpperCase() })} placeholder="FRVO" /></label>
            <label className="sa-field">Direction
              <select value={manualTrade.side} onChange={(event) => setManualTrade({ ...manualTrade, side: event.target.value })}>
                <option>LONG</option>
                <option>SHORT</option>
              </select>
            </label>
            <label className="sa-field">Status
              <select value={manualTrade.status} onChange={(event) => setManualTrade({ ...manualTrade, status: event.target.value })}>
                <option>CLOSED</option>
                <option>OPEN</option>
                <option>CANCELLED</option>
              </select>
            </label>
            <label className="sa-field">Entry price<input value={manualTrade.entryPrice} onChange={(event) => setManualTrade({ ...manualTrade, entryPrice: event.target.value })} placeholder="0.00" /></label>
            <label className="sa-field">Exit price<input value={manualTrade.exitPrice} onChange={(event) => setManualTrade({ ...manualTrade, exitPrice: event.target.value })} placeholder="0.00" disabled={manualTrade.status !== "CLOSED"} /></label>
            <label className="sa-field">Shares<input value={manualTrade.quantity} onChange={(event) => setManualTrade({ ...manualTrade, quantity: event.target.value })} placeholder="100" /></label>
            <label className="sa-field">Realized P/L<input value={manualTrade.profitLoss} onChange={(event) => setManualTrade({ ...manualTrade, profitLoss: event.target.value })} placeholder="-1.61" disabled={manualTrade.status !== "CLOSED"} /></label>
            <label className="sa-field">Entry time<input type="datetime-local" value={manualTrade.openedAt} onChange={(event) => setManualTrade({ ...manualTrade, openedAt: event.target.value })} /></label>
            <label className="sa-field">Exit time<input type="datetime-local" value={manualTrade.closedAt} onChange={(event) => setManualTrade({ ...manualTrade, closedAt: event.target.value })} disabled={manualTrade.status !== "CLOSED"} /></label>
          </div>
          <label className="sa-field">Setup notes<textarea value={manualTrade.setupNotes} onChange={(event) => setManualTrade({ ...manualTrade, setupNotes: event.target.value })} placeholder="Why did this setup qualify?" /></label>
          <div className="sa-grid-two">
            <label className="sa-field">Rule notes<textarea value={manualTrade.ruleNotes} onChange={(event) => setManualTrade({ ...manualTrade, ruleNotes: event.target.value })} placeholder="RSI/VWAP/EMA/candle timing notes." /></label>
            <label className="sa-field">Outcome notes<textarea value={manualTrade.outcomeNotes} onChange={(event) => setManualTrade({ ...manualTrade, outcomeNotes: event.target.value })} placeholder="What happened after entry?" /></label>
          </div>
          <label className="sa-field">Followed rules?
            <select value={manualTrade.followedRules} onChange={(event) => setManualTrade({ ...manualTrade, followedRules: event.target.value })}>
              <option value="">Not marked</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </label>
          <button className="sa-btn" disabled={busy || !manualTrade.symbol}>{busy ? "Saving..." : "Save Trade Journal"}</button>
        </form>
      </section>

      <section className="sa-card" style={{ marginTop: 16 }}>
        <h2>Log Cash Event</h2>
        <p className="sa-muted">Record deposits or withdrawals here when Webull activity is unavailable. These are account records, not score-training samples.</p>
        <form onSubmit={saveManualCash}>
          <div className="sa-grid-two">
            <label className="sa-field">Account
              <select value={manualCash.accountKey || accountKey} onChange={(event) => setManualCash({ ...manualCash, accountKey: event.target.value })}>
                {accounts.length === 0 && <option value="">Selected account</option>}
                {accounts.map((account) => (
                  <option key={account.accountKey} value={account.accountKey}>{account.label} ({account.maskedAccountId})</option>
                ))}
              </select>
            </label>
            <label className="sa-field">Type
              <select value={manualCash.type} onChange={(event) => setManualCash({ ...manualCash, type: event.target.value })}>
                <option>DEPOSIT</option>
                <option>WITHDRAW</option>
                <option>TRANSFER</option>
                <option>ADJUSTMENT</option>
              </select>
            </label>
            <label className="sa-field">Amount<input value={manualCash.amount} onChange={(event) => setManualCash({ ...manualCash, amount: event.target.value })} placeholder="100.00" /></label>
            <label className="sa-field">Currency<input value={manualCash.currency} onChange={(event) => setManualCash({ ...manualCash, currency: event.target.value.toUpperCase() })} /></label>
            <label className="sa-field">Date/time<input type="datetime-local" value={manualCash.occurredAt} onChange={(event) => setManualCash({ ...manualCash, occurredAt: event.target.value })} /></label>
          </div>
          <label className="sa-field">Notes<textarea value={manualCash.notes} onChange={(event) => setManualCash({ ...manualCash, notes: event.target.value })} placeholder="Deposit source, transfer note, or broker memo." /></label>
          <button className="sa-btn secondary" disabled={busy || !manualCash.amount}>{busy ? "Saving..." : "Save Cash Event"}</button>
        </form>
      </section>

      {allAccounts && (
        <section className="sa-card" style={{ marginTop: 16 }}>
          <h2>All Accounts Snapshot</h2>
          <div className="sa-table-wrap">
            <table className="sa-table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Total Equity</th>
                  <th>Cash</th>
                  <th>Positions</th>
                  <th>Open Orders</th>
                  <th>Activity</th>
                </tr>
              </thead>
              <tbody>
                {(allAccounts.accounts || []).map((row) => {
                  const rowBalance = row.overview?.balance?.balance || {};
                  return (
                    <tr key={row.account.accountKey}>
                      <td>{row.account.label}<span className="sa-subtle">{row.account.maskedAccountId}</span></td>
                      <td>{formatMoney(rowBalance.totalEquity, rowBalance.currency || "USD")}</td>
                      <td>{formatMoney(rowBalance.cash, rowBalance.currency || "USD")}</td>
                      <td>{row.overview?.positions?.rows?.length || 0}</td>
                      <td>{row.overview?.openOrders?.rows?.length || 0}</td>
                      <td>{row.activity?.rows?.length || 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {activity && (
        <section className="sa-card" style={{ marginTop: 16 }}>
          <h2>Account Activity</h2>
          {activity.errors?.length > 0 && activity.errors.map((error) => (
            <p className="sa-warning" key={`${error.activityType}-${error.error}`}>{error.activityType}: {error.error}</p>
          ))}
          <div className="sa-table-wrap">
            <table className="sa-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Symbol</th>
                  <th>Side</th>
                  <th>Amount</th>
                  <th>Qty</th>
                  <th>Price</th>
                  <th>Date</th>
                  <th>Description</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(activity.rows || []).map((row, index) => (
                  <tr key={`${row.activityId || row.createdAt || row.tradeDate || index}`}>
                    <td>{row.activityType || "-"}</td>
                    <td>{row.symbol || "-"}</td>
                    <td>{row.side || "-"}</td>
                    <td>{formatMoney(row.amount, row.currency || "USD")}</td>
                    <td>{formatNumber(row.quantity)}</td>
                    <td>{formatMoney(row.price, row.currency || "USD")}</td>
                    <td>{formatDateTime(row.createdAt || row.tradeDate || row.settledAt)}</td>
                    <td>{row.description || "-"}</td>
                    <td>{row.symbol ? <button className="sa-btn secondary" onClick={() => journalActivity(row)} disabled={busy}>Journal</button> : null}</td>
                  </tr>
                ))}
                {!busy && (activity.rows || []).length === 0 && (
                  <tr><td colSpan="9">No account activity returned. If deposits are missing, your Webull app may need Broker Activity permissions for the cash-activity endpoint.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="sa-card" style={{ marginTop: 16 }}>
        <h2>Current Positions</h2>
        <div className="sa-table-wrap">
          <table className="sa-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Side</th>
                <th>Quantity</th>
                <th>Avg Price</th>
                <th>Last</th>
                <th>Market Value</th>
                <th>Unrealized P/L</th>
              </tr>
            </thead>
            <tbody>
              {positions.map((position, index) => (
                <tr key={`${position.symbol || "position"}-${index}`}>
                  <td>{position.symbol ? <Link to={`/ticker/${position.symbol}`}><strong>{position.symbol}</strong></Link> : "-"}</td>
                  <td>{position.side || "-"}</td>
                  <td>{formatNumber(position.quantity)}</td>
                  <td>{formatMoney(position.averagePrice, currency)}</td>
                  <td>{formatMoney(position.lastPrice, currency)}</td>
                  <td>{formatMoney(position.marketValue, currency)}</td>
                  <td>
                    {formatMoney(position.unrealizedPnl, currency)}
                    <span className="sa-subtle">{formatPercent(position.unrealizedPnlPercent)}</span>
                  </td>
                </tr>
              ))}
              {!busy && positions.length === 0 && (
                <tr><td colSpan="7">No current positions returned.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="sa-card" style={{ marginTop: 16 }}>
        <h2>Open Orders</h2>
        <div className="sa-table-wrap">
          <table className="sa-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Side</th>
                <th>Status</th>
                <th>Type</th>
                <th>Quantity</th>
                <th>Filled</th>
                <th>Limit</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {openOrders.map((order, index) => (
                <tr key={`${order.clientOrderId || order.orderId || "open"}-${index}`}>
                  <td>{order.symbol || "-"}</td>
                  <td>{order.side || "-"}</td>
                  <td><StatusBadge status={order.status} /></td>
                  <td>{order.orderType || "-"}</td>
                  <td>{formatNumber(order.quantity)}</td>
                  <td>{formatNumber(order.filledQuantity)}</td>
                  <td>{formatMoney(order.limitPrice, currency)}</td>
                  <td>{formatDateTime(order.createdAt)}</td>
                </tr>
              ))}
              {!busy && openOrders.length === 0 && (
                <tr><td colSpan="8">No open orders returned.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="sa-card" style={{ marginTop: 16 }}>
        <h2>Recent Order History</h2>
        <div className="sa-table-wrap">
          <table className="sa-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Side</th>
                <th>Status</th>
                <th>Type</th>
                <th>Quantity</th>
                <th>Filled</th>
                <th>Avg Fill</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {orderHistory.map((order, index) => (
                <tr key={`${order.clientOrderId || order.orderId || "history"}-${index}`}>
                  <td>{order.symbol || "-"}</td>
                  <td>{order.side || "-"}</td>
                  <td><StatusBadge status={order.status} /></td>
                  <td>{order.orderType || "-"}</td>
                  <td>{formatNumber(order.quantity)}</td>
                  <td>{formatNumber(order.filledQuantity)}</td>
                  <td>{formatMoney(order.averageFilledPrice, currency)}</td>
                  <td>{formatDateTime(order.createdAt)}</td>
                </tr>
              ))}
              {!busy && orderHistory.length === 0 && (
                <tr><td colSpan="8">No recent orders returned.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {overview && (
          <details style={{ marginTop: 14 }}>
            <summary>Provider raw payloads</summary>
            <pre className="sa-code">{JSON.stringify({
              balance: overview.balance?.raw || null,
              positions: overview.positions?.raw || null,
              openOrders: overview.openOrders?.raw || null,
              orderHistory: overview.orderHistory?.raw || null,
            }, null, 2)}</pre>
          </details>
        )}
      </section>
    </>
  );
}

function ExecutionPage() {
  const [status, setStatus] = useState(null);
  const [command, setCommand] = useState("long SPY at market $500");
  const [form, setForm] = useState({
    accountKey: "",
    symbol: "SPY",
    side: "BUY",
    orderType: "MARKET",
    amountUsd: "500",
    quantity: "",
    limitPrice: "",
  });
  const [preview, setPreview] = useState(null);
  const [confirmation, setConfirmation] = useState("");
  const [result, setResult] = useState(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  async function loadStatus(nextAccountKey = "") {
    const params = new URLSearchParams();
    if (nextAccountKey) params.set("accountKey", nextAccountKey);
    const data = await fetchJson(`/stock-analyst/execution/status${params.toString() ? `?${params.toString()}` : ""}`);
    setStatus(data.status);
    if (!nextAccountKey && data.status?.selectedAccount?.accountKey) {
      setForm((current) => ({ ...current, accountKey: current.accountKey || data.status.selectedAccount.accountKey }));
    }
  }

  useEffect(() => {
    loadStatus().catch((error) => setMessage(error.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function parseCommand() {
    setMessage("");
    const data = await fetchJson("/stock-analyst/execution/parse", {
      method: "POST",
      body: JSON.stringify({ command }),
    });
    setForm({
      accountKey: form.accountKey,
      symbol: data.parsed.symbol || "",
      side: data.parsed.side || "BUY",
      orderType: data.parsed.orderType || "MARKET",
      amountUsd: data.parsed.amountUsd || "",
      quantity: data.parsed.quantity || "",
      limitPrice: data.parsed.limitPrice || "",
    });
  }

  async function previewOrder(event) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    setResult(null);
    try {
      const body = {
        ...form,
        accountKey: form.accountKey || status?.selectedAccount?.accountKey || null,
        amountUsd: form.amountUsd || null,
        quantity: form.quantity || null,
        limitPrice: form.limitPrice || null,
        command,
      };
      const data = await fetchJson("/stock-analyst/execution/preview", {
        method: "POST",
        body: JSON.stringify(body),
      });
      setPreview(data.preview);
      setConfirmation("");
    } catch (error) {
      setMessage(error.message);
      setPreview(null);
    } finally {
      setBusy(false);
    }
  }

  async function submitOrder() {
    if (!preview) return;
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson("/stock-analyst/execution/submit", {
        method: "POST",
        body: JSON.stringify({
          previewId: preview.previewId,
          confirmation,
        }),
      });
      setResult(data.execution);
      setPreview(null);
      setConfirmation("");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  const configured = status?.configured;
  const executionEnabled = status?.executionEnabled;
  const accounts = status?.accounts || [];
  const selectedAccount = accounts.find((account) => account.accountKey === form.accountKey) || status?.selectedAccount || null;

  return (
    <>
      <section className="sa-card">
        <h1>Execution</h1>
        <p className="sa-muted">Stock/ETF execution is preview-first and confirmation-gated. Options, futures, and short selling are blocked in this cockpit.</p>
        <div className="sa-grid" style={{ marginBottom: 16 }}>
          <div className="sa-kpi"><span>Webull trading</span><strong>{configured ? "Ready" : "Missing"}</strong></div>
          <div className="sa-kpi"><span>Selected account</span><strong>{selectedAccount?.label || "-"}</strong><span className="sa-subtle">{selectedAccount?.maskedAccountId || ""}</span></div>
          <div className="sa-kpi"><span>Live submit</span><strong>{executionEnabled ? "Enabled" : "Locked"}</strong></div>
          <div className="sa-kpi"><span>Max order</span><strong>${status?.maxOrderNotionalUsd ?? "-"}</strong></div>
        </div>
        {!configured && <p className="sa-error">Add Webull trading env keys before previewing real orders.</p>}
        {!executionEnabled && <p className="sa-muted">Live submit stays locked until both <code>STOCK_EXECUTION_ENABLED=true</code> and <code>WEBULL_TRADING_ENABLED=true</code> are set.</p>}
      </section>

      <section className="sa-card" style={{ marginTop: 16 }}>
        <h2>Order Draft</h2>
        <label className="sa-field">Command
          <input value={command} onChange={(event) => setCommand(event.target.value)} placeholder="long SPY at market $500" />
        </label>
        <button className="sa-btn secondary" type="button" onClick={parseCommand}>Parse Command</button>

        <form onSubmit={previewOrder} style={{ marginTop: 16 }}>
          <div className="sa-grid-two">
            <label className="sa-field">Account
              <select
                value={form.accountKey}
                onChange={(event) => {
                  setForm({ ...form, accountKey: event.target.value });
                  loadStatus(event.target.value).catch((error) => setMessage(error.message));
                }}
              >
                {accounts.length === 0 && <option value="">No account configured</option>}
                {accounts.map((account) => (
                  <option key={account.accountKey} value={account.accountKey}>{account.label} ({account.maskedAccountId})</option>
                ))}
              </select>
            </label>
            <label className="sa-field">Symbol<input value={form.symbol} onChange={(event) => setForm({ ...form, symbol: event.target.value.toUpperCase() })} /></label>
            <label className="sa-field">Side<select value={form.side} onChange={(event) => setForm({ ...form, side: event.target.value })}><option>BUY</option><option>SELL</option></select></label>
            <label className="sa-field">Order type<select value={form.orderType} onChange={(event) => setForm({ ...form, orderType: event.target.value })}><option>MARKET</option><option>LIMIT</option></select></label>
            <label className="sa-field">Dollar amount<input value={form.amountUsd} onChange={(event) => setForm({ ...form, amountUsd: event.target.value, quantity: "" })} placeholder="500" /></label>
            <label className="sa-field">Shares<input value={form.quantity} onChange={(event) => setForm({ ...form, quantity: event.target.value, amountUsd: "" })} placeholder="1" /></label>
            <label className="sa-field">Limit price<input value={form.limitPrice} onChange={(event) => setForm({ ...form, limitPrice: event.target.value })} disabled={form.orderType !== "LIMIT"} /></label>
          </div>
          <button className="sa-btn" disabled={busy || !configured}>{busy ? "Working..." : "Preview With Webull"}</button>
        </form>
        {message && <p className="sa-error">{message}</p>}
      </section>

      {preview && (
        <section className="sa-card" style={{ marginTop: 16 }}>
          <h2>Preview Ready</h2>
          <div className="sa-order-summary">
            <p><strong>Symbol:</strong> {preview.order.symbol}</p>
            <p><strong>Account:</strong> {preview.account?.label || "-"} {preview.account?.maskedAccountId ? `(${preview.account.maskedAccountId})` : ""}</p>
            <p><strong>Side:</strong> {preview.order.side}</p>
            <p><strong>Type:</strong> {preview.order.order_type}</p>
            <p><strong>Entrust:</strong> {preview.order.entrust_type}</p>
            <p><strong>Client order ID:</strong> {preview.order.client_order_id}</p>
            <p><strong>Expires:</strong> {formatDateTime(preview.expiresAt)}</p>
          </div>
          <details>
            <summary>Provider preview response</summary>
            <pre className="sa-code">{JSON.stringify(preview.providerPreview, null, 2)}</pre>
          </details>
          <label className="sa-field" style={{ marginTop: 14 }}>Final confirmation
            <input value={confirmation} onChange={(event) => setConfirmation(event.target.value)} placeholder={preview.confirmationPhrase} />
          </label>
          <button className="sa-btn danger" onClick={submitOrder} disabled={busy || confirmation !== preview.confirmationPhrase || !executionEnabled}>Submit Live Order</button>
          <p className="sa-muted">Type exactly <code>{preview.confirmationPhrase}</code> to unlock submit.</p>
        </section>
      )}

      {result && (
        <section className="sa-card" style={{ marginTop: 16 }}>
          <h2>Submitted</h2>
          <p><strong>Execution ID:</strong> {result.executionId}</p>
          <p><strong>Submitted:</strong> {formatDateTime(result.submittedAt)}</p>
          <pre className="sa-code">{JSON.stringify(result.providerResult, null, 2)}</pre>
        </section>
      )}
    </>
  );
}

function ValuationRatingPill({ rating }) {
  const normalized = String(rating || "UNKNOWN").toUpperCase();
  const className = normalized === "DISCOUNT"
    ? "best"
    : normalized === "FAIR"
      ? "watch"
      : ["EXPENSIVE", "LIFETIME_PLUS", "NO_POSITIVE_EARNINGS", "DATA_ERROR"].includes(normalized)
        ? "avoid"
        : "neutral";
  return <span className={`sa-pill ${className}`}>{normalized.replace(/_/g, " ")}</span>;
}

function ValuationRowsTable({ rows = [] }) {
  if (!rows.length) return null;
  return (
    <div className="sa-table-wrap">
      <table className="sa-table">
        <thead>
          <tr>
            <th>Symbol</th>
            <th>Company</th>
            <th>Provider</th>
            <th>Rating</th>
            <th>Price</th>
            <th>Market Cap</th>
            <th>Net Income</th>
            <th>Payback</th>
            <th>Earnings Yield</th>
            <th>Dividend Yield</th>
            <th>100Y Earnings</th>
            <th>100Y Dividends</th>
            <th>100Y Combined</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.symbol}>
              <td><strong>{row.symbol}</strong></td>
              <td>
                {row.companyName || row.symbol}
                <span className="sa-subtle">{[row.sector, row.fiscalYear ? `FY ${row.fiscalYear}` : ""].filter(Boolean).join(" / ")}</span>
                {row.error ? <span className="sa-subtle">{row.error}</span> : null}
              </td>
              <td>{String(row.provider || "-").toUpperCase()}</td>
              <td><ValuationRatingPill rating={row.rating} /></td>
              <td>{formatMoney(row.price)}</td>
              <td>{formatMoney(row.marketCap)}</td>
              <td>{formatMoney(row.netIncome)}</td>
              <td>{formatYears(row.paybackYears)}</td>
              <td>{formatPercent(row.earningsYieldPercent)}</td>
              <td>{formatPercent(row.dividendYieldPercent)}</td>
              <td>{formatPercent(row.hundredYearEarningsReturnPercent)}</td>
              <td>{formatPercent(row.hundredYearDividendReturnPercent)}</td>
              <td>{formatPercent(row.hundredYearCombinedReturnPercent)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function IndicatorAlertsPage() {
  const [alerts, setAlerts] = useState([]);
  const [events, setEvents] = useState([]);
  const [runtime, setRuntime] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [draft, setDraft] = useState({
    symbol: "SPX",
    indicator: "RSI",
    condition: "AT_OR_BELOW",
    threshold: "30",
    timeframe: "1m",
    outputSize: "390",
    crossLookbackCandles: 1,
    params: { period: 14 },
    deliveryChannels: ["SMS"],
    smsCarrierId: "verizon",
    deliveryWindowStart: "",
    deliveryWindowEnd: "",
    deliveryTimezone: "America/New_York",
    deliveryDays: "",
  });

  const load = useCallback(async () => {
    const data = await fetchJson("/stock-analyst/indicator-alerts");
    setAlerts(data.alerts || []);
    setEvents(data.events || []);
    setRuntime(data.runtime || null);
    setDefaults(data.defaults || null);
    rememberDisplayTimezone(data.defaults?.deliveryTimezone);
    if (data.defaults?.smsCarrierId) {
      setDraft((current) => ({ ...current, smsCarrierId: current.smsCarrierId || data.defaults.smsCarrierId }));
    }
    setDraft((current) => ({
      ...current,
      deliveryWindowStart: current.deliveryWindowStart || data.defaults?.deliveryWindowStart || "",
      deliveryWindowEnd: current.deliveryWindowEnd || data.defaults?.deliveryWindowEnd || "",
      deliveryTimezone: current.deliveryTimezone || data.defaults?.deliveryTimezone || "America/New_York",
      deliveryDays: current.deliveryDays || data.defaults?.deliveryDays || "",
    }));
  }, []);

  useEffect(() => {
    load().catch((error) => setMessage(error.message));
  }, [load]);

  function defaultParamsForIndicator(indicator) {
    const normalized = String(indicator || "").toUpperCase();
    const parameterDefaults = defaults?.parameterDefaults || {};
    if (parameterDefaults[normalized]) return parameterDefaults[normalized];
    if (normalized === "RSI") return parameterDefaults.RSI || { period: 14 };
    if (normalized === "EMA") return parameterDefaults.EMA || { period: 22 };
    if (normalized.startsWith("VWAP")) return parameterDefaults.VWAP || { bandMultiplier: 1.25 };
    if (normalized.startsWith("MACD")) return parameterDefaults.MACD || { fast: 12, slow: 26, signal: 9 };
    if (normalized.startsWith("TSI")) return parameterDefaults.TSI || { long: 25, short: 13, signal: 13 };
    if (normalized.startsWith("SMI")) return parameterDefaults.SMI || { lookback: 20, smooth: 5, signal: 5 };
    if (normalized.startsWith("PPO")) return parameterDefaults.PPO || { slow: 22, fast: 9, signal: 9 };
    if (normalized === "RELATIVE_VOLUME") return parameterDefaults.RELATIVE_VOLUME || { lookback: 20 };
    return {};
  }

  function parameterFieldsForIndicator(indicator) {
    const normalized = String(indicator || "").toUpperCase();
    const parameterSpecs = defaults?.parameterSpecs || {};
    if (Array.isArray(parameterSpecs[normalized])) return parameterSpecs[normalized];
    if (normalized === "RSI") return [{ key: "period", label: "RSI period", placeholder: "14" }];
    if (normalized === "EMA") return [{ key: "period", label: "EMA period", placeholder: "22" }];
    if (normalized.startsWith("VWAP")) return [{ key: "bandMultiplier", label: "Band multiplier", placeholder: "1.25", step: "0.05" }];
    if (normalized.startsWith("MACD")) return [
      { key: "fast", label: "Fast EMA", placeholder: "12" },
      { key: "slow", label: "Slow EMA", placeholder: "26" },
      { key: "signal", label: "Signal", placeholder: "9" },
    ];
    if (normalized.startsWith("TSI")) return [
      { key: "long", label: "Long smoothing", placeholder: "25" },
      { key: "short", label: "Short smoothing", placeholder: "13" },
      { key: "signal", label: "Signal", placeholder: "13" },
    ];
    if (normalized.startsWith("SMI")) return [
      { key: "lookback", label: "Lookback", placeholder: "20" },
      { key: "smooth", label: "Smoothing", placeholder: "5" },
      { key: "signal", label: "Signal", placeholder: "5" },
    ];
    if (normalized.startsWith("PPO")) return [
      { key: "slow", label: "Slow EMA", placeholder: "22" },
      { key: "fast", label: "Fast EMA", placeholder: "9" },
      { key: "signal", label: "Signal", placeholder: "9" },
    ];
    if (normalized === "RELATIVE_VOLUME") return [{ key: "lookback", label: "Volume lookback", placeholder: "20" }];
    return [];
  }

  function setIndicator(indicator) {
    setDraft({
      ...draft,
      indicator,
      params: defaultParamsForIndicator(indicator),
    });
  }

  function updateParam(key, value) {
    setDraft((current) => ({
      ...current,
      params: {
        ...(current.params || {}),
        [key]: value === "" ? "" : Number(value),
      },
    }));
  }

  function cleanParams(params = {}) {
    return Object.fromEntries(Object.entries(params)
      .filter(([, value]) => value !== "" && value !== null && value !== undefined)
      .map(([key, value]) => [key, Number.isFinite(Number(value)) ? Number(value) : value]));
  }

  function buildPayload() {
    return {
      symbol: normalizeTickerSymbol(draft.symbol),
      indicator: draft.indicator,
      condition: draft.condition,
      threshold: draft.threshold === "" ? null : Number(draft.threshold),
      timeframe: draft.timeframe,
      outputSize: Number(draft.outputSize || 390),
      crossLookbackCandles: Number(draft.crossLookbackCandles || 1),
      params: cleanParams(draft.params || {}),
      deliveryChannels: draft.deliveryChannels,
      smsCarrierId: draft.smsCarrierId,
      deliveryWindowStart: draft.deliveryWindowStart || "",
      deliveryWindowEnd: draft.deliveryWindowEnd || "",
      deliveryTimezone: draft.deliveryTimezone || defaults?.deliveryTimezone || "America/New_York",
      deliveryDays: draft.deliveryDays || "",
      emailTo: draft.emailTo || "",
      label: draft.label || "",
    };
  }

  async function createAlert(event) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      await fetchJson("/stock-analyst/indicator-alerts", {
        method: "POST",
        body: JSON.stringify(buildPayload()),
      });
      setMessage("Indicator alert saved.");
      await load();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function updateAlert(alert, patch) {
    setBusy(true);
    setMessage("");
    try {
      await fetchJson(`/stock-analyst/indicator-alerts/${encodeURIComponent(alert.alertId)}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      });
      await load();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function deleteAlert(alert) {
    setBusy(true);
    setMessage("");
    try {
      await fetchJson(`/stock-analyst/indicator-alerts/${encodeURIComponent(alert.alertId)}`, { method: "DELETE" });
      await load();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function runNow() {
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson("/stock-analyst/indicator-alerts/run", { method: "POST" });
      setMessage(`Checked ${data.result?.evaluatedCount || 0}; triggered ${data.result?.triggeredCount || 0}.`);
      await load();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  const indicatorOptions = defaults?.indicators || ["RSI", "EMA", "VWAP", "VWAP_UPPER_BAND", "VWAP_LOWER_BAND", "MACD", "TSI", "SMI", "PPO"];
  const conditionOptions = defaults?.conditions || ["AT_OR_ABOVE", "AT_OR_BELOW", "CROSSING_UP", "CROSSING_DOWN", "ABOVE_SIGNAL", "BELOW_SIGNAL", "CROSSING_ABOVE_SIGNAL", "CROSSING_BELOW_SIGNAL"];
  const parameterFields = parameterFieldsForIndicator(draft.indicator);

  return (
    <>
      <section className="sa-card">
        <div className="sa-live-score-header">
          <div>
            <h1>Indicator Alerts</h1>
            <p className="sa-muted">Create independent indicator triggers for SPX or any supported symbol. Choose an indicator, set its normal parameters, then pick the trigger condition.</p>
          </div>
          <div className="sa-actions">
            <button className="sa-btn secondary" type="button" onClick={runNow} disabled={busy}>{busy ? "Checking..." : "Run Alerts Now"}</button>
          </div>
        </div>
        <div className="sa-live-control-strip">
          <span><strong>Runtime:</strong> {runtime?.enabled ? "on" : "off"}</span>
          <span><strong>Poll:</strong> {runtime?.pollIntervalMs ? `${Math.round(runtime.pollIntervalMs / 1000)}s` : "-"}</span>
          <span><strong>Text:</strong> {defaults?.smsToConfigured ? "configured" : "not set"}</span>
          <span><strong>Email:</strong> {defaults?.emailToConfigured ? "configured" : "not set"}</span>
        </div>
        <form className="sa-grid-two" onSubmit={createAlert} style={{ marginTop: 14 }}>
          <label className="sa-field">Symbol
            <input value={draft.symbol} onChange={(event) => setDraft({ ...draft, symbol: event.target.value.toUpperCase() })} placeholder="SPX" />
          </label>
          <label className="sa-field">Label
            <input value={draft.label || ""} onChange={(event) => setDraft({ ...draft, label: event.target.value })} placeholder="SPX RSI oversold" />
          </label>
          <label className="sa-field">Indicator
            <select value={draft.indicator} onChange={(event) => setIndicator(event.target.value)}>
              {indicatorOptions.map((indicator) => <option key={indicator} value={indicator}>{indicator.replace(/_/g, " ")}</option>)}
            </select>
          </label>
          <label className="sa-field">Condition
            <select value={draft.condition} onChange={(event) => setDraft({ ...draft, condition: event.target.value })}>
              {conditionOptions.map((condition) => <option key={condition} value={condition}>{condition.replace(/_/g, " ").toLowerCase()}</option>)}
            </select>
          </label>
          <label className="sa-field">Threshold
            <input value={draft.threshold} onChange={(event) => setDraft({ ...draft, threshold: event.target.value })} placeholder="30" />
          </label>
          <label className="sa-field">Timeframe
            <select value={draft.timeframe} onChange={(event) => setDraft({ ...draft, timeframe: event.target.value })}>
              {["1m", "2m", "5m", "10m", "15m", "30m", "1h", "daily", "weekly", "monthly"].map((timeframe) => <option key={timeframe} value={timeframe}>{timeframe}</option>)}
            </select>
          </label>
          <label className="sa-field">Bars
            <input value={draft.outputSize} onChange={(event) => setDraft({ ...draft, outputSize: event.target.value })} />
          </label>
          <label className="sa-field">Cross lookback candles
            <input value={draft.crossLookbackCandles ?? 1} onChange={(event) => setDraft({ ...draft, crossLookbackCandles: Number(event.target.value) })} />
          </label>
          <label className="sa-field">Delivery
            <select value={draft.deliveryChannels.join(",")} onChange={(event) => setDraft({ ...draft, deliveryChannels: event.target.value.split(",").filter(Boolean) })}>
              <option value="SMS">Text</option>
              <option value="EMAIL">Email</option>
              <option value="EMAIL,SMS">Email + text</option>
            </select>
          </label>
          {draft.deliveryChannels.includes("SMS") && (
            <label className="sa-field">Text carrier
              <select value={draft.smsCarrierId || defaults?.smsCarrierId || "verizon"} onChange={(event) => setDraft({ ...draft, smsCarrierId: event.target.value })}>
                {(defaults?.carriers || [{ carrierId: "verizon", label: "Verizon / Visible / Xfinity Mobile" }]).map((carrier) => (
                  <option key={carrier.carrierId} value={carrier.carrierId}>{carrier.label}</option>
                ))}
              </select>
            </label>
          )}
          <label className="sa-field">Allowed start
            <input value={draft.deliveryWindowStart || ""} onChange={(event) => setDraft({ ...draft, deliveryWindowStart: event.target.value })} placeholder="09:30" />
          </label>
          <label className="sa-field">Allowed end
            <input value={draft.deliveryWindowEnd || ""} onChange={(event) => setDraft({ ...draft, deliveryWindowEnd: event.target.value })} placeholder="16:00" />
          </label>
          <label className="sa-field">Schedule timezone
            <select value={draft.deliveryTimezone || "America/New_York"} onChange={(event) => setDraft({ ...draft, deliveryTimezone: event.target.value })}>
              {ALERT_TIMEZONE_OPTIONS.map((timezone) => <option key={timezone} value={timezone}>{timezone}</option>)}
            </select>
          </label>
          <label className="sa-field">Allowed days
            <input value={draft.deliveryDays || ""} onChange={(event) => setDraft({ ...draft, deliveryDays: event.target.value })} placeholder="Mon,Tue,Wed,Thu,Fri" />
          </label>
          {parameterFields.length > 0 && (
            <div className="sa-field sa-form-span">
              <span>Indicator settings</span>
              <div className="sa-grid-two">
                {parameterFields.map((field) => (
                  <label className="sa-field" key={field.key}>{field.label}
                    <input
                      type="number"
                      step={field.step || "1"}
                      value={draft.params?.[field.key] ?? ""}
                      onChange={(event) => updateParam(field.key, event.target.value)}
                      placeholder={field.placeholder}
                    />
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="sa-actions">
            <button className="sa-btn" type="submit" disabled={busy}>{busy ? "Saving..." : "Save Indicator Alert"}</button>
          </div>
        </form>
        {message && <p className={message.toLowerCase().includes("failed") || message.toLowerCase().includes("must") ? "sa-error" : "sa-muted"}>{message}</p>}
      </section>

      <section className="sa-card" style={{ marginTop: 16 }}>
        <h2>Active Indicator Alerts</h2>
        <div className="sa-alert-list">
          {alerts.map((alert) => (
            <div className="sa-alert-row" key={alert.alertId}>
              <div>
                <strong>{alert.symbol} {alert.indicator?.replace(/_/g, " ")} {alert.condition?.replace(/_/g, " ").toLowerCase()} {alert.threshold ?? ""}</strong>
                <span className="sa-subtle">{alert.timeframe} / value {alert.lastValue ?? "-"}{alert.lastSignal !== null && alert.lastSignal !== undefined ? ` / signal ${alert.lastSignal}` : ""}</span>
                <span className="sa-subtle">Cross lookback: {alert.crossLookbackCandles || 1} candle(s)</span>
                <span className="sa-subtle">{alert.source || "SITE"}{alert.lastCheckedAt ? ` / checked ${formatDateTime(alert.lastCheckedAt)}` : ""}{alert.lastTriggeredAt ? ` / fired ${formatDateTime(alert.lastTriggeredAt)}` : ""}</span>
                {(alert.deliveryWindowStart || alert.deliveryWindowEnd || alert.deliveryTimezone || alert.deliveryDays) && (
                  <span className="sa-subtle">Window: {alert.deliveryWindowStart || "00:00"}-{alert.deliveryWindowEnd || "24:00"} {alert.deliveryTimezone || "America/New_York"} {alert.deliveryDays || ""}</span>
                )}
                {alert.lastDeliveryWindow?.reason && alert.lastDeliveryWindow.reason !== "WITHIN_DELIVERY_WINDOW" && <span className="sa-subtle sa-warning">{alert.lastDeliveryWindow.reason.replace(/_/g, " ").toLowerCase()}</span>}
                {alert.lastError && <span className="sa-subtle sa-warning">{alert.lastError.message || String(alert.lastError)}</span>}
              </div>
              <div className="sa-actions">
                <button className="sa-btn secondary" type="button" onClick={() => updateAlert(alert, { enabled: !alert.enabled })} disabled={busy || alert.source === "ENV_BOOTSTRAP"}>{alert.enabled ? "Pause" : "Enable"}</button>
                <button className="sa-btn danger" type="button" onClick={() => deleteAlert(alert)} disabled={busy || alert.source === "ENV_BOOTSTRAP"}>Delete</button>
              </div>
            </div>
          ))}
          {!alerts.length && <p className="sa-muted">No indicator alerts yet. Env alerts appear here too when configured.</p>}
        </div>
      </section>

      {events.length > 0 && (
        <section className="sa-card" style={{ marginTop: 16 }}>
          <h2>Recent Indicator Fires</h2>
          {events.slice(0, 12).map((event) => (
            <p className="sa-muted" key={event.eventId}>
              {formatDateTime(event.createdAt)} - {event.symbol} {event.indicator} {event.value} ({event.deliveryStatus})
            </p>
          ))}
        </section>
      )}
    </>
  );
}

function ValuationPage() {
  const [symbol, setSymbol] = useState("AAPL");
  const [single, setSingle] = useState(null);
  const [symbols, setSymbols] = useState("");
  const [sort, setSort] = useState("cheapest");
  const [count, setCount] = useState(25);
  const [scanLimit, setScanLimit] = useState(75);
  const [scan, setScan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  async function valueSymbol(event) {
    event.preventDefault();
    const normalized = normalizeTickerSymbol(symbol);
    if (!normalized) {
      setMessage("Enter a symbol first.");
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson(`/stock-analyst/valuation/${encodeURIComponent(normalized)}`);
      setSingle(data.row || null);
      setMessage(`${normalized} valuation loaded.`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function scanValuations(event) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const data = await fetchJson("/stock-analyst/valuation/scan", {
        method: "POST",
        body: JSON.stringify({
          symbols,
          sort,
          count,
          scanLimit,
        }),
      });
      setScan(data);
      setMessage(`Scanned ${data.scannedCount || 0} symbols and returned ${data.returnedCount || 0}.`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  const rows = scan?.rows || [];

  return (
    <>
      <section className="sa-card">
        <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <h1>Company Valuation</h1>
            <p className="sa-muted">Measures valuation like an owner: market value divided by annual net income. Lower payback years means the company earns back its current valuation faster at the latest reported earnings pace.</p>
          </div>
          <div className="sa-kpi">
            <span>Expensive line</span>
            <strong>100 years</strong>
            <span className="sa-subtle">More than one long investing lifetime.</span>
          </div>
        </div>
        {message && <p className={message.toLowerCase().includes("required") || message.toLowerCase().includes("failed") || message.toLowerCase().includes("error") ? "sa-error" : "sa-muted"}>{message}</p>}
      </section>

      <section className="sa-card" style={{ marginTop: 16 }}>
        <h2>Value One Stock</h2>
        <form onSubmit={valueSymbol} className="sa-inline-form">
          <label className="sa-field">Symbol<input value={symbol} onChange={(event) => setSymbol(event.target.value.toUpperCase())} placeholder="AAPL" /></label>
          <button className="sa-btn" disabled={busy}>{busy ? "Working..." : "Value Symbol"}</button>
        </form>
        {single && (
          <div className="sa-grid-four" style={{ marginTop: 14 }}>
            <div className="sa-kpi"><span>Market cap</span><strong>{formatMoney(single.marketCap)}</strong></div>
            <div className="sa-kpi"><span>Net income</span><strong>{formatMoney(single.netIncome)}</strong><span className="sa-subtle">FY {single.fiscalYear || "-"}</span></div>
            <div className="sa-kpi"><span>Payback years</span><strong>{formatYears(single.paybackYears)}</strong></div>
            <div className="sa-kpi"><span>Rating</span><strong><ValuationRatingPill rating={single.rating} /></strong><span className="sa-subtle">{String(single.provider || "-").toUpperCase()}</span></div>
          </div>
        )}
      </section>

      <section className="sa-card" style={{ marginTop: 16 }}>
        <h2>Discount Scanner</h2>
        <form onSubmit={scanValuations}>
          <div className="sa-grid-two">
            <label className="sa-field">Symbols to scan
              <textarea value={symbols} onChange={(event) => setSymbols(event.target.value.toUpperCase())} placeholder="Leave blank to use the configured scan universe, or enter AAPL,MSFT,NVDA" />
            </label>
            <div className="sa-grid-two">
              <label className="sa-field">Sort
                <select value={sort} onChange={(event) => setSort(event.target.value)}>
                  <option value="cheapest">Cheapest payback first</option>
                  <option value="expensive">Slowest payback first</option>
                </select>
              </label>
              <label className="sa-field">Show top<input value={count} onChange={(event) => setCount(Number(event.target.value))} /></label>
              <label className="sa-field">Scan limit<input value={scanLimit} onChange={(event) => setScanLimit(Number(event.target.value))} /></label>
              <button className="sa-btn" disabled={busy}>{busy ? "Scanning..." : "Run Valuation Scan"}</button>
            </div>
          </div>
        </form>
        {scan && (
          <div className="sa-grid-three" style={{ marginTop: 14 }}>
            <div className="sa-kpi"><span>Scanned</span><strong>{formatNumber(scan.scannedCount)}</strong></div>
            <div className="sa-kpi"><span>Returned</span><strong>{formatNumber(scan.returnedCount)}</strong></div>
            <div className="sa-kpi"><span>Sort</span><strong>{String(scan.sort || "").replace(/_/g, " ")}</strong></div>
          </div>
        )}
      </section>

      {rows.length > 0 && (
        <section className="sa-card" style={{ marginTop: 16 }}>
          <h2>{sort === "expensive" ? "Most Expensive Payback" : "Fastest Payback"}</h2>
          <ValuationRowsTable rows={rows} />
        </section>
      )}
    </>
  );
}

function StrategyPage() {
  return (
    <section className="sa-card">
      <h1>Strategy Rules</h1>
      <div className="sa-list">
        {[
          "Do not hold into an earnings open without a clear written strategy.",
          "Do not use options or futures in this cockpit.",
          "Prefer ordinary shares and broker margin only when size, invalidation, and liquidity are defined.",
          "A scanner hit is not a trade. It is a research candidate until price confirms.",
          "Every trade should have invalidation before entry.",
        ].map((rule) => <div className="sa-list-item" key={rule}>{rule}</div>)}
      </div>
    </section>
  );
}

function SettingsPage() {
  const [state, setState] = useState(null);
  const [settings, setSettings] = useState({});
  const [message, setMessage] = useState("");

  useEffect(() => {
    fetchJson("/stock-analyst/state").then((data) => {
      setState(data.state);
      setSettings(data.state.settings || {});
      rememberDisplayTimezone(
        data.state.settings?.stockAlertDisplayTimezone ||
        data.state.settings?.stockIndicatorAlertDeliveryTimezone ||
        data.state.settings?.stockAlertScoreUpdateDeliveryTimezone ||
        data.state.settings?.stockAlertDeliveryTimezone
      );
    });
  }, []);

  async function save() {
    const data = await fetchJson("/stock-analyst/settings", { method: "POST", body: JSON.stringify({ settings }) });
    setState(data.state);
    rememberDisplayTimezone(
      data.state?.settings?.stockAlertDisplayTimezone ||
      data.state?.settings?.stockIndicatorAlertDeliveryTimezone ||
      data.state?.settings?.stockAlertScoreUpdateDeliveryTimezone ||
      data.state?.settings?.stockAlertDeliveryTimezone
    );
    setMessage("Settings saved");
  }

  return (
    <section className="sa-card">
      <h1>Settings</h1>
      <label className="sa-field">Default universe<textarea value={settings.defaultUniverse || ""} onChange={(event) => setSettings({ ...settings, defaultUniverse: event.target.value })} placeholder="AAPL,NVDA,AMD,PLTR" /></label>
      <div className="sa-grid-two">
        <label className="sa-field">Max scan symbols<input value={settings.maxScanSymbols || ""} onChange={(event) => setSettings({ ...settings, maxScanSymbols: Number(event.target.value) })} /></label>
        <label className="sa-field">Earnings danger window days<input value={settings.earningsDangerWindowDays || ""} onChange={(event) => setSettings({ ...settings, earningsDangerWindowDays: Number(event.target.value) })} /></label>
        <label className="sa-field">RSI min<input value={settings.preferRsiMin || ""} onChange={(event) => setSettings({ ...settings, preferRsiMin: Number(event.target.value) })} /></label>
        <label className="sa-field">RSI max<input value={settings.preferRsiMax || ""} onChange={(event) => setSettings({ ...settings, preferRsiMax: Number(event.target.value) })} /></label>
      </div>
      <h2>Earnings Command Center</h2>
      <div className="sa-grid-two">
        <label className="sa-field">Min avg daily volume<input value={settings.earningsMinAverageDailyVolume || ""} onChange={(event) => setSettings({ ...settings, earningsMinAverageDailyVolume: Number(event.target.value) })} /></label>
        <label className="sa-field">Min option bid activity<input value={settings.earningsMinOptionBidActivity || ""} onChange={(event) => setSettings({ ...settings, earningsMinOptionBidActivity: Number(event.target.value) })} /></label>
        <label className="sa-field">Min option ask activity<input value={settings.earningsMinOptionAskActivity || ""} onChange={(event) => setSettings({ ...settings, earningsMinOptionAskActivity: Number(event.target.value) })} /></label>
        <label className="sa-field">Reaction threshold %<input value={settings.earningsReactionThresholdPercent || ""} onChange={(event) => setSettings({ ...settings, earningsReactionThresholdPercent: Number(event.target.value) })} /></label>
        <label className="sa-field">Long entry RSI<input value={settings.earningsLongEntryRsi || ""} onChange={(event) => setSettings({ ...settings, earningsLongEntryRsi: Number(event.target.value) })} /></label>
        <label className="sa-field">Short entry RSI<input value={settings.earningsShortEntryRsi || ""} onChange={(event) => setSettings({ ...settings, earningsShortEntryRsi: Number(event.target.value) })} /></label>
        <label className="sa-field">Reversal overbought RSI<input value={settings.earningsReversalOverboughtRsi || ""} onChange={(event) => setSettings({ ...settings, earningsReversalOverboughtRsi: Number(event.target.value) })} /></label>
        <label className="sa-field">Reversal oversold RSI<input value={settings.earningsReversalOversoldRsi || ""} onChange={(event) => setSettings({ ...settings, earningsReversalOversoldRsi: Number(event.target.value) })} /></label>
        <label className="sa-field">VWAP standard deviation band<input value={settings.vwapStdDevBand || ""} onChange={(event) => setSettings({ ...settings, vwapStdDevBand: Number(event.target.value) })} /></label>
        <label className="sa-field">VWAP timeframes<input value={(settings.vwapTimeframes || []).join(",")} onChange={(event) => setSettings({ ...settings, vwapTimeframes: event.target.value.split(",").map((item) => item.trim()).filter(Boolean) })} /></label>
        <label className="sa-field">Hold-time buckets in minutes<input value={(settings.holdTimeBucketsMinutes || []).join(",")} onChange={(event) => setSettings({ ...settings, holdTimeBucketsMinutes: event.target.value.split(",").map((item) => Number(item.trim())).filter((item) => Number.isFinite(item) && item > 0) })} placeholder="5,10,15,30,60,120,240" /></label>
      </div>
      <h2>Live Watchlist Scoring</h2>
      <div className="sa-grid-two">
        <label className="sa-field">Long alert score<input value={settings.watchlistLongAlertScore || ""} onChange={(event) => setSettings({ ...settings, watchlistLongAlertScore: Number(event.target.value) })} /></label>
        <label className="sa-field">Short alert score<input value={settings.watchlistShortAlertScore || ""} onChange={(event) => setSettings({ ...settings, watchlistShortAlertScore: Number(event.target.value) })} /></label>
        <label className="sa-field">Tradeable score threshold<input value={settings.liveTradeableScoreThreshold || ""} onChange={(event) => setSettings({ ...settings, liveTradeableScoreThreshold: Number(event.target.value) })} /></label>
        <label className="sa-field">Watch score threshold<input value={settings.liveWatchScoreThreshold || ""} onChange={(event) => setSettings({ ...settings, liveWatchScoreThreshold: Number(event.target.value) })} /></label>
        <label className="sa-field">Hold score threshold<input value={settings.liveHoldScoreThreshold || ""} onChange={(event) => setSettings({ ...settings, liveHoldScoreThreshold: Number(event.target.value) })} /></label>
        <label className="sa-field">Trim score threshold<input value={settings.liveTrimScoreThreshold || ""} onChange={(event) => setSettings({ ...settings, liveTrimScoreThreshold: Number(event.target.value) })} /></label>
        <label className="sa-field">Exit score threshold<input value={settings.liveExitScoreThreshold || ""} onChange={(event) => setSettings({ ...settings, liveExitScoreThreshold: Number(event.target.value) })} /></label>
        <label className="sa-field">Add-on score threshold<input value={settings.liveAddOnScoreThreshold || ""} onChange={(event) => setSettings({ ...settings, liveAddOnScoreThreshold: Number(event.target.value) })} /></label>
        <label className="sa-field">Near-level distance %<input value={settings.liveNearLevelDistancePercent || ""} onChange={(event) => setSettings({ ...settings, liveNearLevelDistancePercent: Number(event.target.value) })} /></label>
        <label className="sa-field">Far-from-level distance %<input value={settings.liveFarFromLevelDistancePercent || ""} onChange={(event) => setSettings({ ...settings, liveFarFromLevelDistancePercent: Number(event.target.value) })} /></label>
        <label className="sa-field">Watchlist refresh seconds<input value={settings.watchlistScanIntervalSeconds || ""} onChange={(event) => setSettings({ ...settings, watchlistScanIntervalSeconds: Number(event.target.value) })} /></label>
        <label className="sa-field">Max scored watchlist symbols<input value={settings.watchlistScoreMaxSymbols || ""} onChange={(event) => setSettings({ ...settings, watchlistScoreMaxSymbols: Number(event.target.value) })} /></label>
        <label className="sa-field">Primary level indicator<input value={settings.primaryLevelIndicator || ""} onChange={(event) => setSettings({ ...settings, primaryLevelIndicator: event.target.value.toUpperCase() })} placeholder="EMA22" /></label>
        <label className="sa-field">Level timeframes<input value={(settings.levelTimeframes || []).join(",")} onChange={(event) => setSettings({ ...settings, levelTimeframes: event.target.value.split(",").map((item) => item.trim()).filter(Boolean) })} placeholder="1m,2m,5m,10m,15m,30m,1h,daily,weekly,monthly,quarterly,biannual,yearly" /></label>
        <label className="sa-field">EMA 22 pullback distance %<input value={settings.ema22PullbackDistancePercent || ""} onChange={(event) => setSettings({ ...settings, ema22PullbackDistancePercent: Number(event.target.value) })} /></label>
        <label className="sa-field">Candle entry window seconds<input value={settings.candleEntryWindowSeconds || ""} onChange={(event) => setSettings({ ...settings, candleEntryWindowSeconds: Number(event.target.value) })} /></label>
        <label className="sa-field">Candle opening window seconds<input value={settings.candleOpeningWindowSeconds || ""} onChange={(event) => setSettings({ ...settings, candleOpeningWindowSeconds: Number(event.target.value) })} /></label>
        <label className="sa-field">1h mid-candle risk minutes<input value={settings.candleOneHourRiskBufferMinutes || ""} onChange={(event) => setSettings({ ...settings, candleOneHourRiskBufferMinutes: Number(event.target.value) })} /></label>
        <label className="sa-field">Higher candle entry window days<input value={settings.higherCandleEntryWindowDays || ""} onChange={(event) => setSettings({ ...settings, higherCandleEntryWindowDays: Number(event.target.value) })} /></label>
        <label className="sa-field">Higher candle opening window days<input value={settings.higherCandleOpeningWindowDays || ""} onChange={(event) => setSettings({ ...settings, higherCandleOpeningWindowDays: Number(event.target.value) })} /></label>
        <label className="sa-field">Candle timing curve power<input value={settings.candleTimingCurvePower || ""} onChange={(event) => setSettings({ ...settings, candleTimingCurvePower: Number(event.target.value) })} /></label>
        <label className="sa-field">Candle market timezone<input value={settings.candleMarketTimezone || ""} onChange={(event) => setSettings({ ...settings, candleMarketTimezone: event.target.value })} placeholder="America/New_York" /></label>
        <label className="sa-field">Oscillator extreme percentile<input value={settings.oscillatorExtremePercentileThreshold || ""} onChange={(event) => setSettings({ ...settings, oscillatorExtremePercentileThreshold: Number(event.target.value) })} /></label>
        <label className="sa-field">Oscillator near-zero percentile<input value={settings.oscillatorNearZeroPercentileThreshold || ""} onChange={(event) => setSettings({ ...settings, oscillatorNearZeroPercentileThreshold: Number(event.target.value) })} /></label>
        <label className="sa-field">Oscillator near-signal percentile<input value={settings.oscillatorNearSignalPercentileThreshold || ""} onChange={(event) => setSettings({ ...settings, oscillatorNearSignalPercentileThreshold: Number(event.target.value) })} /></label>
      </div>
      <h2>Alert Delivery Windows</h2>
      <p className="sa-muted">Leave start/end blank for 24-hour delivery. Render env vars override these saved settings when configured.</p>
      <div className="sa-grid-two">
        <label className="sa-field">Page display timezone
          <select value={settings.stockAlertDisplayTimezone || "America/New_York"} onChange={(event) => {
            rememberDisplayTimezone(event.target.value);
            setSettings({ ...settings, stockAlertDisplayTimezone: event.target.value });
          }}>
            {ALERT_TIMEZONE_OPTIONS.map((timezone) => <option key={timezone} value={timezone}>{timezone}</option>)}
          </select>
        </label>
        <label className="sa-field">Live score start<input value={settings.stockAlertDeliveryWindowStart || ""} onChange={(event) => setSettings({ ...settings, stockAlertDeliveryWindowStart: event.target.value })} placeholder="09:30" /></label>
        <label className="sa-field">Live score end<input value={settings.stockAlertDeliveryWindowEnd || ""} onChange={(event) => setSettings({ ...settings, stockAlertDeliveryWindowEnd: event.target.value })} placeholder="16:00" /></label>
        <label className="sa-field">Live score timezone
          <select value={settings.stockAlertDeliveryTimezone || "America/New_York"} onChange={(event) => setSettings({ ...settings, stockAlertDeliveryTimezone: event.target.value })}>
            {ALERT_TIMEZONE_OPTIONS.map((timezone) => <option key={timezone} value={timezone}>{timezone}</option>)}
          </select>
        </label>
        <label className="sa-field">Live score days<input value={settings.stockAlertDeliveryDays || ""} onChange={(event) => setSettings({ ...settings, stockAlertDeliveryDays: event.target.value })} placeholder="Mon,Tue,Wed,Thu,Fri" /></label>
        <label className="sa-field">Status update start<input value={settings.stockAlertScoreUpdateDeliveryWindowStart || ""} onChange={(event) => setSettings({ ...settings, stockAlertScoreUpdateDeliveryWindowStart: event.target.value })} placeholder="09:30" /></label>
        <label className="sa-field">Status update end<input value={settings.stockAlertScoreUpdateDeliveryWindowEnd || ""} onChange={(event) => setSettings({ ...settings, stockAlertScoreUpdateDeliveryWindowEnd: event.target.value })} placeholder="16:00" /></label>
        <label className="sa-field">Status update timezone
          <select value={settings.stockAlertScoreUpdateDeliveryTimezone || "America/New_York"} onChange={(event) => setSettings({ ...settings, stockAlertScoreUpdateDeliveryTimezone: event.target.value })}>
            {ALERT_TIMEZONE_OPTIONS.map((timezone) => <option key={timezone} value={timezone}>{timezone}</option>)}
          </select>
        </label>
        <label className="sa-field">Status update days<input value={settings.stockAlertScoreUpdateDeliveryDays || ""} onChange={(event) => setSettings({ ...settings, stockAlertScoreUpdateDeliveryDays: event.target.value })} placeholder="Mon,Tue,Wed,Thu,Fri" /></label>
        <label className="sa-field">Indicator alert start<input value={settings.stockIndicatorAlertDeliveryWindowStart || ""} onChange={(event) => setSettings({ ...settings, stockIndicatorAlertDeliveryWindowStart: event.target.value })} placeholder="09:30" /></label>
        <label className="sa-field">Indicator alert end<input value={settings.stockIndicatorAlertDeliveryWindowEnd || ""} onChange={(event) => setSettings({ ...settings, stockIndicatorAlertDeliveryWindowEnd: event.target.value })} placeholder="16:00" /></label>
        <label className="sa-field">Indicator alert timezone
          <select value={settings.stockIndicatorAlertDeliveryTimezone || "America/New_York"} onChange={(event) => setSettings({ ...settings, stockIndicatorAlertDeliveryTimezone: event.target.value })}>
            {ALERT_TIMEZONE_OPTIONS.map((timezone) => <option key={timezone} value={timezone}>{timezone}</option>)}
          </select>
        </label>
        <label className="sa-field">Indicator alert days<input value={settings.stockIndicatorAlertDeliveryDays || ""} onChange={(event) => setSettings({ ...settings, stockIndicatorAlertDeliveryDays: event.target.value })} placeholder="Mon,Tue,Wed,Thu,Fri" /></label>
      </div>
      <h2>Model Learning And Backtests</h2>
      <div className="sa-grid-two">
        <label className="sa-field">Empirical blend weight<input value={settings.tradeScoreEmpiricalBlendWeight || ""} onChange={(event) => setSettings({ ...settings, tradeScoreEmpiricalBlendWeight: Number(event.target.value) })} placeholder="0.72" /></label>
        <label className="sa-field">Symbol min samples<input value={settings.tradeScoreSymbolMinimumSamples || ""} onChange={(event) => setSettings({ ...settings, tradeScoreSymbolMinimumSamples: Number(event.target.value) })} /></label>
        <label className="sa-field">Symbol full influence samples<input value={settings.tradeScoreSymbolFullInfluenceSamples || ""} onChange={(event) => setSettings({ ...settings, tradeScoreSymbolFullInfluenceSamples: Number(event.target.value) })} /></label>
        <label className="sa-field">Symbol max influence<input value={settings.tradeScoreSymbolMaxInfluence || ""} onChange={(event) => setSettings({ ...settings, tradeScoreSymbolMaxInfluence: Number(event.target.value) })} placeholder="0.65" /></label>
        <label className="sa-field">Backtest default timeframe<input value={settings.backtestDefaultTimeframe || ""} onChange={(event) => setSettings({ ...settings, backtestDefaultTimeframe: event.target.value })} placeholder="daily" /></label>
        <label className="sa-field">Backtest bars<input value={settings.backtestOutputSize || ""} onChange={(event) => setSettings({ ...settings, backtestOutputSize: Number(event.target.value) })} /></label>
        <label className="sa-field">Backtest warmup bars<input value={settings.backtestWarmupBars || ""} onChange={(event) => setSettings({ ...settings, backtestWarmupBars: Number(event.target.value) })} /></label>
        <label className="sa-field">Backtest long entry score<input value={settings.backtestLongEntryScoreThreshold || ""} onChange={(event) => setSettings({ ...settings, backtestLongEntryScoreThreshold: Number(event.target.value) })} /></label>
        <label className="sa-field">Backtest short entry score<input value={settings.backtestShortEntryScoreThreshold || ""} onChange={(event) => setSettings({ ...settings, backtestShortEntryScoreThreshold: Number(event.target.value) })} /></label>
        <label className="sa-field">Backtest exit score<input value={settings.backtestExitScoreThreshold || ""} onChange={(event) => setSettings({ ...settings, backtestExitScoreThreshold: Number(event.target.value) })} /></label>
        <label className="sa-field">Backtest opposite exit score<input value={settings.backtestOppositeScoreThreshold || ""} onChange={(event) => setSettings({ ...settings, backtestOppositeScoreThreshold: Number(event.target.value) })} /></label>
        <label className="sa-field">Backtest max hold bars<input value={settings.backtestMaxHoldBars || ""} onChange={(event) => setSettings({ ...settings, backtestMaxHoldBars: Number(event.target.value) })} /></label>
        <label className="sa-field">Backtest slippage bps<input value={settings.backtestSlippageBps || ""} onChange={(event) => setSettings({ ...settings, backtestSlippageBps: Number(event.target.value) })} /></label>
        <label className="sa-field">Backtest score fade from peak<input value={settings.backtestScoreFadeFromPeak || ""} onChange={(event) => setSettings({ ...settings, backtestScoreFadeFromPeak: Number(event.target.value) })} /></label>
        <label className="sa-field">One-hour boundary entries
          <select value={settings.backtestOneHourBoundaryEntries === false ? "false" : "true"} onChange={(event) => setSettings({ ...settings, backtestOneHourBoundaryEntries: event.target.value === "true" })}>
            <option value="true">Required except EMA scalp</option>
            <option value="false">Any score trigger</option>
          </select>
        </label>
        <label className="sa-field">One trade per setup cycle
          <select value={settings.backtestOneTradePerSetupCycle === false ? "false" : "true"} onChange={(event) => setSettings({ ...settings, backtestOneTradePerSetupCycle: event.target.value === "true" })}>
            <option value="true">Enabled</option>
            <option value="false">Disabled</option>
          </select>
        </label>
        <label className="sa-field">EMA scalp away from one-hour start
          <select value={settings.backtestAllowEmaScalpAwayFromOneHour === false ? "false" : "true"} onChange={(event) => setSettings({ ...settings, backtestAllowEmaScalpAwayFromOneHour: event.target.value === "true" })}>
            <option value="true">Allowed</option>
            <option value="false">Blocked</option>
          </select>
        </label>
      </div>
      <h2>Indicator Alerts</h2>
      <div className="sa-grid-two">
        <label className="sa-field">Indicator alerts enabled
          <select value={settings.stockIndicatorAlertsEnabled === false ? "false" : "true"} onChange={(event) => setSettings({ ...settings, stockIndicatorAlertsEnabled: event.target.value === "true" })}>
            <option value="true">Enabled</option>
            <option value="false">Disabled</option>
          </select>
        </label>
        <label className="sa-field">Indicator alert poll seconds<input value={Math.round(Number(settings.stockIndicatorAlertPollIntervalMs || 60000) / 1000)} onChange={(event) => setSettings({ ...settings, stockIndicatorAlertPollIntervalMs: Number(event.target.value) * 1000 })} /></label>
        <label className="sa-field">Indicator alert cooldown seconds<input value={Math.round(Number(settings.stockIndicatorAlertCooldownMs || 300000) / 1000)} onChange={(event) => setSettings({ ...settings, stockIndicatorAlertCooldownMs: Number(event.target.value) * 1000 })} /></label>
        <label className="sa-field">Default indicator alert timeframe<input value={settings.stockIndicatorAlertDefaultTimeframe || "1m"} onChange={(event) => setSettings({ ...settings, stockIndicatorAlertDefaultTimeframe: event.target.value })} /></label>
        <label className="sa-field">Default indicator alert bars<input value={settings.stockIndicatorAlertDefaultOutputSize || 390} onChange={(event) => setSettings({ ...settings, stockIndicatorAlertDefaultOutputSize: Number(event.target.value) })} /></label>
        <label className="sa-field">Default RSI period<input value={settings.stockIndicatorAlertDefaultRsiPeriod || 14} onChange={(event) => setSettings({ ...settings, stockIndicatorAlertDefaultRsiPeriod: Number(event.target.value) })} /></label>
        <label className="sa-field">Default EMA period<input value={settings.stockIndicatorAlertDefaultEmaPeriod || 22} onChange={(event) => setSettings({ ...settings, stockIndicatorAlertDefaultEmaPeriod: Number(event.target.value) })} /></label>
        <label className="sa-field">Default VWAP band<input value={settings.stockIndicatorAlertDefaultVwapBandMultiplier || 1.25} onChange={(event) => setSettings({ ...settings, stockIndicatorAlertDefaultVwapBandMultiplier: Number(event.target.value) })} /></label>
        <label className="sa-field">Default MACD fast/slow/signal<input value={`${settings.stockIndicatorAlertDefaultMacdFast || 12},${settings.stockIndicatorAlertDefaultMacdSlow || 26},${settings.stockIndicatorAlertDefaultMacdSignal || 9}`} onChange={(event) => {
          const [fast, slow, signal] = event.target.value.split(",").map((item) => Number(item.trim()));
          setSettings({ ...settings, stockIndicatorAlertDefaultMacdFast: fast, stockIndicatorAlertDefaultMacdSlow: slow, stockIndicatorAlertDefaultMacdSignal: signal });
        }} /></label>
        <label className="sa-field">Default TSI long/short/signal<input value={`${settings.stockIndicatorAlertDefaultTsiLong || 25},${settings.stockIndicatorAlertDefaultTsiShort || 13},${settings.stockIndicatorAlertDefaultTsiSignal || 13}`} onChange={(event) => {
          const [long, short, signal] = event.target.value.split(",").map((item) => Number(item.trim()));
          setSettings({ ...settings, stockIndicatorAlertDefaultTsiLong: long, stockIndicatorAlertDefaultTsiShort: short, stockIndicatorAlertDefaultTsiSignal: signal });
        }} /></label>
        <label className="sa-field">Default SMI lookback/smooth/signal<input value={`${settings.stockIndicatorAlertDefaultSmiLookback || 20},${settings.stockIndicatorAlertDefaultSmiSmooth || 5},${settings.stockIndicatorAlertDefaultSmiSignal || 5}`} onChange={(event) => {
          const [lookback, smooth, signal] = event.target.value.split(",").map((item) => Number(item.trim()));
          setSettings({ ...settings, stockIndicatorAlertDefaultSmiLookback: lookback, stockIndicatorAlertDefaultSmiSmooth: smooth, stockIndicatorAlertDefaultSmiSignal: signal });
        }} /></label>
        <label className="sa-field">Default PPO slow/fast/signal<input value={`${settings.stockIndicatorAlertDefaultPpoSlow || 22},${settings.stockIndicatorAlertDefaultPpoFast || 9},${settings.stockIndicatorAlertDefaultPpoSignal || 9}`} onChange={(event) => {
          const [slow, fast, signal] = event.target.value.split(",").map((item) => Number(item.trim()));
          setSettings({ ...settings, stockIndicatorAlertDefaultPpoSlow: slow, stockIndicatorAlertDefaultPpoFast: fast, stockIndicatorAlertDefaultPpoSignal: signal });
        }} /></label>
      </div>
      <button className="sa-btn" onClick={save}>Save Settings</button>
      {message && <p className="sa-muted">{message}</p>}
      <p className="sa-muted">State updated: {state?.updatedAt || "-"}</p>
    </section>
  );
}

function StockAnalystRoutes() {
  const [authChecked, setAuthChecked] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);

  useEffect(() => {
    fetchJson("/auth/site-session")
      .then((data) => setAuthenticated(Boolean(data.authenticated)))
      .catch(() => setAuthenticated(false))
      .finally(() => setAuthChecked(true));
  }, []);

  if (!authChecked) {
    return <main className="sa-login-shell"><section className="sa-login-card">Loading private cockpit...</section></main>;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage onLogin={setAuthenticated} />} />
      <Route path="/" element={<Navigate to="/products" replace />} />
      <Route path="/products" element={<Protected authenticated={authenticated}><Shell onLogout={setAuthenticated}><ProductChooserPage /></Shell></Protected>} />
      <Route path="/dashboard" element={<Protected authenticated={authenticated}><Shell onLogout={setAuthenticated}><Dashboard /></Shell></Protected>} />
      <Route path="/command-center" element={<Protected authenticated={authenticated}><Shell onLogout={setAuthenticated}><CommandCenterPage /></Shell></Protected>} />
      <Route path="/scan" element={<Protected authenticated={authenticated}><Shell onLogout={setAuthenticated}><ScanPage /></Shell></Protected>} />
      <Route path="/hourly-scanner" element={<Protected authenticated={authenticated}><Shell onLogout={setAuthenticated}><HourlyScannerPage /></Shell></Protected>} />
      <Route path="/day-trade" element={<Protected authenticated={authenticated}><Shell onLogout={setAuthenticated}><DayTradePage defaultSymbol="SPX" /></Shell></Protected>} />
      <Route path="/live-score" element={<Protected authenticated={authenticated}><Shell onLogout={setAuthenticated}><TickerPage defaultSymbol="SPX" /></Shell></Protected>} />
      <Route path="/indicator-alerts" element={<Protected authenticated={authenticated}><Shell onLogout={setAuthenticated}><IndicatorAlertsPage /></Shell></Protected>} />
      <Route path="/valuation" element={<Protected authenticated={authenticated}><Shell onLogout={setAuthenticated}><ValuationPage /></Shell></Protected>} />
      <Route path="/earnings" element={<Protected authenticated={authenticated}><Shell onLogout={setAuthenticated}><EarningsPage /></Shell></Protected>} />
      <Route path="/saved-earnings" element={<Protected authenticated={authenticated}><Shell onLogout={setAuthenticated}><SavedEarningsPage /></Shell></Protected>} />
      <Route path="/ticker/:symbol" element={<Protected authenticated={authenticated}><Shell onLogout={setAuthenticated}><TickerPage /></Shell></Protected>} />
      <Route path="/watchlist" element={<Protected authenticated={authenticated}><Shell onLogout={setAuthenticated}><WatchlistPage /></Shell></Protected>} />
      <Route path="/journal" element={<Protected authenticated={authenticated}><Shell onLogout={setAuthenticated}><JournalPage /></Shell></Protected>} />
      <Route path="/trades" element={<Protected authenticated={authenticated}><Shell onLogout={setAuthenticated}><BrokeragePage /></Shell></Protected>} />
      <Route path="/execution" element={<Protected authenticated={authenticated}><Shell onLogout={setAuthenticated}><ExecutionPage /></Shell></Protected>} />
      <Route path="/strategy" element={<Protected authenticated={authenticated}><Shell onLogout={setAuthenticated}><StrategyPage /></Shell></Protected>} />
      <Route path="/settings" element={<Protected authenticated={authenticated}><Shell onLogout={setAuthenticated}><SettingsPage /></Shell></Protected>} />
      <Route path="*" element={<Navigate to={authenticated ? "/products" : "/login"} replace />} />
    </Routes>
  );
}

function StockAnalystApp() {
  return (
    <div className="sa-root">
      <BrowserRouter>
        <StockAnalystRoutes />
      </BrowserRouter>
    </div>
  );
}

export default StockAnalystApp;
